if (!window.$f) {
    window.$f = {
        window: {
            app_router: null,
            active_view: null,
            active_modal: null,
            active_collection: null,
            active_notification: null,
            active_title_nav: null
        },
        user: {
            id: null,
            username: null,
            is_authenticated: false,
            role: null,
            is_new: null,
            time_zone: null,
            is_owner: null,
            is_admin: null,
            is_manager: null,
            is_superuser: null,
            company: {
                id: null,
                name: null,
                features: null,
                default_team: {
                    id: null,
                    display_name: null
                },
                has_feature: function (feature) {
                    return _.contains($f.user.company.features || [], feature);
                }
            },
            default_team: {
                id: null,
                display_name: null
            }
        },
        models: {
            mixins: {}
        },
        collections: {
            mixins: {}
        },
        views: {},
        version: { id: 1.0 },

        shared: {
            comments: {
                collections: {},
                models: {},
                utils: {}
            },
            sensors: {
                collections: {},
                models: {},
                util: {}
            }
        },
        web: {
            components: {
                location_attributes_filter: {
                    collections: {},
                    views: {}
                },
                form_builder: {
                    configure_card: {
                        views: {}
                    }
                }
                // extended on base_webapp.js
            }
        }
    };
}

export const global_utils = {
    SYSTEM_ADMIN_USER_ID: 1,
    STRING_DELIMITER: '~',
    STRING_DELIMITER_REPLACEMENT: '*^tilde^*',
    DEFAULT_DATE_PICKER_MIN_DATE: '2012-01-01',
    DATE_FMT: 'MMM D, YYYY',
    TIME_FMT: 'h:mm a z',
    UTC_ISO_FORMAT: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
    DATE_AND_TIME_FMT: 'MMM D, YYYY, h:mm a (z)',
    DATE_AND_TIME_NO_TZ_FMT: 'MMM D, YYYY, h:mm a',
    ANNOUNCEMENT_DATE_AND_TIME_FMT: 'MMM D, YYYY h:mm A',
    // Radius of the Earth in Miles
    EARTHS_RADIUS: 3960,
    REPORT_HOUR_OFFSET: 4, // start reports 4 hours ahead from start of day via rrule config
    ACCEPTED_DOCUMENT_FILED_TYPES:
        'pdf,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv',
    ACCEPTED_INSTRUCTION_FIELD_TYPES:
        'pdf,image/jpg,image/jpeg,image/bmp,image/png,image/gif,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv',
    ACCEPTED_INSTRUCTION_FIELD_TYPES_DF:
        'pdf,image/jpg,image/jpeg,image/bmp,image/png,image/gif,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv,video/mp4,video/webm',
    ACCEPTED_INSTRUCTION_FIELD_IMAGE_EXTENSIONS: ['jpg', 'jpeg', 'bmp', 'png', 'gif'],
    ACCEPTED_INSTRUCTION_FIELD_VIDEO_EXTENSIONS: ['mp4', 'webm'],
    ACCEPTED_IMPORT_FORM_WIZARD_FIELD_TYPES: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ],
    DEPENDENT_FIELD_TOP_LEVEL: 0, // The default field level for dependent fields,
    DECIMALS_IN_TEMPERATURE_VALUE: 2, // Precision for temperature values
    REPORTS_DATE_FORMAT: 'MMM D, YYYY',
    REPORTS_DATE_FORMAT_TZ: 'MMM D, YYYY (z)',
    DATE_OPERATORS: {
        TODAY: 'today',
        YESTERDAY: 'yesterday',
        DATE_WEEK_THIS: 'date_week_this',
        DATE_WEEK_LAST: 'date_week_last',
        DATE_MONTH_THIS: 'date_month_this',
        DATE_MONTH_LAST: 'date_month_last',
        DATE_OCCUR_AFTER: 'date_occur_after',
        DATE_OCCUR_BEFORE: 'date_occur_before',
        DATE_OCCUR_BETWEEN: 'date_occur_between',
        DATE_SPECIFIC_BEFORE: 'date_specific_before',
        DATE_SPECIFIC_AFTER: 'date_specific_after'
    },

    /**
     * Is the value null or undefined?
     *
     * Someday we can replace this with lodash.isNil
     */
    is_nil: function (value) {
        return value === undefined || value === null;
    },

    /**
     * Deeply clone an object.
     *
     * WARNING: this is expensive and should not be used unless needed!
     *
     * @todo: we should update this to use something like lodash. Minimizing risk/scope at the time of this writing by
     * keeping what we've been doing elsewhere.
     */
    deepclone: function (thing) {
        return window.cloneDeep(thing);
    },

    /**
     * On web and mobile, the active company is stored differently. This gets the active company for both web and mobile.
     *
     * @return {ActiveCompany|Company}
     */
    get_active_company: function () {
        if (this.is_mobile_app()) {
            return $f.active_company;
        }

        return new Company($f.user.company);
    },

    /**
     * Get the submitted date from the data, selecting from 'date_submitted' and 'date_completed'.
     * When the dates are close enough, 'date_completed' is used, to reduce user confusion.
     *
     * @param {*} smetadata Submission smetadata object (Required)
     * @returns submitted date to use
     */
    pick_date_submitted: function (smetadata) {
        const date_submitted = smetadata.get('date_submitted');
        const date_completed = smetadata.get('date_completed');

        if (
            date_submitted &&
            date_completed &&
            Math.abs(moment(date_submitted.$date).diff(moment(date_completed.$date), 'seconds')) < 300
        ) {
            return date_completed;
        } else {
            return date_submitted;
        }
    },

    /**
     * Add time zone to the submission.smetadata.<data_field> field and
     * format it. It also selects correct date in case of 'date_submitted'.
     *
     * This function support dates in ES5 and ES7, the format for each of them
     * are as follow:
     *
     * ES5:
     * "submitted_date": { "$date": 1633985346359 }
     *
     * ES7:
     * "submitted_date": "2021-10-11T20:49:06"
     *
     *
     * @param  {dict}    smetadata  Submission smetadata object
     * @param  {string}  format     Format of the date (Defaults to DATE_AND_TIME_FMT)
     * @param  {string}  date_field smetadata field name to use (Defaults to date_submittted)
     * @param  {string}  time_zone  Time zone (Defaults to using the smetadata time_zone field)
     * @param  {boolean} hide_tz    Hide time zone if it's the same as the active user's
     * @return {string} Formatted date
     */
    format_submission_date: function (smetadata, format, date_field, time_zone, hide_tz) {
        const field_submitted = 'date_submitted';
        format = format || $f.utils.DATE_AND_TIME_FMT;
        date_field = date_field || field_submitted;
        time_zone = time_zone || smetadata.get('time_zone') || moment.tz.guess();

        if (!smetadata || !smetadata.get(date_field)) {
            return '';
        }

        if (hide_tz) {
            var user_time_zone = $f.active_user.get('time_zone');
            if (moment.tz(user_time_zone).utcOffset() === moment.tz(time_zone).utcOffset()) {
                format = format.replace('z', '');
            }
        }

        const date = field_submitted === date_field ? this.pick_date_submitted(smetadata) : smetadata.get(date_field);
        return moment
            .utc(date.$date || date)
            .tz(time_zone)
            .format(format);
    },

    /**
     * Add time zone to specific date and format it.
     *
     * @param  {Date}    date       Date to format
     * @param  {string}  time_zone  Time zone (Defaults to using moment.tz.guess())
     * @param  {string}  format     Format of the date (Defaults to DATE_AND_TIME_FMT)
     * @param  {boolean} hide_tz    Hide time zone if it's the same as the active user's
     * @return {string} Formatted date
     */
    format_date: function (date, time_zone = moment.tz.guess(), format = $f.utils.DATE_AND_TIME_FMT, hide_tz = false) {
        if (hide_tz) {
            const user_time_zone = $f.active_user.get('time_zone');
            if (moment.tz(user_time_zone).utcOffset() === moment.tz(time_zone).utcOffset()) {
                format = format.replace('z', '');
            }
        }

        return moment(date).tz(time_zone).format(format);
    },

    /**
     * Convert $date epoch ts to ISO FORMAT date
     */
    convert_epoch_ts_to_iso_string: function (epoch_ts) {
        return moment(epoch_ts).toISOString();
    },

    sanitize: function (text) {
        return window.DOMPurify.sanitize(String(text), {
            ALLOWED_TAGS: $f.bleached_allowed_tags,
            ALLOWED_ATTR: $f.bleached_allowed_attributes,
            ALLOWED_PROTOCOLS: $f.bleached_allowed_protocols
        });
    },

    validate_email: function (email) {
        var re_comma_email = /,/;
        var re = null;

        if (re_comma_email.test(email)) {
            re =
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))(?:,\s*(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))/;
        } else {
            re =
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        }

        return re.test(email);
    },

    validate_phone: function (number) {
        var re = /^(([0-9]{0,2})[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
        return re.test(number);
    },

    title_case: function (str) {
        str = str.toLowerCase().replace(/\b[a-z]/g, function (letter) {
            return letter.toUpperCase();
        });
        return str;
    },

    has_local_storage: function () {
        if (typeof Storage !== 'undefined') {
            return true;
        } else {
            return false;
        }
    },

    /**
     *  Simple function to create a name for display, a.k.a. display_name for a user
     *  there is a sister function for Python in util.py
     */
    generate_display_name: function (first_name, last_name, email, sms_number) {
        if (first_name && !last_name) {
            return first_name;
        }
        return first_name ? first_name + ' ' + last_name : email || sms_number;
    },

    get_error_response: function (response, default_msg) {
        try {
            return JSON.parse(response.responseText).message;
        } catch (e) {
            return default_msg ? default_msg : null;
        }
    },

    /**
     * @param  {Date} date Either uses a date object passed in or creates one for today
     * @return {Date} returns date object representing the start of the week from the date passed in or today
     */
    get_start_of_week: function (date) {
        date = date ? date : new Date();
        var moment_date = moment.isMoment(date) ? date : moment(date);
        return moment_date.startOf('isoWeek').toDate();
    },

    /**
     * Get the start of day for a datetime object.
     *
     * @param {Date|moment} date    The date to user; can be a JS Date() or a moment() date
     * @returns {Date}              A JS Date() for the start of the day
     */
    get_start_of_day: function (date) {
        date = date || new Date(Date.now()); // Date.now() is mockable in tests
        var moment_date = moment.isMoment(date) ? date : moment(date);
        return moment_date.startOf('day').toDate();
    },

    /**
     * Get the start of day (as epoch ts) with offset of N hours
     *
     * @param date {Date|moment}
     * @param hours_offset {int}
     */
    get_start_of_day_ts_with_offset_in_tz: function (date, hours_offset, tz) {
        var format_str = `YYYY-MM-DDT0${hours_offset}:00:00`;
        return moment.tz(moment(date).format(format_str), tz).valueOf();
    },

    /**
     * Get the end of day for a datetime object.
     *
     * @param {Date|moment} date    The date to user; can be a JS Date() or a moment() date
     * @returns {Date}              A JS Date() for the end of the day
     */
    get_end_of_day: function (date) {
        date = date ? date : new Date();
        var moment_date = moment.isMoment(date) ? date : moment(date);
        return moment_date.endOf('day').toDate();
    },

    /**
     * @param {Date|moment} date    The date to user; can be a JS Date() or a moment() date
     * @returns {Date}              A JS Date() for the start of tomorrow
     */
    get_tomorrow: function (date) {
        date = date || new Date();
        var moment_date = moment.isMoment(date) ? date : moment(date);
        var tomorrow = moment_date.add(1, 'day').startOf('day');
        return tomorrow.toDate();
    },

    /**
     * @param {Date|moment} date    The date to user; can be a JS Date() or a moment() date
     * @returns {Date}              A JS Date() for the end of the week (ISO week sunday)
     */
    get_end_of_week: function (date) {
        date = date || new Date();
        var moment_date = moment.isMoment(date) ? date : moment(date);
        return moment_date.endOf('isoWeek').toDate();
    },

    /**
     * Get the week of the month for a specified datetime.
     * @see http://stackoverflow.com/a/21738982/477632
     *
     * @param  {Date} date The datetime to check
     */
    get_num_week_of_month: function (date) {
        if (!moment.isMoment(date)) {
            date = moment(date);
        }
        return Math.ceil(date.date() / 7);
    },

    /**
     * The same as get_relative_date but sets a fixed start point at today at zero hours.
     *
     * @param end_date
     * @param include_timezone
     * @returns {*|{label, color}}
     */
    get_relative_date_from_today: function (end_date, include_timezone) {
        include_timezone = include_timezone || false;
        return this.get_relative_date(moment(new Date().setHours(0, 0, 0, 0)), end_date, include_timezone);
    },

    get_formatted_time: function (date) {
        return moment(date).format('h:mm a');
    },

    get_full_formatted_time: function (date) {
        return moment(date).format('MMM DD hh:mm a z');
    },

    get_formatted_time_from_24_hour_time: function (time) {
        return moment(time, 'HH:mm').format('h:mm a');
    },

    get_24_hour_time_from_formatted_time: function (time) {
        return moment(time, 'h:mm a').format('HH:mm');
    },

    /**
     * Return UTC Date Object with the exact year, month, day, and hour of the moment object but with no tz offset
     *      this is needed when we are using _local times, and don't want those dates localized to the client timezone
     *      when instantiating a Date(epoch_ts || date_str) obj.
     *
     * @param moment_obj {moment}
     */
    get_utc_date_without_offset: function (moment_obj) {
        return new Date(moment_obj.utc().format('YYYY-MM-DD hh:00:00'));
    },

    /**
     * Is the end_date past now?
     *
     * @param end_date (moment or Date) object
     */
    is_overdue: function (end_date) {
        return moment() > end_date;
    },

    /**
     * Get date formatted as a label and a color.
     * Color is red if overdue
     *
     * @param {Date} comparison_date - date to compare to now.
     * @param {String} (optional) moment formatting string. Default is MMM D, ie. Jan 17
     *
     * @returns {obj} {label: formatted_date, color: string}
     */
    get_date_str_with_color: function (end_date, moment_format) {
        moment_format = moment_format || 'MMM D';
        var response = { color: '' };
        if ($f.utils.is_overdue(end_date)) {
            response.color = 'red';
        }
        response.label = moment(end_date).format(moment_format);
        return response;
    },

    /**
     * Calculates in days how far apart two dates are, for example
     * 'tomorrow' or 'yesterday'.  This will return an array, the first
     * value containing the string to display and the second is the color
     * value
     *
     * Note: Expects comparisons from the beginning of the day or the return string values are wrong.
     *
     * @note @author:david we are overriding this in mobile to translate
     * Tomorrow/Yesterday/Today strings. Once we update this to use localization,
     * we won't need to override it in mobile.js.
     *
     * @param  {Date}       start_date the date to start from
     * @param  {Date}       end_date the date to go to
     * @param  {Boolean}    include_timezone whether to include the timezone in the format where it is relevant
     */
    get_relative_date: function (start_date, end_date, include_timezone) {
        include_timezone = include_timezone || false;
        end_date = moment(end_date);
        var days_diff = end_date.diff(start_date, 'days', true); // true = floating point difference

        var label = '';
        var color = '';

        if (days_diff >= 0 && days_diff < 1) {
            label = 'Today';
            color = 'green';
        } else if (days_diff < 0 && days_diff > -1) {
            label = 'Yesterday';
        } else if (days_diff >= 1 && days_diff < 2) {
            label = 'Tomorrow';
        } else {
            label = include_timezone ? end_date.format('MMM D (z)') : end_date.format('MMM D');
        }

        if (days_diff < 0) {
            color = 'red';
        }

        return { label: label, color: color };
    },

    /**
     *  Return formatted project time range
     *  @param {Object} project
     *      example: { date_start_naive: "2018-02-12T00:00:00", date_due_naive: "2018-02-12T23:59:59" }
     *
     *  Example (12:00AM - 11:59PM)
     */
    get_project_time_range: function (project) {
        if (!project) {
            return '';
        }
        var date_due_str = moment(project.date_due_local).format('hh:mm A');
        var date_start_str = moment(project.date_start_local).format('hh:mm A');
        return date_start_str + ' - ' + date_due_str;
    },

    /**
     * Takes in an options {} and makes sure that success/error/complete callbacks are included.

     * @param  {dictionary} options         Our options/callbacks for when the function finishes
     * @param  {function} options.success   Our success callback to fire when we have all our dependencies
     * @param  {function} options.error     Our error callback to fire if we encounter an errror
     * @param  {function} options.complete  A callback to fire on both success or error
     * @param  {function} options.*         Any other values in options will be in the returned options
     * @return {dictionary} options         the options dict that was passed in with proper success/error/complete callbacks
     */
    setup_options_callbacks: function (options) {
        options = options || {};
        options = $.extend(
            {
                // Defaults:
                success: function () {},
                error: function () {},
                complete: function () {}
            },
            options
        );

        // Always complete() after success() and error()
        var _success = options.success;
        options.success = function () {
            _success.apply(this, arguments);
            options.complete();
        };
        var _error = options.error;
        options.error = function () {
            _error.apply(this, arguments);
            options.complete();
        };

        return options;
    },

    /**
     * Escape a string so that it can be tested by regex.
     */
    escape_for_regex: function (str) {
        // copied from jQuery.Autocomplete (also here: http://stackoverflow.com/a/6969486/477632)
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    },

    local_guid: function () {
        // local storage convention borrowed from http://backbonejs.org/docs/backbone.localStorage.html
        function S4() {
            return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
        }

        return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
    },

    remove_url_hashbangs: function () {
        // If supported by browser (IE10+), remove URL hash to avoid
        // refreshes always leading to same page
        if (window.history && window.history.replaceState) {
            if ($f.flags.get_flag('clientside_invite_flow')) {
                history.replaceState('', document.title, `${window.location.pathname}${window.location.search}`);
            } else {
                history.replaceState('', document.title, window.location.pathname);
            }

            return;
        }

        // if we're not on mobile, then use a page reload fallback
        if (!$f.mobile.is_mobile_app()) {
            window.location = window.location.pathname;
        }
    },

    /**
     * Encode id and display value into a single string for passing into a urlparam between pages
     * @param {number} id of the model being passed
     * @param {string} display_value for the model being passed
     * @return {string} The urlparam of format <id>~<display_value>
     */
    encode_id_and_value_param: function (id, value) {
        var id_str, value_str;
        try {
            id_str = id.toString();
        } catch (e) {
            id_str = 'undefined';
        }
        try {
            value_str = value.toString();
        } catch (e) {
            value_str = 'undefined';
        }

        return `${id_str}${$f.utils.STRING_DELIMITER}${encodeURIComponent(
            value_str.replace($f.utils.STRING_DELIMITER, $f.utils.STRING_DELIMITER_REPLACEMENT)
        )}`;
    },

    /**
     * Decode id and display value from a single string url param
     * @param {string} urlparam of the form <id>~<display_value> to parse
     * @return {Object} with { value: <id> and display_value:<display_value> }
     */
    decode_id_and_val_param: function (param) {
        var values = param.split($f.utils.STRING_DELIMITER);

        if (values.length != 2) {
            // old url's could hit this, assume it is the value
            console.error('Bad param sent to decode_id_and_val_param', param);
            return { value: param, display_value: undefined };
        }

        // @author:david @jira:TASKS-425 do not need to decodeURIComponent() because Backbone handles that in the
        // router for us, and doing it twice can cause an error.
        return {
            value: parseInt(values[0]),
            display_value: values[1].replace($f.utils.STRING_DELIMITER_REPLACEMENT, $f.utils.STRING_DELIMITER)
        };
    },

    ellipsis: function (text, length) {
        length || (length = 50);
        if (text.length - 3 <= length) {
            return text;
        }

        let truncated_text = text.substr(0, length);
        // removing any unclosed trailing tags and closing them
        truncated_text = truncated_text.replace(/<\/?[a-z]*\s*$/, '');
        let parser = new DOMParser();
        return parser.parseFromString(truncated_text, 'text/html').body.innerHTML + '...';
    },

    replace_line_break: function (text, replacement) {
        text = text || '';
        replacement = replacement || '';
        return text.replace(/<br>/g, replacement);
    },

    replace_newline: function (text, replacement) {
        text = text || '';
        replacement = replacement || '';
        return text.replace(/\n/g, replacement);
    },

    capitalize_string: function (string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    },

    /**
     * Expand a textarea's height to match the height of the text inside it. A 'max-height' CSS value
     * should be set to prevent the textarea from growing too big.
     *
     * Usage:
     *     $('textarea.autoexpand').on('keyup', expand_textarea);
     */
    expand_textarea: function (event) {
        var textarea = event.target;
        var new_height = textarea.scrollHeight;
        var current_height = textarea.clientHeight;

        if (new_height > current_height) {
            // CSS must prevent this from getting too large
            textarea.style.height = new_height + 'px';
        }
    },

    /**
     * Converts distance measurements from standard to metric based on company settings
     * @param  {int}  value [A distance in miles]
     * @param  {Boolean} round [if true will round  to 2 decimal places]
     * @return {int}        [A distance in either miles or kilometers]
     */
    convert_distance: function (value, round) {
        // @todo: kyle set this up to read from company measurement settings
        // when we implement in the future
        if (round) {
            return Math.round(value * 100) / 100;
        }

        return value;
    },

    /**
     * Converts a millisecond duration into a string representation
     *
     * @param duration {number}: Number of milliseconds
     * @param accuracy {string}: Specify the level of accuracy you want to print
     *      'centiseconds' - 00:00:00.00
     *      'seconds'      - 00:00:00
     *      'minutes'      - 00:00
     *      'hours'        - 00
     * @returns {string} : "hh:mm:ss.cc" or "37 hours" if duration is over one day
     */
    get_string_from_milliseconds: function (duration, accuracy) {
        // Check if we are getting a number and its positive
        if (!duration || typeof duration !== 'number' || isNaN(duration) || duration < 0) {
            duration = 0;
        }

        // 86400000 is 1 day in ms
        if (duration < 86400000) {
            switch (accuracy) {
                case 'centiseconds':
                    return moment.utc(duration).format('HH:mm:ss.SS');
                case 'minutes':
                    return moment.utc(duration).format('mm:ss');
                case 'hours':
                    return moment.utc(duration).format('HH');
                case 'seconds':
                default:
                    return moment.utc(duration).format('HH:mm:ss');
            }
        }

        // 3600000 is 1 hour in ms
        return `${parseInt(duration / 3600000)} hours`;
    },

    get_distance_between_lat_lon_points: function (lat1, lon1, lat2, lon2) {
        var _deg2rad = function (deg) {
            return deg * (Math.PI / 180);
        };

        var R = $f.utils.EARTHS_RADIUS; // Radius of the earth in mi
        var dLat = _deg2rad(lat2 - lat1); // _deg2rad below
        var dLon = _deg2rad(lon2 - lon1);
        var a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(_deg2rad(lat1)) * Math.cos(_deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        var d = R * c; // Distance in mi
        return d;
    },

    /**
     * Our loading spinner has become too complex to copy/paste everywhere, so it needs to be portable
     * and useable throughout our HTML. Rather than creating a region for it (which would require every view
     * to be a Layout), we'll just use this utility function.
     */
    get_loading_spinner_html: function () {
        var el = $('#loading_dialog_wrapper .loading_spinner');
        if (!el || !el.length) {
            throw new Error('Could not find global loading dialog for get_loading_spinner_html()');
        }

        return el.prop('outerHTML');
    },

    /**
     * function to test if element has text that i truncated or not.
     * @param $element {jQuery element}
     * @returns {boolean}
     */
    is_ellipsis_active: function ($element) {
        return $element.length && $element.outerWidth() !== $element[0].scrollWidth;
    },

    /**
        NOTE: @halah @jira:pp-75
    This function is used to hide form templates that are secure from users that not owners
    or didn't create the form
    * @param form_template {FormTemplate}
    * @returns {boolean}
    */
    can_show_form_template: function (form_template) {
        if (!$f.user.is_admin) {
            return false;
        }
        if (!form_template.get('is_secure')) {
            return true;
        }

        return $f.user.is_owner || form_template.get('user_id') == $f.user.id;
    },

    /**
     * Return hash string from input str
     * Derived from: https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
     *
     * @param str_to_hash
     * @returns {string}
     */
    hash_str: function (str_to_hash) {
        var hash = 0;
        if (str_to_hash.length === 0) {
            return '';
        }
        for (var i = 0; i < str_to_hash.length; i++) {
            var char = str_to_hash.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash.toString();
    },

    /**
     * Check if our element is (at least partially) in view. This is only used to verify that we are still
     * in view after the intersection observer fires its callback. (@see init_intersection_observer())
     *
     * @author:david We could update this to use the pageVisibility HTML5 API, but that's not
     * supported by all browsers, but it seems silly to include a polyfill just for this:
     * https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
     *
     * Source: https://stackoverflow.com/a/26039199/477632
     */
    is_element_in_viewport: function ($element) {
        var rect = $element.get(0).getBoundingClientRect();
        var $win = $(window);
        var doc_el = document.documentElement;

        var window_height = $win.height() || doc_el.clientHeight;
        var window_width = $win.width() || doc_el.clientWidth;

        var is_vert_in_view = rect.top <= window_height && rect.top + rect.height >= 0;
        var is_hor_in_view = rect.left <= window_width && rect.left + rect.width >= 0;

        return is_vert_in_view && is_hor_in_view;
    },

    /**
     * Uses the correct instance of ajax depending on the existence of $f.mobile.
     *
     * @returns {$f.mobile.ajax || $.ajax}
     */
    select_ajax: function () {
        return $f.mobile ? $f.mobile.ajax : $.ajax; // @jira:MOBILE-639
    },

    /**
     * Takes in a file size in Bytes & format to 10KB etc
     * @halah based this on
     * https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
     *
     * @param size_in_bytes {number}
     * @param decimal_points {number}
     * @returns {string}
     */
    format_bytes: function (size_in_bytes, decimal_points) {
        size_in_bytes = size_in_bytes || 0;
        decimal_points = decimal_points <= 0 ? 0 : decimal_points || 2;

        if (size_in_bytes === 0) {
            return '0 Bytes';
        }

        var k = 1024;
        var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        var i = Math.floor(Math.log(size_in_bytes) / Math.log(k));

        return `${parseFloat((size_in_bytes / Math.pow(k, i)).toFixed(decimal_points))} ${sizes[i]}`;
    },
    /**
     * Convert fahrenheit temperature value to the value defined in company
     * settings.
     * @param value {number} Value in fahrenheit
     *
     * @return {number} Value converted to company temperature_unit.
     */
    convert_to_users_temperature_units: function (value) {
        var temperature_unit = $f.user.temperature_unit ?? $f.utils.get_active_company().get('temperature_unit');

        var is_centigrade = temperature_unit === 'C';
        var display_unit = is_centigrade ? '°C' : '°F';

        if (isNaN(parseFloat(value))) {
            return '';
        }

        var temperature = !is_centigrade ? value : $f.utils.fahrenheit_to_centigrade(value);

        return `${$f.utils.round(temperature, 1)} ${display_unit}`;
    },
    /**
     * Convert fahrenheit to centigrade.
     * @param value {number} Value in fahrenheit
     *
     * @param decimal_places {number} Defaults to 1
     * @return {number} Value converted to centigrade.
     */
    fahrenheit_to_centigrade: function (value, decimal_places) {
        decimal_places = typeof decimal_places !== 'undefined' ? decimal_places : 1;
        return $f.utils.round(((value - 32) * 5) / 9, decimal_places);
    },
    /**
     * Convert centigrade to fahrenheit.
     * @param value {number} Value in centigrade
     *
     * @param decimal_places {number} Defaults to 1
     * @return {number} Value converted to fahrenheit.
     */
    centigrade_to_fahrenheit: function (value, decimal_places) {
        decimal_places = typeof decimal_places !== 'undefined' ? decimal_places : 1;
        return $f.utils.round((value * 9) / 5 + 32, decimal_places);
    },
    /**
     * Return a floating point rounded to the given precision
     * @param value {number} Value in centigrade
     * @param precision {number} Number of decimal places
     *
     * @return {number} Value converted to fahrenheit.
     */
    round: function (value, precision) {
        var multiplier = Math.pow(10, precision || 0);
        return Math.round(value * multiplier) / multiplier;
    },

    /**
     * Return a floating point rounded to the given precision
     * @param value {number} Temperature value in centigrade or fahrenheit
     * @param decimal_places {number} Number of decimal places
     *
     * @return {number} Value rounded and converted to fahrenheit.
     */
    get_rounded_temperature_in_fahrenheit_units: function (value, decimal_places) {
        var precision =
            typeof decimal_places === 'number' && decimal_places.toString().indexOf('.') === -1
                ? decimal_places
                : $f.utils.DECIMALS_IN_TEMPERATURE_VALUE;

        var temperature_unit = $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');

        if (temperature_unit === 'C') {
            value = $f.utils.centigrade_to_fahrenheit(value, precision);
        }

        return parseFloat(value);
    },

    /**
     * Return a floating point rounded to the given precision
     * @param value {number} Temperature value in centigrade or fahrenheit
     * @param decimal_places {number} Number of decimal places
     *
     * @return {number} Value rounded and converted to centigrade.
     */
    get_rounded_temperature_in_configured_units: function (value, decimal_places) {
        var precision =
            typeof decimal_places === 'number' && decimal_places.toString().indexOf('.') === -1
                ? decimal_places
                : $f.utils.DECIMALS_IN_TEMPERATURE_VALUE;

        var temperature_unit = $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');

        if (temperature_unit === 'C') {
            value = $f.utils.fahrenheit_to_centigrade(value, precision);
        }

        return value;
    },

    /**
     * Evaluate a value against the criteria value via the given operator.
     *
     * Examples:
     *  ('>=', 5, 10) : 5 >= 10 ==> False
     *  ('between', 3, [7, 9]) : 3 >= 7 && 3 <= 9 ==> False
     *  ('between', 8, [7, 9]) : 8 >= 7 && 8 <= 9 ==> True
     *
     * @param operator {string}
     * @param value { any }
     * @param criteria_value { any }
     *
     * @return { boolean }
     */
    evaluate_operator: function (operator, value, criteria_value) {
        if (
            _.contains(['between', 'outside'], operator) &&
            (!_.isArray(criteria_value) || criteria_value.length !== 2)
        ) {
            console.error(`Trying to check search criteria value: ${criteria_value} with operator "${operator}"`);
            return false;
        }

        switch (operator) {
            case 'between': // Note: Only inclusive of bottom boundary - simplifies UI of adding multiple in a row
                return value >= criteria_value[0] && value < criteria_value[1];
            case 'inclusive_between':
                return value >= criteria_value[0] && value <= criteria_value[1];
            case 'outside':
                return value < criteria_value[0] || value > criteria_value[1];
            case '==':
                return value === criteria_value;
            case '<':
                return value < criteria_value;
            case '<=':
                return value <= criteria_value;
            case '>':
                return value > criteria_value;
            case '>=':
                return value >= criteria_value;
            default:
                console.error(`Bad operator passed into evaluate_operator: ${operator}`);
                return false;
        }
    },

    /**
     * Convert acceptance_values to event.value for legacy mobile devices for range fields
     *
     * @param acceptance_values
     */
    get_legacy_range_event_value: function (field_model, acceptance_values) {
        var acceptance_criteria = field_model.get('acceptance_criteria');
        var matching_criteria = [];
        _.each(acceptance_values, function (matching_acceptance_value) {
            acceptance_criteria.each(function (criterion) {
                if (criterion.get('metadata').get('acceptance_value') === matching_acceptance_value) {
                    matching_criteria.push({
                        field_id: field_model.get('id'),
                        operator_id: criterion.get('condition').get('operator_id'),
                        value: criterion.get('condition').get('condition_value')
                    });
                }
            });
        });
        var criteria_list = new FieldEventSearchCriterias(matching_criteria, {
            metadata: { type: 'formfield_dependent_event', simple_logic: 'or' }
        });
        return { criterias: criteria_list.toJSON() };
    },

    /**
     * Convert acceptance_values to legacy event value for yesno fields
     * @param acceptance_values
     */
    get_legacy_yesno_event_value: function (field_model, acceptance_values) {
        var acceptance_criteria = field_model.get('acceptance_criteria');

        var yes_criterion = acceptance_criteria.find_criterion_with_condition_value_of(true);
        var no_criterion = acceptance_criteria.find_criterion_with_condition_value_of(false);

        if (_.isEqual([yes_criterion.get('metadata').get('acceptance_value')], acceptance_values)) {
            return true;
        } else if (_.isEqual([no_criterion.get('metadata').get('acceptance_value')], acceptance_values)) {
            return false;
        } else if (_.isEqual(acceptance_values, [-1, 1])) {
            // note we are combinging pass or fail to mean "any".
            return -1;
        } else {
            // do no event on neutral since this doesn't exist for legacy mobile apps.
            return null;
        }
    },

    /**
     * Parse api response and sets paginator count if applicable
     *
     * Parsing options:
     *      legacy - top level obj
     *      v1 - results + count top level keys
     *      v2 - data + meta top level key
     *
     *
     * @param obj - model or collection
     * @param response - xhr response
     * @private
     */
    parse_zenput_api_response: function (obj, response) {
        // 201 response can be empty
        if (_.isEmpty(response)) {
            return response;
        }
        //  legacy - top level obj
        if (!response.meta && !response.results) {
            return response;
        }
        if (obj.paginator) {
            if (response.count !== undefined) {
                obj.paginator.set('results_count', response.count);
            } else if (response.meta && response.meta.count !== undefined) {
                obj.paginator.set('results_count', response.meta.count);
            }
        }
        //  v2 - data + meta top level key
        if (response.data && response.meta) {
            return response.data;
        } else {
            //  v1 - results + count top level keys
            return response.results;
        }
    },

    validate_file_type: function (file, accepted_types) {
        file = file || {};

        if (!file.type) {
            return false;
        }
        // get file extension
        var extension = file.type;
        return _.contains(accepted_types, extension);
    },

    validate_file_size: function (file) {
        var limit = 100 * 1024 * 1024; // 100MB
        return file.size <= limit;
    },

    is_mobile_app: function () {
        return !!$f.mobile;
    },

    /**
     * Logs a message to Sentry if it's wired up. (Note: sentry is not configured in local development)
     *
     * @param {string}  msg               The message to send
     * @param {string}  details           Any details to send with the error
     * @param {boolean} log_to_console    If true (default), will log message/details to console as well
     */
    log_error: function (msg, details = {}, log_to_console = true) {
        if ($f.utils.is_mobile_app()) {
            $f.mobile.logging.log_error(msg, details, log_to_console);
            return;
        }

        if (log_to_console) {
            console.error(msg, details);
        }
        const sentry = window.Sentry;
        if (sentry) {
            sentry.withScope((scope) => {
                scope.setExtras(details);
                sentry.captureMessage(msg);
            });
        }
    },

    /**
     *  Used to convert '2014-01-10' into a new Date(2014,01,10) instead of using string
     *  because javascript will add timezone to string date
     *  @param {String} date_string String representation of date in format 'YYYY-MM-DD' '2014-02-02'
     *  @return {Date} Will return the date for the date_string, or if not in correct formate, current date
     */
    split_datetime_string: function (date_string) {
        if (typeof date_string == 'string') {
            var f = date_string.split('-');
            if (f.length == 3) {
                // Remember month is 0 start
                return new Date(f[0], f[1] - 1, f[2]);
            }
        }
        return new Date();
    },

    /**
     * Using backbone underscore to template strings
     * @param {String} untemplated_string A string that may contain html in it
     * @return {String} Will return the string rendered with <%- to prevent any JS or HTML execution
     */
    template_string: (untemplated_string) => {
        return _.template('<%- untemplated_string %>')({ untemplated_string });
    }
};

$f.storage = null; // ZenputStorage

/**
 * This helper allows us to recalculate formula fields on both
 * Mobile and Individual Submission Page
 */
export const global_formula_utils = {
    /**
     * Only allow calculation of N/A values for sum and subtract types
     */
    allow_na_values: function (formula_operation_type) {
        return ['sbt', 'sum'].indexOf(formula_operation_type) !== -1;
    },

    calculate: function (model, collection) {
        var _this = this;
        var total = 0;
        var denominator = 0;
        var is_first = true;
        var operation = model.get('metadata').formula_operation_type;
        var do_not_count_unanswered = model.get('metadata').formula_ignore_unanswered;
        var formula_advanced = model.get('metadata').formula_advanced || '';
        var allow_na_values = $f.utils.formula.allow_na_values(operation);

        _.each(model.get('options'), function (event) {
            var field = collection.get(event.id);

            if (!field) {
                return false;
            } // error check, continue on

            // N/A should only be calculated for sum and subtract formulas
            if (field.get('type') === 'yesno' && field.get('value') === 'na' && !allow_na_values) {
                return true;
            }

            if (
                do_not_count_unanswered &&
                (field.get('value') === '' || field.get('value') === null || field.get('value') === undefined)
            ) {
                return true; // Do not count the field since unanswered, keep looping
            }

            var weight = _this.get_field_weight(event, field, model);

            if (weight == null || weight == undefined) {
                return false;
            }

            switch (operation) {
                case 'pct':
                    denominator += _this.get_max_weight(weight, event, field, model);
                    total += weight;
                    break;
                case 'avg':
                    denominator++;
                    total += weight;
                    break;
                case 'sum':
                    total += weight;
                    break;
                case 'adv':
                    formula_advanced = $f.utils.formula._replace_question_id({
                        formula: formula_advanced,
                        field_id: field.id,
                        weight: weight
                    });
                    break;
                case 'sbt':
                    // If it is the first value, then use that for total and subtract from there
                    // else you will be doing 0 - weight
                    total = is_first ? weight : total - weight;
                    break;
            }
            is_first = false;
            return true; // keep looping
        });

        var value = null;
        switch (operation) {
            case 'pct':
                if (denominator != 0) {
                    value = (total / denominator) * 100;
                }
                break;
            case 'avg':
                if (denominator != 0) {
                    value = total / denominator;
                }
                break;
            case 'sum':
                value = total;
                break;
            case 'sbt':
                value = total;
                break;
            case 'adv':
                value = this._extract_equation(formula_advanced);
                break;
        }

        var max_result = model.attributes.max_result;
        var min_result = model.attributes.min_result;
        // Due to some strangeness with Javascript floating points, we need to trim our decimal place
        // for an accurate calculation. For example, 1.6 + 0.8 = 2.4000000000000004. (See below value.toFixed())
        // More Info: http://stackoverflow.com/a/588014/477632, http://floating-point-gui.de/
        var result_value = value == null ? null : parseFloat(value.toFixed(2));

        if (result_value != null) {
            if (max_result != null && result_value > parseFloat(max_result)) {
                result_value = max_result;
            }
            if (min_result != null && result_value < parseFloat(min_result)) {
                result_value = min_result;
            }
        }
        model.set('value', result_value);
    },

    /**
     * Get the max weight for the given field.
     *
     * For traditional formulas for range fields: we'll use the higher value from field.get('options').
     * For everything else, including score-by-range formulas: we'll get the max_weight off the object if it's set.
     *
     *  @param {number} The current weight value of the field
     *  @param {Event} event The event
     *  @param {BaseField} field The field the weight is for.
     *  @param {BaseField} formula_field The formula field calculating the weight value for
     **/
    get_max_weight: function (weight, event, field) {
        if (field.get('type') === 'range' && (!event.weights || !event.weights.length)) {
            // if traditional range formula
            var options = field.get('options');
            if (options instanceof Array && options.length === 2) {
                return options[1];
            }
        }

        // for score by range and everything else
        if (typeof event.max_weight === 'undefined' || event.max_weight === null) {
            return weight;
        }

        return event.max_weight;
    },

    /**
     *  Get the weight value for a formula field given a field
     *
     *  @param {Event} event The event
     *  @param {BaseField} field The field to get the weight value for
     *  @param {BaseField} formula_field The formula field calculating the weight value for
     */
    get_field_weight: function (event, field, formula_field) {
        var check_value = undefined;

        var _clean_value = function (_val) {
            // check if strings are coming through as boolean. this happens
            // with yesno and checkbox
            if (typeof _val !== 'string') {
                return _val;
            }

            if (_val == 'true' || _val == 'false') {
                return _val === 'true'; // this will evaluate to boolean true/false
            } else if (_val === '') {
                return null;
            }

            return _val;
        };

        var get_weight_field_value = function (_event, _field) {
            if (!('weights' in _event) || !_event.weights || _event.weights.length === 0) {
                return parseFloat(_field.get('value'));
            }

            if (check_value === undefined) {
                check_value = _field.get('value');
            }

            // null range values have no weight
            if (check_value === null || check_value === '') {
                return 0;
            }

            for (var i in _event.weights) {
                var weight = _event.weights[i];
                var weight_field_value = weight.field_value;
                var operator_id = weight.operator_id;
                var val = weight.value;
                if (val === null || typeof val === 'undefined') {
                    return null;
                }

                if (operator_id === '<' && check_value < weight_field_value) {
                    return parseFloat(val);
                }

                if (
                    operator_id === 'between' &&
                    check_value >= weight_field_value[0] &&
                    check_value < weight_field_value[1]
                ) {
                    return parseFloat(val);
                }

                if (operator_id === '>=' && check_value >= weight_field_value) {
                    return parseFloat(val);
                }
            }
        };

        var field_value = 0;
        switch (field.get('type')) {
            case 'number':
            case 'range':
                if (formula_field.get('metadata').formula_version < 3) {
                    field_value = parseFloat(field.get('value'));
                    break;
                }

                field_value = get_weight_field_value(event, field);

                break;
            case 'temperature':
            case 'stopwatch':
                if (formula_field.get('metadata').formula_version < 3) {
                    break;
                }

                field_value = get_weight_field_value(event, field);

                break;
            case 'formula':
                field_value = parseFloat(field.get('value'));
                break;
            case 'checkbox':
                // If checkbox and value is not set, set to false
                if (
                    field.get('type') == 'checkbox' &&
                    (field.get('value') == null || field.get('value') == '' || field.get('value') == undefined)
                ) {
                    check_value = false;
                }
            // No break here continues calculation below
            /* // eslint-disable-next-line no-fallthrough */
            case 'yesno':
            case 'select':
                // NOTE: checkbox sets check_value above

                var val = null;
                check_value = check_value == undefined ? field.get('value') : check_value;

                if (formula_field.get('metadata').formula_version < 2) {
                    val = event.weights[check_value];
                } else {
                    check_value = _clean_value(check_value);
                    if (check_value !== null && typeof check_value === 'object') {
                        // @jira:FORMS-155 @author:bosco Handle change select allowing more than one value.  If more than one value, field_value will not be a number
                        if (check_value.length == 1) {
                            check_value = check_value[0];
                        } else {
                            // Not single value so cannot get weight properly
                            break;
                        }
                    }
                    for (var i in event.weights) {
                        var id = _clean_value(event.weights[i].id);
                        if (id === check_value) {
                            val = event.weights[i].value;
                            break;
                        }
                    }
                }
                // check to see if a value was found
                if (val == null || val == undefined) {
                    return null;
                }
                field_value = parseFloat(val);
                break;
        }

        return isNaN(field_value) ? 0 : field_value;
    },

    /**
     * This will run through an entire collection and calculate all the formula
     * fields.  This is used upon first rendering a form to make sure there is
     * a default calculation
     */
    calculate_all: function (collection) {
        var this_ = this;
        _.each(collection.where({ type: 'formula' }), function (model) {
            this_.calculate(model, collection);
        });
    },
    /*
        src http://clarkfeusier.com/2015/01/16/interview-question-balanced-parentheses-stack.html
    */
    _are_parentheses_balanced: function (formula) {
        var stack = [];
        var open = { '(': ')' };
        var closed = { ')': true };

        for (var i = 0; i < formula.length; i++) {
            var chr = formula[i];
            if (open[chr]) {
                stack.push(chr);
            } else if (closed[chr]) {
                if (open[stack.pop()] !== chr) {
                    return false;
                }
            }
        }

        return stack.length === 0;
    },

    /**
     * Replace the question id (i.e. Q1, Q11 etc) with the value for that
     * field in the provided formula.
     *
     * @param {obj} options     containing a field_id, weight and formula
     * @return {String}         the substituted formula
     */
    _replace_question_id: function (options) {
        if (options === undefined || (options.field_id !== 0 && !options.field_id)) {
            throw new Error('Must provide options with a field with an id');
        }

        var lookahead = '(?=\\-|\\+|\\*|\\/|\\s|\\(|\\)|$)';
        var regex = new RegExp('Q' + options.field_id + lookahead, 'gi');
        return (options.formula || '').replace(regex, options.weight || 0);
    },

    _validate_equation: function (value) {
        value = value || '';
        var user_formula = value.replace(/\s*/g, ''); // strip of all white spaces

        var excluded_symbols = /[!$%^&_|~=`{}\[\]:";'<>?,]/g; // check for symbols that are not operations (+,-,/,*,.)
        var letters = /(Q[^\d]+)|[^q()+*\-\/((\d+)|(\d+)\.\d*)]/gi; // only accept q in all of it's forms
        var valid_formula =
            /((\d+)|(\d+)\.\d*)|(Q[0-9]+)|(Q([0-9]+)[+\-\*\/])|(((\d+)|(\d+)\.\d*)[+\-\*\/]((\d+)|(\d+)\.\d*))/gi;

        // check if there are symbols or letters aside from q
        if (excluded_symbols.test(user_formula) || letters.test(user_formula)) {
            return false;
        }

        // regex to test if equation is valid
        return valid_formula.test(user_formula) && this._are_parentheses_balanced(user_formula);
    },
    /**
     * This will go through the advanced formula equation and Extract the result using regex
     * to determine if the formula is a valid equation and eval to evaluate the result.
     * If the result is either an infinity, not a number or null, it will return null.
     *
     * @param {value} The value of the adv. field
     */
    _extract_equation: function (value) {
        value = value || '';

        if (!this._validate_equation(value)) {
            return null;
        }

        try {
            var user_formula = value.replace(/\s*/g, '');
            var a = eval(user_formula);

            if (isNaN(a) || !isFinite(a)) {
                throw new Error('The result is not a number or infinity');
            }

            return a;
        } catch (err) {
            return null;
        }
    }
};

/**
 * Our own base model with common functionality used across Zenput, including:
 *
 * Submodels: specify which model attriibutes are models, and events from those models will be passed
 * up to this parent model. Define them with:
 *
 *     submodels: {
 *         <attribute name>: <model class>
 *     }
 *
 * Model history: save "snapshots" of the model state and restore them as needed to provide undo functionality:
 *
 *     model.store(): save the model's current state as a "snapshot"
 *     model.restore(): revert to the last snapshot
 *     model.clear_history(): clear out all model history "snapshots"
 */
export const BaseZenputModel = Backbone.Model.extend({
    legacy_classname: '$f.models.BaseZenputModel',

    // @todo @author:david we should eventually build a reset_to_defaults() function
    // into this, but we need to be careful about nulls in defaults that we immediately
    // set to [] or {} in initialize()
    //
    // @see http://stackoverflow.com/questions/6889457/easiest-way-to-reset-backbones-model-to-initial-defaults
    //
    // @todo @author:david we should also extend Backbone's default `clone` method to take into
    // account the submodels we define here.)

    _model_history: null, // [] - a queue of historical states of this model

    /**
     * Specify models within this model here as { attribute : submodel_class }
     * Change events from these submodels will be translated to events on this model
     * Can also be overriden with a function. Only access with this._get_submodels()
     */
    submodels: {},

    constructor: function (attributes, options) {
        attributes = this._get_submodeled_attributes(attributes);
        BaseZenputModel.__super__.constructor.call(this, attributes, options);
        this._model_history = [];
    },

    /**
     * Store's a JSON representation of the model in _model_history to be accessed later in time.
     *
     * @see restore() to restore from _model_history
     */
    store: function () {
        this._model_history.push(JSON.stringify(this.toJSON()));
    },

    /**
     * Retrieve a JSON representation of the model from _model_history
     *
     * @see store() to save in _model_history
     */
    restore: function () {
        if (this._model_history.length == 0) {
            return;
        }
        this.set(JSON.parse(this._model_history.pop()));
    },

    /**
     * Clear's the model's _model_history
     */
    clear_history: function () {
        this._model_history = [];
    },

    set: function (key, val, options) {
        // Below copied from Backbone src to handle setting both ('attr', 'val') and { attr: 'val' }
        var attrs = null;
        if (key == null) {
            return this;
        }
        if (typeof key === 'object') {
            attrs = key;
            options = val;
        } else {
            (attrs = {})[key] = val;
        }

        attrs = this._get_submodeled_attributes(attrs);
        return BaseZenputModel.__super__.set.call(this, attrs, options);
    },

    /**
     * Normalize submodel attributes to allow either an object or function that returns an object.
     *   Follows pattern of events, templateHelpers etc in Marionette.
     * @private
     * @returns { Object }
     */
    _get_submodels: function () {
        if (!this.submodels) {
            return {};
        }
        return _.result(this, 'submodels');
    },

    /**
     * Takes a set of attributes and converts any that should be models (as defined by this.submodels) into models.
     * If the attribute value is null, we will keep it null and not convert it into its Backbone model type.
     *
     * @param  {object} attributes the attributes to convert
     * @return {object}            the cleaned attributes with submodels
     *
     * @see this.submodels
     */
    _get_submodeled_attributes: function (attributes) {
        if (!attributes) {
            return {};
        }
        var submodels = this._get_submodels();
        if (Object.keys(submodels).length == 0) {
            return attributes;
        }

        var this_ = this;
        $.each(submodels, function (submodel_attr, klass) {
            var current = attributes[submodel_attr];
            if (!current) {
                return true;
            }

            if (current instanceof Backbone.Model || current instanceof Backbone.Collection) {
                return true;
            }
            attributes[submodel_attr] = new klass(attributes[submodel_attr]);
        });

        // Pass all change events from new submodels up to the main event
        $.each(attributes, function (key, val) {
            if (!(val instanceof Backbone.Model) && !(val instanceof Backbone.Collection)) {
                return true;
            }

            if (!submodels[key]) {
                // We will only forward events for attributes that are explicitly marked as submodels. This is safer,
                // since theoretically we could have a model in our attributes that is a model holding us in its
                // attributes (which would cause an infinite event loop).
                return true;
            }

            this_.stopListening(val, 'change'); // in case we set listeners on this before
            this_.listenTo(val, 'change', function (event) {
                this_.trigger('change', val);
                this_.trigger('change:' + key, val);
            });
        });

        return attributes;
    },

    parse: function (response) {
        // Please note that many submodels override this without calling super, update as seen to use super
        return $f.utils.parse_zenput_api_response(this, response);
    }
});

export const BaseZenputCollection = Backbone.Collection.extend({
    legacy_classname: '$f.collections.BaseZenputCollection',

    typeahead_search_attrs: ['name'], // typeahead collection attributes to search when searching locally
    cache_options: null, // if this is set, then use ajax caching
    is_fetching: false,
    is_fetched: false,
    search_param: 'search_query', // url param to use for model searches

    // @author:david we should include paginator setup here, with a paginator_options class variable.

    /**
     * Add ajax caching if cache_options is set
     * cache_ttl: seconds for cache length, default is 1 minute, set to false if you want an indefinite cache
     * cache_buster: invalidate cache if it does exist
     */
    fetch: function (options) {
        var this_ = this;
        options = options || {};
        var success = options.success || function () {};
        var error = options.error || function () {};
        this.cache_options = this.cache_options || {};

        if (this.cache_options.cache_buster) {
            Backbone.fetchCache.clearItem(this);
        }

        if (this.cache_options.cache) {
            options.cache = true;
            options.expires = this.cache_options.cache_ttl || 60;
        }

        this.is_fetching = true;

        // extend behavior on success cb
        options.success = function (resp) {
            success.apply(this_, arguments);
            this_.set_fetch_states(true);
        };

        // extend behavior on failure cb
        options.error = function (resp) {
            error.apply(this_, arguments);
            this_.set_fetch_states(false);
        };

        return BaseZenputCollection.__super__.fetch.call(this, options);
    },

    /**
     * Handle setting the fetched state on success/error callbacks
     *
     * @param success: Boolean - did it successfully fetch
     */
    set_fetch_states: function (success) {
        this.is_fetching = false;
        this.is_fetched = success;
    },

    parse: function (response) {
        // Please note that many submodels override this without calling super, update as seen to use super
        return $f.utils.parse_zenput_api_response(this, response);
    },

    /**
     * DEPRICATED: Internal method for searching what's in the collection without fetching from the server.
     * Externally, better to use search() with search_online = false
     *
     * @depricated This is unecessary for our new caching mechanism (which uses backbone.fetch-cache-mobile.js's _filter_results_for_local_search())
     */
    _collection_typeahead_search: function (query) {
        if (!query) {
            return this;
        }
        var this_ = this;

        if (this.cache_enabled) {
            // if cache_enabled (cache 2.0), this will/should be done behind the scenes during fetch
            console.warn(
                'WARNING: using _collection_typeahead_search() with Cache 2.0. This degrades performance and may lead to incorrect results.'
            );
        }

        query = $f.utils.escape_for_regex(query.toLowerCase());
        return this_.filter(function (data) {
            var pattern = new RegExp(query, 'gi');

            var match_str = '';
            $.each(this_.typeahead_search_attrs, function (i, attr) {
                match_str += data.get(attr) ? ' ' + data.get(attr) : '';
            });
            match_str = match_str.trim().toLowerCase();

            return pattern.test(match_str);
        });
    },

    /**
     * Search from server with fallback to local search.
     *
     * @param  {string}   query             The query to search.
     * @param  {Function} callback          What to do after searching both on the server and locally.
     * @param  {boolean}  search_online     (Defaults to true.) If true, will search online and fallback to a local search.
     *                                      If false, will only search locally. @note: this is only honored by collections that
     *                                      are not using our cache 2.0 system, which handles offline results internally.
     */
    typeahead_search: function (query, callback, search_online) {
        if (!query) {
            query = '';
        }
        callback || (callback = function () {});
        var this_ = this;

        /**
         * Do not search online if requested. However, if this collection uses our caching system
         * (built into Backbone.Collection, this.cache_enabled == true), we will ignore search_online=false, as that's handled internally
         * by our caching plugin.
         *
         * @author:david offline searching here will no longer be necessary once everything's using our new cache.
         */
        if (!this.cache_enabled && search_online === false) {
            return callback(this._collection_typeahead_search(query));
        }

        var _cb = function () {
            if (this_.cache_enabled) {
                // our new cache does searching internally, no need to use _collection_typeahead_search()
                callback(this_);
            } else {
                // collections not using our new cache filter results through _collection_typeahead_search() after we get them
                callback(this_._collection_typeahead_search(query));
            }
        };
        var paginator_values = { start: 0 };
        paginator_values[this.search_param] = query;
        this.paginator.set(paginator_values);
        this.fetch({
            reset: true,
            timeout: 20000,
            success: function () {
                _cb();
            },
            error: function () {
                _cb();
            }
        });
    },

    comparator: function (item) {
        var val = null;

        // make sure we have a proper paginator and 'order_by' value
        try {
            val = item.get(this.paginator.get('order_by'));
        } catch (e) {
            /* keep val as null */
        }

        if (typeof val === 'string') {
            val = val.toLowerCase();
        }
        return val;
    },

    /**
     * In memory search against the collection based on search_term.* regexp pattern
     * @param  {string} search_term What to search against
     * @param  {string} search_key  Attribute to search against for the model, only implemented for top-level attributes
     *                              If not provided, will default to the first typeahead_search_attrs if available.
     * @return {Collection}         A new colleciton of the same type, filtered down. ** No reference between this return and the original collection **
     */
    in_memory_search: function (search_term, search_key) {
        try {
            search_key = search_key || this.typeahead_search_attrs[0];
        } catch (e) {
            throw new Error('Unknown search_key for in_memory_search()');
        }

        if (search_term === '' || search_term === undefined) {
            return new this.constructor(this.models);
        }
        return new this.constructor(
            this.filter(function (data) {
                var pattern = new RegExp(search_term, 'gi');

                return pattern.test(data.get(search_key));
            })
        );
    }
});

export const ZenputStorage = BaseZenputModel.extend({
    legacy_classname: '$f.models.ZenputStorage',

    defaults: {
        expiration: 0,
        storage_base_url: '',
        read_auth: null,
        write_auth: null
    },

    _is_fetching: false,
    _refresh_callback_queue: null, // [{ success, error }] - a queue of success/error callbacks so that if this is called
    // twice in a row, we don't have to make two requests

    url: '/api/v2/users/current/storage_auth/',
    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this._refresh_callback_queue = [];

        this._load_bootstrapped_data();
    },

    /**
     * Refreshes authentication data from the server if the expiration is approaching.
     *
     * @param options.success   A function to call when authentication is available
     * @param options.error     A function to call when authentication is not available
     * @param options.complete  A function to call when we've succeeded or failed to re-authenticate.
     */
    refresh: function (options) {
        options = $f.utils.setup_options_callbacks(options); // this builds options.complete() into success and error
        var this_ = this;

        if (!this._is_expiring()) {
            options.success();
            return;
        }

        this._refresh_callback_queue.push(options);

        if (this._is_fetching) {
            return; // a request is already in progress. New callbacks will be fired when we get results back
        }

        this._is_fetching = true;
        this.fetch({
            success: function () {
                this_._is_fetching = false;
                this_._fire_all_refresh_callbacks_of_type('success');
            },
            error: function () {
                this_._is_fetching = false;
                this_._fire_all_refresh_callbacks_of_type('error');
            }
        });
    },

    /**
     * Call all callbacks of a specified type in our request queue. This will also empty the queue after
     * everything is called to make sure they're not called again.
     *
     * @param {success|error} The type of callback to call
     */
    _fire_all_refresh_callbacks_of_type: function (type) {
        while (this._refresh_callback_queue.length) {
            var options = this._refresh_callback_queue.pop();
            var fn = options[type];
            fn && fn();
        }
    },

    /**
     * Get a clean filename that only contains "0-9, a-Z, -, _". Spaces are turned into underscores.
     *
     * @warning Do NOT pass in a path or filename with file extension. This will remove "/"s and "."s
     *
     * @param  {string} filename The input filename
     * @return {string}          The sanitized output filename
     */
    sanitize_s3_filename: function (filename) {
        return filename.replace(/[^a-zA-Z 0-9_-]/g, '').replace(/ /g, '_');
    },

    /**
     * Create a filename for S3 based on a company ID, category, and filename.
     *
     *      Example: /company/<id>/<category>/[id|<instance id>]/year/month/day/<file>-<timestamp>.<extension>
     *
     * See our full design doc for more examples:
     * https://docs.google.com/document/d/16iFaoI4vTtDCcb7sFlg5w9he5NUIdIsQhxTWyMMOLc0
     *
     * @param  {string} company_id  The company ID associated with this file.
     * @param  {string} category    The category for this file. Can be split into subcategories with a "/"
     *                              (e.g., forms/submissions and forms/templates).
     * @param  {string} instance_id (optional) the instance ID of the task, template, etc.
     * @param  {string} filename    The ending filename to use. We will append this with a timestamp to make it unique.
     *
     * @return {string}             An S3 path, starting with company/
     */
    create_s3_filepath: function (company_id, category, instance_id, filename) {
        if (!filename) {
            throw new Error('Programmer Error: Unknown filename in $f.storage.create_s3_filepath');
        }
        if (!category) {
            throw new Error('Programmer Error: Unknown category in $f.storage.create_s3_filepath');
        }
        if (!company_id) {
            throw new Error('Programmer Error: Unknown company_id in $f.storage.create_s3_filepath');
        }

        if ((instance_id && typeof instance_id !== 'number') || instance_id % 1 !== 0) {
            throw new Error('Programmer Error: instance_id must be an integer');
        }

        if (typeof company_id !== 'number' || company_id % 1 !== 0) {
            throw new Error('Programmer Error: company_id must be an integer');
        }

        var filename_arr = filename.split('.');
        var file_ext = filename_arr.length > 1 ? '.' + filename_arr.pop() : '';
        filename = filename_arr.join('.');
        filename = this.sanitize_s3_filename(filename);
        filename = `${filename}-${new Date().getTime() + _.random(0, 99999)}${file_ext}`;

        var path = `company/${company_id}/${category}/`;

        if (instance_id) {
            path += `id/${instance_id}/`;
        }

        path += `${moment.utc().format('YYYY/MM/DD')}/${filename}`;

        return path.toLowerCase();
    },

    /**
     * Creates a filename for a Form Submission media file to upload into s3, based on active_collection.
     *
     * This can take either an entire submission FormStructure, or just the rdbs_id and company_id
     *
     * @param {string}  file_name   The filename to be set
     * @param {int}     rdbms_id    The rdbms_id of the template
     * @param {int}     company_id  The company id of the template
     *
     * @returns an S3 path in the format of this.create_s3_filepath()
     */
    create_form_submission_media_filename: function (file_name, rdbms_id, company_id) {
        return this.create_s3_filepath(company_id, 'forms/submissions', rdbms_id, file_name);
    },

    /**
     * Given an S3 URL, we will append the thumbnail size right before
     * the file extension.  i.e. 'image-88x88.jpg'
     *
     * @param url - url to S#
     * @param size - 88 or 234 (default is 234)
     */
    get_thumbnail: function (url, size) {
        size = size || '234';
        const imageUrl = url.s3_key ? url.s3_key : url;
        return imageUrl.replace(/\.([^.]+)$/, `-${size}x${size}.$1`);
    },

    /**
     * Given an URI it returns whether or not it is a data URI or a cordova file plugin URI.
     * @param uri
     * @returns {boolean|*|{get}}
     */
    is_data_uri: function (uri) {
        const filePath = uri.s3_key ? uri.s3_key : uri;
        // startsWith  method is not not be available in all JavaScript implementations yet
        if (!String.prototype.startsWith) {
            String.prototype.startsWith = function (searchString, position) {
                position = position || 0;
                return this.substr(position, searchString.length) === searchString;
            };
        }
        return (
            filePath.startsWith('data:') ||
            filePath.startsWith('cdvfile:') ||
            filePath.startsWith('ms-appdata:') ||
            filePath.startsWith('file:')
        );
    },

    /**
     * Given a thumbnail size, return the given url without the thumbnail size
     *
     * @param url - url for the thumbnail
     * @param size - 88 or 234 default is 234
     */
    get_high_res_url_from_thumbnail: function (url, size) {
        size = size || '234';
        return url.replace('-' + size + 'x' + size + '.', '.');
    },

    /**
     * Checks the file path passed in and returns the extension
     * @param  {string} path    url path of a file
     * @return {string}         file extension
     */
    get_file_extension: function (path) {
        const filePath = path.s3_key ? path.s3_key : path;
        return filePath.substr(filePath.lastIndexOf('.') + 1).toLowerCase();
    },

    /**
     * Gets a FormData object with file and authentication details for direct uploads to Zenput Storage (S3).
     * This will request new authentication if necessary.
     *
     * @param {object} options Options for creating the FormData
     */
    get_upload_form_data: function (options) {
        options.success = options.success || function () {};
        options.error = options.error || function () {};
        var this_ = this;

        this.refresh({
            success: function () {
                options.success(
                    this_._get_upload_form_data_from_auth({
                        write_auth: this_.get('write_auth'),
                        file: options.file,
                        filename: options.filename,
                        file_type: options.file_type
                    })
                );
            },
            error: function () {
                options.error();
            }
        });
    },

    /**
     * Upload a file to Zenput Storage (S3). This will fetch new authentication from zenput.com
     * beforehand if necessary (@see get_upload_form_data()).
     *
     * @param  {dict}       options             important options for the upload, including the filename, url, etc.
     * @param  {string}     options.filename    The filename for the file to upload
     * @param  {File}       options.file        The file object to upload
     * @param  {string}     options.mime_type   The mime type of the file. Default: file.type
     * @param  {number}     options.timeout     The timeout for the upload. Default: 30000
     * @param  {function}   options.success     success() callback, passes back the following:
     *                                              new_url: the filename confirmed by amazon
     *                                              base64_string: if supported by the browser, the base64 src for the uploaded file
     * @param  {function}   options.error       error() callback
     * @param  {function}   options.on_progress callback for reporing progress
     */
    perform_file_upload: function (options) {
        options = $f.utils.setup_options_callbacks(options);
        options.on_progress = options.on_progress || function () {};
        options.timeout = options.timeout || 30000;
        var this_ = this;

        if (!options.filename) {
            throw new Error('Programmer error: no filename specified for S3 upload.');
        }
        if (!options.file) {
            throw new Error('Programmer error: no file specified for S3 upload.');
        }

        if (!window.FormData) {
            options.error('Your browser does not support this feature.');
            return;
        }

        var xhr = new XMLHttpRequest();

        if (xhr.upload) {
            xhr.upload.onprogress = function (event) {
                if (!event.lengthComputable) {
                    return;
                }

                options.on_progress((event.loaded / event.total) * 100);
            };
        }

        var file_type = options.mime_type || options.file.type;

        this.get_upload_form_data({
            file: options.file,
            filename: options.filename,
            file_type: file_type,
            success: function (form_data) {
                $.ajax({
                    type: 'POST',
                    url: this_._get_upload_url(),
                    processData: false,
                    contentType: false,
                    data: form_data,
                    timeout: options.timeout,
                    xhr: function () {
                        return xhr;
                    },
                    error: function () {
                        options.error('Unable to upload photo.');
                    },
                    success: function () {
                        if (!window.FileReader || file_type.indexOf('image') === -1) {
                            // can't generate base64 or shoudln't because it's not an image
                            options.success(options.filename);
                            return;
                        }

                        // For convenience, try to calculate base64 of file if supported
                        var fr = new FileReader();
                        fr.onloadend = function () {
                            options.success(options.filename, fr.result);
                        };
                        fr.onerror = function () {
                            options.success(options.filename);
                        };
                        fr.readAsDataURL(options.file);
                    }
                });
            },

            error: function (response) {
                options.error(response.message || 'Unable to upload photo.');
            }
        });

        return xhr;
    },

    /**
     * Get the base URL for files hosted on Zenput Storage.
     */
    get_storage_base_url: function () {
        return this.get('storage_base_url');
    },

    /**
     * Get a signed URL for an S3 path. This will not re-sign if signing is included in the path.
     *
     * @param {string} path The path to use in building the URL.
     */
    get_storage_full_url: function (path) {
        var full_path = `${this.get_storage_base_url()}/${path}`;
        var read_auth = this.get('read_auth');
        var company_id = $f.utils.get_active_company().id;

        if (full_path.indexOf('Policy=') !== -1) {
            return full_path; // path was already signed - possibly by the server. Don't overwrite it
        }

        if (typeof path === 'string' && path.indexOf(`company/${company_id}/`) !== 0) {
            const baseUrl = $f.base_url || API_URL; // webapp vs mobile
            return `${baseUrl}/api/v2/users/current/storage?path=${path}&redirect=true`;
        }

        if ($f.utils.get_active_company().has_feature('unrestricted_s3')) {
            return full_path;
        }

        if (!read_auth) {
            return full_path; // shouldn't happen, but could if our servers are down, etc.
        }

        return `${full_path}?${read_auth.query_string}`;
    },

    /**
     * Webapp only: load any authentication info that's been bootstrapped into the page for us
     */
    _load_bootstrapped_data: function () {
        if (!$f.storage_auth_data) {
            return;
        }

        this.set($f.storage_auth_data);
    },

    /**
     * Determine if this authentication is expired or expiring soon.
     */
    _is_expiring: function () {
        var expiration = moment(this.get('expiration'));
        return expiration.subtract(1, 'hour').isBefore(moment());
    },

    /**
     * Get the URL used for direct uploads.
     */
    _get_upload_url: function () {
        var write_auth = this.get('write_auth');
        var storage_url = this.get('storage_base_url');

        if ($f.utils.get_active_company().has_feature('unrestricted_s3')) {
            return storage_url;
        }

        if (!write_auth) {
            return storage_url;
        }

        return `${storage_url}/?${write_auth.query_string}`;
    },

    /**
     * Get a FormData containing the necessary data and authorization for direct uploads to Zenput Storage (S3).
     *
     * @param {object}  options.write_auth  The 'write' authentication to build the FormData with
     * @param {File}    options.file        The File object to upload
     * @param {string}  options.filename    The name of the file (including 'path') being uploaded
     * @param {string}  options.file_type   The content-type of the file
     *
     * @returns {FormData} The authorized FormData structure used for uploading to Zenput Storage.
     */
    _get_upload_form_data_from_auth: function (options) {
        var fd = new FormData();

        fd.append('key', options.filename);
        fd.append('Content-Type', options.file_type);

        fd.append('acl', options.write_auth.acl);
        fd.append('policy', options.write_auth.policy_base64);
        fd.append('signature', options.write_auth.signature);
        fd.append('AWSAccessKeyId', options.write_auth.key_id);
        fd.append('x-amz-server-side-encryption', options.write_auth.encryption);

        // IMPORTANT: this must go last. Per S3 docs: "this field must be the last one in the form, as any
        // fields below it are ignored by S3."
        fd.append('file', options.file);

        return fd;
    }
});

// Acceptance Criteria Models

export const AcceptanceCriterionMetadata = BaseZenputModel.extend({
    legacy_classname: '$f.models.AcceptanceCriterionMetadata',

    defaults: {
        acceptance_value: 0 // 0 = netural, 1 = pass, -1 = fail.
    }
});

export const AcceptanceCriterionCondition = BaseZenputModel.extend({
    legacy_classname: '$f.models.AcceptanceCriterionCondition',

    defaults: {
        condition_value: null, // single value or array depending on operator
        operator_id: null // string, see $f.utils.evaluate_operator for possible enumerations
    },

    /**
     * Get display values for <= and >= as single unicode characters.
     * Note: not currently appropriate for range operator ids, but may be extended if we are creating to_strings
     * of these conditions.
     * @returns {string}
     */
    get_display_for_operator_id: function () {
        switch (this.get('operator_id')) {
            case '>=':
                return '≥';
            case '<=':
                return '≤';
            default:
                return this.get('operator_id');
        }
    },

    evaluate_value: function (value) {
        return $f.utils.evaluate_operator(this.get('operator_id'), value, this.get('condition_value'));
    },

    /**
     * Does this operator_id require 2 values
     *
     * @returns {boolean}
     */
    is_range_operator: function () {
        return _.contains(['outside', 'between'], this.get('operator_id'));
    }
});

export const AcceptanceCriterion = BaseZenputModel.extend({
    legacy_classname: '$f.models.AcceptanceCriterion',

    submodels: {
        metadata: AcceptanceCriterionMetadata,
        condition: AcceptanceCriterionCondition
    },

    initialize: function () {
        if (!this.get('metadata')) {
            this.set('metadata', new this.submodels.metadata());
        }
        if (!this.get('condition')) {
            this.set('condition', new this.submodels.condition());
        }
    }
});

/**
 * Acceptance Criteria holds the criteria to assign the value of field to either pass/fail or neutral
 * based on the raw field input.
 */
export const AcceptanceCriteriaCollection = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.AcceptanceCriteria',

    model: AcceptanceCriterion,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    /**
     * Filter all criteria for those that are for pass or failure and then return if
     *    any of those criteria evaluate to true against the value
     *
     * @param acceptance_value {0|1|-1}
     * @param value {any} - field value to compare
     * @returns {boolean}
     */
    _evaluate_acceptance_values: function (acceptance_value, value) {
        var filtered_criteria = this.filter(function (criterion) {
            return criterion.get('metadata').get('acceptance_value') == acceptance_value;
        });
        var num_matches = filtered_criteria.reduce(function (memo, criterion) {
            return memo + (criterion.get('condition').evaluate_value(value) ? 1 : 0);
        }, 0);
        return num_matches > 0;
    },

    /**
     * Get the first criteria that evaluates to true against the value
     *
     * @param value {any} - field value to compare
     * @returns {AcceptanceCriterion || null} (null if not found)
     */
    get_matched_criteria: function (value) {
        var matched_criteria = this.find(function (criterion) {
            if (criterion.get('condition').evaluate_value(value)) {
                return true;
            }
        });

        return matched_criteria;
    },

    /**
     * Determine an acceptance value(-1,0,1) given a field input value.
     * Failure criteria -> Pass criteria -> Neutral
     *
     * @param value { any }
     * @returns {number} -1 is a failure, 0 is neutral, and 1 is passing
     */
    evaluate_acceptance_value: function (value) {
        if (this._evaluate_acceptance_values(-1, value) === true) {
            return -1;
        }
        if (this._evaluate_acceptance_values(1, value) === true) {
            return 1;
        }
        return 0;
    },

    /**
     * Helper to find the a criterion with a condition.condition_value matching the condition_value passed in
     * @param condition_value {any}
     * @private
     * @returns {AcceptanceCriterion || null} (null if not found)
     */
    find_criterion_with_condition_value_of: function (condition_value) {
        var criteria = null;
        this.each(function (criterion) {
            if (criterion.get('condition').get('condition_value') === condition_value) {
                criteria = criterion;
            }
        });
        return criteria;
    },

    /**
     * Cheap and dirty way to recursively convert all submodels to json, and return a pure POJO
     * @returns {object}
     */
    toJSON: function () {
        return JSON.parse(JSON.stringify(this.models));
    }
});

//
// Models
//

/**
 * Our base model class for all form fields
 */
export const BaseField = BaseZenputModel.extend({
    legacy_classname: '$f.models.BaseField',

    acceptance_criteria_klass: AcceptanceCriteriaCollection,

    // @NOTE:bosco - Do not put {} here, it is pass by reference, so all BaseField will get changes
    defaults: {
        id: null,
        key: '', // currently unused
        type: null,
        title: null,
        instructions: '',
        value: '',
        file: null,
        default_value: null,
        hidden: false,
        visible: true,
        metadata: null,
        options: null, // []
        events: null, // [] These are dependent field events, see below for a more thorough discussion.
        title_image: null, // []
        acceptance_value: null // 1, 0, -1 (pass, neutral, fail)
        /**
         * activity_data: { field_id: , field_version_id: } // Only added if the form template has been migrated
         * // these are added in when a form is submitted
         * account:
         */
    },

    /**
     * Events array.
     *
     * Originally designed to handle multiple types of events, it is only implemented for dependent fields
     *  changes.
     *
     *  Each event is of the type {target: id, value: <any>, acceptance_values: [], event: 'change'}
     *
     *  Fields:
     *  event - always 'change'
     *  target - field id to show or hide based on the fields value
     *  value: either a single value OR a complex POJO that constructs FieldEventSearchCriterias
     *  acceptance_values: (optional - only for fields that use acceptance_criteria)
     *     array of values that match against the acceptance_value of the field.
     *
     */

    submodels: function () {
        return {
            acceptance_criteria: this.acceptance_criteria_klass
        };
    },

    initialize: function () {
        if (!this.get('title_image')) {
            this.set('title_image', [], { silent: true });
        }
        if (!this.get('options')) {
            this.set('options', [], { silent: true });
        }
        if (!this.get('events')) {
            this.set('events', [], { silent: true });
        }
        if (!this.get('metadata')) {
            this.set('metadata', {}, { silent: true });
        }

        // @author:david this is bad. We shouldn't run mobile code in forms.js. For now, just checking $f.mobile
        if ($f.mobile && (typeof this.id == 'undefined' || this.id === null) && $f.mobile.get_window_collection()) {
            var metadata = $f.mobile.get_window_collection().metadata;
            if (metadata && metadata.get('auto_increment_next_id')) {
                this.id = metadata.get('auto_increment_next_id');
            }
        }

        // @jira:MOBILE-696 May 2018: handle missing hidden flag for some dependent fields due to earlier bug
        // Formulas can't be dependent fields, yet they can still be 'hidden:true' for their own reasons, so ignore them
        if (this.get('type') !== 'formula') {
            if (this.get('metadata') && !_.contains([null, undefined], this.get('metadata').dependent_parent_id)) {
                this.set('hidden', true);
            }

            // @author:david unfortunately we can't assume hidden=false if there is no metadata.dependent_parent_id,
            // because forms modified with the old builder did not use this flag :'(
        }

        // @jira:FORMS-395 June 2018: Some section and instruction fields were being marked as required and preventing
        // forms from being submitted. This will require those blocks from pending submissions.
        if (this.get('required') === true && (this.get('type') === 'section' || this.get('type') === 'instructions')) {
            this.unset('required');
        }
    },

    /**
     * This gets called when you issue a 'remove' on a collection
     * that contains this model
     */
    remove: function () {
        this.trigger('destroy', this, this.collection);
    },

    /**
     * This gets called before submit action.
     * Overwrite if additional processing is needed.
     */
    get_submission_value: function () {
        return this.get('value');
    },

    /**
     *
     * Find an attribute of the metadata attribute
     * will throw an exception if metadata does not exist
     *
     * @param {*} attribute
     */
    get_metadata_attribute: function (attribute) {
        var metadata = this.get('metadata');

        if (metadata instanceof Backbone.Model) {
            return metadata.get(attribute);
        }

        return metadata[attribute];
    },

    /**
     *
     * Set an attribute of the metadata attribute
     * will throw an exception if metadata does not exist
     *
     * @param {*} attribute
     */

    set_metadata_attribute: function (attribute, value) {
        var metadata = this.get('metadata');

        if (metadata instanceof Backbone.Model) {
            return metadata.set(attribute, value);
        }

        return (metadata[attribute] = value);
    },

    is_dependent_parent: function () {
        return this.get('events') && this.get('events').length > 0;
    },

    is_dependent_field: function () {
        if (!this.get('metadata')) {
            return false;
        }

        var parent_id = this.get_metadata_attribute('dependent_parent_id');

        return parent_id === 0 || !!parent_id;
    },

    /**
     * If this is a 'hidden'/dependent field, search the form for the parent field it belongs to.
     * returns The parent field (or null if none)
     */
    get_dependent_field_parent: function () {
        if (this.is_dependent_field()) {
            var dependent_parent_id = this.get_metadata_attribute('dependent_parent_id');
            return this.collection.get(dependent_parent_id);
        }

        return null;
    },

    /**
     * Will return the lvl 0 dependent parent for any nested dependent field
     */
    get_dependent_group_parent: function () {
        var parent = this.get_dependent_field_parent();

        if (!parent && this.is_dependent_parent()) {
            return this;
        }

        if (parent) {
            return parent.get_dependent_group_parent();
        }

        return null;
    },

    /**
     * Determine how many levels deep this dependent field is nested
     */
    get_dependent_field_level: function () {
        var level = $f.utils.DEPENDENT_FIELD_TOP_LEVEL;
        if (!this.is_dependent_field()) {
            return level;
        }

        level++;

        var stack = [this.get_dependent_field_parent()];
        while (stack.length > 0) {
            var current_field = stack[0];
            var parent = current_field.get_dependent_field_parent();

            if (parent && parent.id !== current_field.id) {
                level++;
                stack.unshift(parent);
            }
            stack.pop();
        }

        return level;
    },

    // Sort the possible dependent fields by their form order.
    get_sorted_dependent_field_ids: function () {
        var this_ = this;
        if (!this.is_dependent_parent()) {
            return [];
        }

        return _.sortBy(this.get('events'), function (event) {
            if (!event.target) {
                return -1;
            }
            return this_.collection.metadata.get('order').indexOf(event.target.toString());
        })
            .map(function (event) {
                return event.target;
            })
            .filter(function (id) {
                return id !== null;
            });
    },

    /**
     * Returns the last field in a dependent parents descendent tree including all nested dependent parents
     * based on the order of the form template collection
     */
    get_dependent_parent_groups: function () {
        var this_ = this;
        var group_ends = [];
        if (!this.is_dependent_parent()) {
            return group_ends;
        }

        // Dependent parent with no dependent fields set
        if (this.get('events')[0].target === null) {
            return [[this.id, null]];
        }

        var sorted_dependent_field_ids = this.get_sorted_dependent_field_ids();

        // If the last dependent field for this field is not another parent add as the first end
        var dependent_field_id = _.last(sorted_dependent_field_ids);
        var last_dependent_field = this.collection.get(dependent_field_id);
        if (!last_dependent_field) {
            $f.utils.log_error('Invalid dependant field detected, please fix customer data', {
                form_template_id: this_.collection.metadata.get('rdbms_id'),
                field_id: dependent_field_id
            });
        }
        if (last_dependent_field && !last_dependent_field.is_dependent_parent()) {
            group_ends.push(dependent_field_id);
        }

        while (sorted_dependent_field_ids.length > 0) {
            var current_field_id = _.last(sorted_dependent_field_ids);
            var current_field = this.collection.get(current_field_id);
            sorted_dependent_field_ids.pop();

            if (!current_field || !current_field.is_dependent_parent()) {
                continue;
            }

            // This parent field has no dependents set. This is a group end.
            if (current_field.get('events')[0].target === null) {
                group_ends.push(current_field_id);
                continue;
            }

            // Add each dependent field to the end of the stack
            _.each(current_field.get_sorted_dependent_field_ids(), function (id) {
                return sorted_dependent_field_ids.push(id);
            });

            // Add the last dependent field to the group ends;
            var _last_dependent_field_id = _.last(sorted_dependent_field_ids);
            var field_in_form = this_.collection.get(_last_dependent_field_id);
            // this checks if the field exists in the form, to prevent adding an invalid dependent field to the group ends
            if (field_in_form) {
                group_ends.push(_last_dependent_field_id);
            }
        }

        return _.map(group_ends, function (end) {
            return [this_.id, end];
        });
    },

    /**
     * Traverse all nested fields and get the full list of decendents
     *
     * @return {array} ids
     */
    get_dependent_parent_descendents: function () {
        var this_ = this;

        if (!this.is_dependent_parent()) {
            return null;
        }

        var dependent_parents = [this];
        var descendent_ids = [];

        while (dependent_parents.length) {
            var dependent_field_ids = dependent_parents[0].get_sorted_dependent_field_ids();

            _.each(dependent_field_ids, function (field_id) {
                descendent_ids.push(field_id);
                var field = this_.collection.get(field_id);

                if (field.is_dependent_parent()) {
                    dependent_parents.push(field);
                }
            });

            dependent_parents.shift();
        }

        return this.collection.get_fields_sorted_by_order(descendent_ids);
    },

    is_section_parent: function () {
        return this.get('type') === 'section';
    },

    /**
     * Returns whether this field is a child of a section
     *
     * @return {boolean}
     */
    is_section_child: function () {
        return !!this.get_section_parent();
    },

    /**
     * Returns whether this field belongs to a section with dynamic visibility configured
     *
     * @return {boolean}
     */
    has_parent_with_dynamic_visibility: function () {
        return this.is_section_child() && this.get_section_parent().has_dynamic_visibility();
    },

    /**
     * Returns whether this field has dynamic visibility configured
     *
     * @return {boolean}
     */
    has_dynamic_visibility: function () {
        var dynamic_visibility = this.get_metadata_attribute('dynamic_visibility') || {};
        var has_visibility_choice = !_.isUndefined(dynamic_visibility.visibility_choice);
        var attribute_criteria_filters = dynamic_visibility.attribute_criteria_filters || { filters: [] };
        var has_filters = !_.isEmpty(attribute_criteria_filters.filters);
        return has_visibility_choice && has_filters;
    },

    is_dynamic_visibility_allowed: function () {
        const primary_location_field_exists =
            this.collection.filter((model) => model.is_primary_location()).length !== 0;
        if (this.is_primary_location() || this.is_dependent_field() || primary_location_field_exists === false) {
            return false;
        }

        return !this.has_parent_with_dynamic_visibility();
    },
    /**
     * Whether this field is the first location that appears in the form,
     * taking into account the order of the fields
     *
     * @return Boolean indicating if location is primary
     */
    is_primary_location: function () {
        if (this.get('type') !== 'account') {
            return false;
        }

        const accountFieldIdsOrdered = this.collection.metadata
            .get('order')
            .map((id) => ({ id: parseInt(id, 10), type: this.collection.get(id).get('type') }))
            .filter((f) => f.type === 'account');

        if (_.isEmpty(accountFieldIdsOrdered) || accountFieldIdsOrdered[0].id !== this.get('id')) {
            return false;
        }

        return this.is_dependent_field() === false && this.is_section_child() === false;
    },

    /**
     * Get the section parent field if any
     *
     * @return {BaseField || null}
     */
    get_section_parent: function () {
        var this_ = this;

        return (
            this.collection.where({ type: 'section' }).filter(function (section) {
                return section.get_metadata_attribute('section_field_ids').includes(this_.id);
            })[0] || null
        );
    },

    /**
     * Get next model with hidden:false within this collection
     *
     * @returns: {BaseField || null}
     */
    get_next_visible: function () {
        if (!this.collection) {
            return null;
        }
        var ordered_ids = this.collection.get_order();
        var current_idx = ordered_ids.indexOf(this.id);
        if (current_idx >= ordered_ids.length || current_idx == -1) {
            return null;
        }

        // loop through from the next element until we get a visible field.
        for (var idx = current_idx + 1; idx < ordered_ids.length; idx++) {
            var model = this.collection.get(ordered_ids[idx]);
            if (!model.is_currently_hidden) {
                return model;
            }
        }

        return null;
    },

    /**
     * Updates the field's metadata without removing exisiting one.
     * @param   {Object} options   a dict of options to be added/removed from metadata.
     * @see MobileDocumentField for usage
     */
    update_metadata: function (options) {
        options = options || {};
        var existing_metadata = this.get('metadata');

        this.set({
            metadata: $.extend({}, existing_metadata, options)
        });
    },

    /**
     * If we have acceptance criteria, set acceptance_value
     * @param (boolean) - default: false - should we set acceptance_criteria with { silent: true };
     */
    set_acceptance_value: function (use_silent_update) {
        use_silent_update = use_silent_update === undefined ? false : use_silent_update;
        var acceptance_criteria = this.get('acceptance_criteria');
        if (!acceptance_criteria) {
            return;
        }
        this.set('acceptance_value', acceptance_criteria.evaluate_acceptance_value(this.get('value')), {
            silent: use_silent_update
        });
    }
});

/**
 * Metadata stores information about the Collection of fields
 * i.e. the form itself.  This lives on the collection object
 */
export const FormMetadata = Backbone.Model.extend({
    legacy_classname: '$f.models.Metadata',

    defaults: function () {
        return {
            rdbms_id: null,
            title: 'New Form',
            auto_increment_next_id: 0,
            activity_template: null, // added if the form template has been migrated, becomes {"id": id}
            date_created: null,
            order: [],
            company: { id: null },
            created_by: {
                display_name: '',
                id: null
            },
            is_creator_lock: false,
            is_secure: false,
            is_copy_enabled: false, // is true if we are allowed to copy previous submissions
            is_unanswered_hidden: false, // if true, exporting submission pdf will hide unanswered questions
            permit_brandwide_submission_access: false, // if true, then brand wide submissions are shown on activity/<id>/submissions page
            is_force_camera: false, // if true, we will prevent users from selecting images from their gallery
            is_show_yesno_history: false, // if true, we will show answers from previous submissions under question
            is_shared_form: false,
            is_force_probe_input: false, // if true, we will block users from using the keyboard to fill out temperature fields
            allow_auditors_access_locations: false, // if true will allow auditors to submit forms at managing and reporting company locations,
            order_fields_by_location: false,
            allow_follow_up_actions: $f.flags.get_flag('new_forms_default_fua') ? true : false
        };
    }
});

export const FormSmetadata = Backbone.Model.extend({
    legacy_classname: '$f.models.FormSmetadata',

    defaults: {
        guid: null,
        time_zone: null, // the users timezone when a form is submitted
        date_created: null,
        date_submitted: null, // added when it hits the server
        date_modified: null, // last time modified
        date_completed: null, // added when the user hits submit
        time_to_complete: null, // Seconds to complete form based on date_completed - date_created
        created_by: null, // user id
        user_role: null, // user role {id: id, name: 'string' }
        lat: null,
        lon: null,
        distance_to_account: null, // Distance from submission lat lon to a single account lat lon
        environment: null, // either 'web' or 'app'
        platform: null, // either 'android' or 'ios'
        user_agent: null,
        task: null, // set only if form is submittted to complete a specific task
        project: null, // set only if task is part of a project,
        location_custom_field_order: null,
        follow_up_actions: null
    }
});

export const FormTemplate = Backbone.Model.extend({
    legacy_classname: '$f.models.FormTemplate',

    defaults: {
        id: null,
        title: null
    }
});

export const FormTemplates = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.FormTemplates',

    model: FormTemplate,
    user_id: null,
    group_id: null,
    role_id: null,
    is_public: null,
    category_id: null,
    shared_only: null,
    typeahead_search_attrs: ['title'],

    initialize: function (models, options) {
        // @author: kgstew need to check if we are passing in options correctly. Should be
        // Backbone.collection([models], {options}) but many of our legacy calls are Backbone.collection({options})
        if (typeof models === 'object' && !options) {
            options = models;
        }
        options = options || {};

        this.user_id = options.user_id || null;
        this.group_id = options.group_id || null;
        this.role_id = options.role_id || null;
        this.is_public = options.is_public || null;
        this.category_id = options.category_id || null;
        this.shared_only = options.shared_only || null;

        this.paginator = new Paginator({ order_by: 'title' });
    },

    url: function () {
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'category_id')) {
            attrs.push('category_id');
        }
        if (!_.contains(attrs, 'filter')) {
            attrs.push('filter');
        }

        if (this.group_id) {
            return '/api/v1/users/group/get_forms_access/' + this.group_id;
        } else if (this.user_id) {
            return '/api/v1/users/get_forms_access/' + this.user_id;
        } else if (this.role_id) {
            return '/api/v1/users/role/get_forms_access/' + this.role_id;
        } else if (this.is_public) {
            return '/api/v1/forms/list_brand_templates/?' + this.paginator.get_query_string({ get_count: true });
        } else {
            var paginator_query = this.paginator.get_query_string({ get_count: true });
            if (this.shared_only) {
                paginator_query = `shared_only=true&${paginator_query}`;
            }
            // This is the API we use for autocomplete. Override the collection url to use a different one
            return `/api/v1/forms/list_templates/?${paginator_query}`;
        }
    },

    // @author: kgstew There is a bug in backbone here where instantiating this collection with options will add an empty model
    // that does not get replaced if the collection fails to fetch records. Adding this generic parse will reset
    // the collection if it fetches no results
    parse: function (response) {
        FormTemplates.__super__.parse.call(this, response);

        return response.results;
    }
});

export const FormTemplateCategory = Backbone.Model.extend({
    legacy_classname: '$f.models.FormTemplateCategory',

    defaults: {
        id: null,
        name: null
    }
});

/**
 * Collection of form template categories. Can be limited to only the categories which have forms assigned to them.
 */
export const FormTemplateCategories = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.FormTemplateCategories',

    model: FormTemplateCategory,
    paginator: null,
    categories_with_forms: true,

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        this.paginator = new Paginator({ start: null, limit: null, order_by: 'name' });
    },

    url: function () {
        return '/api/v3/forms/categories/?limit=1000';
    },

    comparator: function (item) {
        return item.get(this.paginator.get('order_by'));
    }
});

export const SearchCriteria = BaseZenputModel.extend({
    legacy_classname: '$f.models.SearchCriteria',

    // Please do not set these to null, they are suppose to be empty strings
    defaults: {
        field_id: 'all',
        operator_id: 'contains',
        value: '',
        display: ''
    },

    save: function () {
        // Model does not save itself. Done in the collection
        return true;
    },

    reset: function () {
        this.set(this.defaults);
    },

    // explicit team criteria should be locked if it is the first criteria,
    // this is necessary because user criterias are also identified as user_group.
    is_team_filter: function () {
        return (
            ['smetadata.distribute_groups.id', 'user_group', 'distribute_group'].indexOf(this.get('field_id')) != -1 &&
            this.id === 1
        );
    },

    /**
     * Check if the field_id in the model is no longer part of the given form template
     *
     * @param {FormStructure} form_template The template for the search criteria
     * @return {boolean} True if field is no longer part of the given template or False if it still is
     */
    is_no_existing_field: function (form_template) {
        var field_id = this.get('field_id');
        if (!isNaN(field_id) && form_template.get(field_id) == undefined) {
            // Is form field, if it does not exist then is no existing field
            return true;
        }
        return false;
    },

    /**
     * Check if this search criteria is valid irrespective of field, usage, or type
     *
     * @return {boolean} True if value can be compared using operator_id
     */
    is_valid: function () {
        var value = this.get('value');
        var has_value = value !== null && value !== undefined;
        switch (this.get('operator_id')) {
            case '=':
            case '!=':
            case '>':
            case '>=':
            case '<':
            case '<=':
            case 'between':
            case 'not_between':
            case 'date_specific_after':
            case 'date_specific_before':
            case 'date_occur_between':
                return has_value;
            default:
                return true;
        }
    }
});

/**
 * The metadata object for a SearchCriterias.
 */
export const SearchCriteriaMetadata = Backbone.Model.extend({
    legacy_classname: '$f.models.SearchCriteriaMetadata',

    defaults: {
        name: '',
        next_criteria_id: 1,
        simple_logic: 'and',
        display_result_type: 'list', // This is the result type that should be displayed by default
        id: null,
        version: 2.0,
        created_by: { id: null, display_name: '' },
        type: null
    },

    validation: {
        name: {
            required: true,
            minLength: 1,
            msg: 'Please enter a valid title.'
        }
    },

    get: function (attr, options) {
        var results = SearchCriteriaMetadata.__super__.get.call(this, attr, options);
        if (attr == 'next_criteria_id') {
            // when getting next criteria id auto increment it
            // Bosco - I know backbone says do not manipulate the attributes themselves,
            // but doing set calls get, so you get infinite loop
            this.attributes.next_criteria_id += 1;
        }
        return results;
    }
});

/**
 * A special SearchCriteriaMetadata that is used for saveable variations of SearchCriteriaMetadata.
 * This expects a report_type ($f.reports.models.ReportType), which we will use to determine how the search's type will be displayed.
 */
export const SaveableSearchCriteriaMetadata = SearchCriteriaMetadata.extend({
    legacy_classname: '$f.models.SaveableSearchCriteriaMetadata',

    report_type: null, // $f.reports.models.ReportType

    defaults: {
        name: '',
        next_criteria_id: 1,
        simple_logic: 'and',
        display_result_type: 'xlsx', // This is the result type that should be displayed by default
        id: null,
        version: 2.0,
        created_by: { id: null, display_name: '' },
        company: { id: null },
        type: null
    },

    initialize: function (attributes, options) {
        SaveableSearchCriteriaMetadata.__super__.initialize.apply(this, arguments);

        options = options || {};
        if (options.report_type) {
            this.report_type = options.report_type;
        }

        if (!this.get('company')) {
            this.set('company', {}, { silent: true });
        }
        if (!this.get('company').id) {
            this.get('company').id = $f.user.company.id;
        }
    },

    /**
     * Get the human-readable name of this report type.
     */
    get_type_for_display: function () {
        if (!this.report_type) {
            // older search criterias (on the dashboard/list view) don't necessarily have a report_type
            console.error('SearchCriteriaList does not have a report_type object. Cannot determine type for display.');
            return '';
        }

        return this.report_type.get('name');
    }
});

export const FormTemplateSubmissionsSearchCriteriaMetadata = SaveableSearchCriteriaMetadata.extend({
    legacy_classname: '$f.models.FormTemplateSubmissionsSearchCriteriaMetadata',

    /*
        @todo: @author:bosco Figure out how to subclass and extend defaults properly.
        defaults: _.extend(
            {},
            SearchCriteriaMetadata.defaults,
            {
                display_result_type: 'list',
                has_rule: null,
                type: 'form_template_submissions'
            }
        )
    */
    defaults: {
        name: '',
        next_criteria_id: 1,
        simple_logic: 'and',
        display_result_type: 'list', // This is the result type that should be displayed by default
        id: null,
        version: 2.0,
        created_by: { id: null, display_name: '' },
        form_template: { id: null, title: null },
        type: 'form_template_submissions'
    },

    /**
     * Get the ID of the associated form template. On the dashboard, we use 'form_template_id', so
     * this serves as a wrapper until we consistantly use one or the other.
     * @return {int} The ID for the associated form template.
     */
    get_form_template_id: function () {
        return this.get('form_template').id;
    }
});

export const TaskProjectUtilsMixin = {
    legacy_classname: '$f.models.mixins.TaskProjectUtils',

    /**
     * Attempt to get a date attribute (date due, date start, etc.) zoned in whatever is specified
     * in the time_zone attribute. If no time_zone is set, the local time zone is used.
     *
     * @return {moment} The moment date, zoned.
     */

    _get_zoned_time: function (date_attr) {
        if (!this.get(date_attr) || !this.get(date_attr).$date) {
            return null;
        }
        var date = moment(this.get(date_attr).$date);

        if (this.get('time_zone')) {
            return date.tz(this.get('time_zone'));
        }

        // Unfortunately, .format('z') won't work unless we set a time zone, so we have to set moment.tz.guess()
        // if no other time_zone is specified.
        return date.tz(moment.tz.guess());
    },

    /**
     * Attempt to get a string representation of the due time range for recurring projects.
     * Only return time if this is a daily recurring task associated with a project
     *
     * @return {[string]}       String representation of the due time and time zone
     */

    get_time_due_str: function () {
        if (!this.get('date_due') || !this.get('date_start')) {
            return '';
        }

        var date_due = this._get_zoned_time('date_due');
        if (!date_due) {
            return '';
        }

        if (!this.get('project') || !this.get('project').id) {
            return '';
        }

        // Don't return any time if this is not a daily task (i.e., the length of it is longer than 23:45)
        if (moment(this.get('date_due').$date).diff(moment(this.get('date_start').$date)) > 85500000) {
            // 23 hours and 45 minutes
            return '';
        }

        return `${$f.utils.get_formatted_time(date_due)} ${date_due.format('z')}`;
    },

    /**
     * Attempt to get a string representation of the due time range for recurring projects.
     * Only return time if this is associated with a project
     *
     * @return {[string]}       String representation of due start time and due end time
     */

    get_time_start_and_due_str: function () {
        if (!this.get('project') || !this.get('project').id) {
            return '';
        }

        var due = this.get_time_due_str();
        if (!due) {
            return '';
        }

        var date_start = this._get_zoned_time('date_start');
        if (!date_start) {
            return due;
        } // this should never happen because get_time_due_str checks for date_start, but just in case...

        return `${$f.utils.get_formatted_time(date_start)} - ${due}`;
    }
};

export const BaseDashboardCardResultListItem = BaseZenputModel.extend(
    _.extend(
        {
            // callers can specify whatever they want, or subcalsses can specify their own defaults
            defaults: {}
        },
        TaskProjectUtilsMixin,
        {
            legacy_classname: '$f.models.BaseDashboardCardResultListItem'
        }
    )
);

export const BaseDashboardCardResultList = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.BaseDashboardCardResultList',

    model: BaseDashboardCardResultListItem,

    search_criterias: null, // SearchCriterias
    dashboard_filters: null, // $f.dashboards.collections.DashboardFilterList // DEM: update model here
    global_search_options: null, // DashboardSearchOptions - the global team filter // update model here
    paginator: null, // Paginator

    url: '/api/v1/dashboards/dashboard',
    zenput_code: null, // number to represent a code

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.search_criterias) {
            throw new Error('Programmer Error: BaseDashboardCardResultList requires search_criterias');
        }

        if (!this.dashboard_filters) {
            throw new Error('Programmer Error: BaseDashboardCardResultList requires dashboard_filters');
        }

        // dashboard cards are smaller than pages, so we will defaualt to 5 results at a time
        this.paginator = new Paginator({
            start: 0,
            limit: 5,
            order_by: null
        });
    },

    sync: function (method, model, options) {
        switch (method) {
            case 'read':
                options.url = `${model.url}/search/`;
                break;
            case 'create':
            case 'update':
            case 'delete':
                throw new Error(
                    'Programmer Error: method not supported for $f.dashboards.collections.BaseDashboardCardResultList'
                );
        }

        return Backbone.sync(method, model, options);
    },

    /**
     * Override our fetch to insert our search_criterias and dashboard_filters as a POST, not GET.
     */
    fetch: function (options) {
        options = options ? _.clone(options) : {};
        options.data = options.data || {};

        if (this.global_search_options) {
            this.global_search_options.update_paginator(this.paginator); // add group_id to our paginator
        }

        $.extend(options.data, this.paginator.toJSON(), {
            search_criteria: JSON.stringify(this.search_criterias),
            dashboard_filters: JSON.stringify(this.dashboard_filters)
        });

        options.type = 'POST';
        return BaseDashboardCardResultList.__super__.fetch.call(this, options);
    },

    parse: function (response) {
        if (!response.results) {
            return response;
        }

        this.zenput_code = response.zenput_code;

        if (this.paginator && response.count != null) {
            this.paginator.set('results_count', response.count);
        }

        return response.results;
    }
});

export const TopItemHeader = Backbone.Model.extend({
    legacy_classname: '$f.models.TopItemHeader',

    defaults: {
        info: {},
        label: '',
        sort_key: ''
    }
});

export const TopItemHeaders = Backbone.Collection.extend({
    legacy_classname: '$f.collections.TopItemsHeadersList',

    model: TopItemHeader
});

/**
 * Extra header functionality for BaseDashboardCardResultList.
 */
export const DashboardCardResultListWithHeadersMixin = {
    legacy_classname: '$f.collections.mixins.DashboardCardResultListWithHeaders',

    reset_headers: false, // should we reset/overwrite our headers on each fetch
    headers: null, // an array to hold the headers
    column_filter_index: null, // The index to filter the collection on

    set_headers: function (headers) {
        // Make sure we're add()ing an array, not a collection, which would add an element that is a collection
        headers = headers instanceof Backbone.Collection ? headers.toJSON() : headers;

        if (this.reset_headers) {
            this.headers = new TopItemHeaders();
        } else {
            this.headers = this.headers || new TopItemHeaders();
        }

        if (!headers) {
            return;
        }

        this.headers.add(headers);
    },

    parse: function (response) {
        if (response && response.headers) {
            this.set_headers(response.headers);
        }

        return this.mixin_super().parse.apply(this, arguments);
    },

    get_today_header_id: function () {
        var header_id = null;

        this.headers.each(function (header) {
            var is_today = moment(new Date(header.get('label'))).isSame(new Date(), 'day');
            if (!is_today) {
                return true;
            }

            header_id = header.id;
        });

        return header_id;
    }
};

export const SearchCriterias = Backbone.Collection.extend({
    legacy_classname: '$f.collections.SearchCriteriaList',

    model: SearchCriteria,

    /**
     * @param {SearchCriteriaMetadata} The SearchCriteriaMetadata model to use
     */
    metadata: SearchCriteriaMetadata,

    result_type: {}, // Either list or type of chart.  Set elsewhere

    /**
     * @param {list} the default sort for the search
     */
    default_sort: [],

    /**
     *  Extend constructor if models contains metadata and result_type pass them to initalize as options to be set
     */
    constructor: function (models, options) {
        options = options ? options : {};
        if ($.isArray(models)) {
            return SearchCriterias.__super__.constructor.call(this, models, options);
        }
        var search_criterias = models;
        if (models != undefined) {
            if (models.search != undefined) {
                search_criterias = models.search;
            }
            if (models.metadata != undefined) {
                options.metadata = models.metadata;
            }
            if (models.result_type != undefined) {
                options.result_type = models.result_type;
            }
            // note: this is only used by the FormTemplateSubmissions but it cannot be implemented further down in subclasses
            // as we have stripped out this data otherwise
            if (models.distribute_group_filter != undefined) {
                options.distribute_group_filter = models.distribute_group_filter;
            }
        }
        return SearchCriterias.__super__.constructor.call(this, search_criterias, options);
    },

    initialize: function (models, options) {
        options = options || {};

        if (options.metadata) {
            this.metadata = new this.metadata(options.metadata);
        } else {
            this.metadata = new this.metadata();
        }

        if (options.result_type) {
            this.result_type = $.extend(true, {}, options.result_type);
        } else {
            this.result_type = {};
        }
    },

    isNew: function () {
        return this.metadata.id === null;
    },

    get_next_criteria_id: function () {
        return this.metadata.get('next_criteria_id');
    },

    /**
     * Sets what should be displayed when this search criterias are loaded
     *
     *  @param {String} result_type The type of result, ie 'line', 'column', 'list', etc
     *  @param {Array} result_type_options A key value array with the information for the result type.
     */
    set_display_result_type: function (result_type, result_type_options) {
        this.metadata.set('display_result_type', result_type);
        this.result_type[result_type] = result_type_options;
        this.result_type[result_type].type = result_type;
    },

    fetch: function (options) {
        // Currently there is no fetch.  Add fetch saved search eventually
        console.error('Programmer Error: Fetch is not implemented in SearchCriterias');
    },

    save: function (attributes, options) {
        console.error('Programmer Error: Save is not implemented in SearchCriterias');
    },

    destroy: function (attributes, options) {
        console.error('Programmer Error: Destroy is not implemented in SearchCriterias');
    },

    save_local: function (key) {
        if (!this.isNew()) {
            return;
        } // we don't persist saved searches to local storage
        localStorage.setItem(key, JSON.stringify(this));
    },

    /**
     *  Extend _prepareModel so next criteria id is set
     */
    _prepareModel: function (attrs, options) {
        options = options || {};
        if (attrs instanceof Backbone.Model) {
            if (attrs.get('id') == null || attrs.get('id') == undefined) {
                attrs.set('id', this.get_next_criteria_id());
            }
        } else if (attrs != undefined) {
            if (attrs.id == undefined) {
                attrs.id = this.get_next_criteria_id();
            }
        }
        return SearchCriterias.__super__._prepareModel.call(this, attrs, options);
    },

    toJSON: function (options) {
        var results = {};
        results.search = SearchCriterias.__super__.toJSON.call(this, options);
        results.metadata = this.metadata.toJSON(options);
        results.result_type = $.extend(true, {}, this.result_type);
        return results;
    },

    /**
     *  Reset this search criteria list from another one
     *
     *  @param {SearchCriterias} search_criterias search_criterias to reset this list with
     *  @param {Array} options Options for the reset of this collection
     */
    reset_from_another: function (search_criterias, options) {
        var reset_search_criterias = null;

        if (search_criterias instanceof Backbone.Collection) {
            reset_search_criterias = search_criterias.toJSON();
        } else {
            reset_search_criterias = search_criterias;
        }

        this.reset(reset_search_criterias.search, options);
        // @note: @author:bosco Below will fire 2 changes events.  Fine for now, since we do not listen to them, but could be a problem if we do
        this.metadata.clear({ silent: true });
        this.metadata.set(reset_search_criterias.metadata);
        this.result_type = reset_search_criterias.result_type || {};
    }
});

/**
 * A version of SearchCriteria that we use for field event values, and weather or not
 * a dependent field should be shown.
 *
 * @see MobileBaseField.perform_event()
 */
export const FieldEventSearchCriteria = SearchCriteria.extend({
    legacy_classname: '$f.models.FieldEventSearchCriteria',

    /**
     * Test a given value against this search criteria
     */
    validate_value: function (value) {
        return $f.utils.evaluate_operator(this.get('operator_id'), value, this.get('value'));
    }
});

/**
 * A collection wrapper for the above FieldEventSearchCriteria
 *
 * @see MobileBaseField.perform_event()
 */
export const FieldEventSearchCriterias = SearchCriterias.extend({
    legacy_classname: '$f.collections.FieldEventSearchCriterias',

    model: FieldEventSearchCriteria,

    /**
     * We don't need all the metadata that the base version provides,
     * so let's clear out what we don't need.
     */
    metadata: SearchCriteriaMetadata.extend({
        toJSON: function () {
            var json = SearchCriteriaMetadata.prototype.toJSON.apply(this, arguments);

            delete json.display_result_type;
            delete json.created_by;
            delete json.name;

            return json;
        }
    }),

    /**
     * Test a given value against our search criterias.
     */
    validate_value: function (value) {
        var num_success = 0;

        this.each(function (criteria, i) {
            if (!criteria.validate_value(value)) {
                return true;
            } // continue
            num_success++;
        });

        if (this.metadata.get('simple_logic') == 'and' && num_success == this.length) {
            return true;
        }
        if (this.metadata.get('simple_logic') == 'or' && num_success > 0) {
            return true;
        }
        return false;
    }
});

/**
 * A single Submission for a form template
 */
export const Submission = Backbone.Model.extend({
    legacy_classname: '$f.models.Submission',

    defaults: {
        form_structure: null // FormStructure
    }
});

/**
 * Submissions for a form template
 */
export const Submissions = Backbone.Collection.extend({
    legacy_classname: '$f.collections.Submissions',

    search_criterias: null,
    rdbms_id: null,

    paginator: null, // Paginator
    paginator_options: { output: 'list', data_source: 'elastic_search', view_type: 'full' },

    model: Submission,

    url: function () {
        return this.rdbms_id ? '/api/v1/forms/list/' + this.rdbms_id : '/api/v1/forms/list/';
    },

    triggers: {
        sort_change_noop: 'reports:collections:submissions:change_sort',
        columns_change_noop: 'reports:collections:submissions:change_columns'
    },

    initialize: function (models, options) {
        options = options || {};

        if (options.paginator_options) {
            $.extend(this.paginator_options, options.paginator_options);
        }

        $.extend(this, options);
        var this_ = this;

        if (!this.search_criterias) {
            throw new Error('Programmer error: search_criterias not set');
        }
        if (!this.paginator) {
            // Set own defaults (not currently using ordery_by). Only set if one not passed in
            this.paginator = new SubmissionsPaginator(this.paginator_options);
        }

        this.on('add remove reset', function () {
            this_.paginator.set('results_count', this_.length);
        });
    },

    // @todo: Update fetch to automatically update the paginator order_by with the order set in the search criteria,
    // so the comparator will always sort offline consistently with server results. This works fine for now since
    // submissions list fetch gets the submissions in the correct order.
    comparator: function (item) {
        // order from oldest to newest to match default SubmissionsPaginator order_by for server fetch
        return -item.get('form_structure').smetadata.get('date_submitted').$date;
    },

    fetch: function (options) {
        options = options ? _.clone(options) : {};
        options.data = options.data || {};
        $.extend(options.data, this.paginator.toJSON(), {
            search_criteria: JSON.stringify(this.search_criterias)
        });

        $.extend(options, {
            dataType: 'json',
            type: 'POST',
            cache: false
        });
        return Submissions.__super__.fetch.call(this, options);
    },

    parse: function (response, options) {
        var form_submissions = response;
        var list_of_models = [];
        // This is to handle legacy where response was an array with keys 'form_submissions' and 'form_template'
        // When no longer passing array, this parse function can be removed.
        if (response.form_submissions) {
            form_submissions = response.form_submissions;
        }
        $.each(form_submissions, function (key, collection) {
            var form_structure = new FormStructure(collection);
            list_of_models.push({ form_structure: form_structure, id: form_structure._id.$oid });
        });
        return list_of_models;
    },

    /**
     *  Does a fetch for the next results using what is currently in the paginator
     */
    fetch_next: function (options) {
        this.paginator.set('start', this.paginator.get('start') + this.paginator.get('limit'));
        this.fetch(options);
    },

    /**
     *  Does a fetch for the previous results using what is currently in the paginator
     */
    fetch_previous: function (options) {
        if (this.paginator.get('start') < 0) {
            // If at begining there is no previous
            // @todo: - REVIEW - What is the proper way to handle this
            if (options.error) {
                options.error(this, null, options);
            }
            return false;
        }
        this.paginator.set('start', this.paginator.get('start') - this.paginator.get('limit'));
        return this.fetch(options);
    },

    /**
     *  Return submissions in the collection as a list
     *
     *  @requires {Array} List of submissions in this collection
     */
    get_list_of_submissions: function () {
        var list_of_models = [];
        $.each(this.models, function (index, submission_model) {
            list_of_models.push(submission_model.get('form_structure'));
        });
        return list_of_models;
    },

    /**
     *  Set the columns that should be displayed for the submissions
     *
     *  @param {Array} columns A list of columns that should be displayed with the submissions
     */
    set_columns: function (columns) {
        if (!this.search_criterias.result_type.list) {
            // This sets the default (@todo: tie in paginator) sort if there is not list information
            this.set_display_result_type([{ field_id: 'smetadata.date_submitted', order: -1 }], columns);
            return;
        }
        this.search_criterias.result_type.list.columns = columns;
        this.trigger('reports:collections:submissions:change_columns');
    },

    /**
     *  Get the columns set on the submissions
     *  @return {Array||Null} the columns set for the submission
     */
    get_columns: function () {
        return this.search_criterias.get_list_columns();
    },

    /**
     * Sets columns from a headers collection
     *
     * @params {$f.reports.collections.Headers} headers Contains what columns are sorted, how and the order
     */
    set_columns_from_headers: function (headers) {
        var sorted_fields = [];
        $.each(headers.get_sorted_fields(), function (index, field) {
            sorted_fields.push({ field_id: field.get('id'), order: field.get('order') });
        });
        this.set_sort(sorted_fields);
    },

    /**
     *  Set the sort for the submissions
     *
     *  @param {Array} sort A list in order to perform sort for submissions.  [{"field_id":<field to sort>,"order":<order to sort 1 Asc and -1 Desc>}]
     */
    set_sort: function (sort) {
        // @todo:  Integrate sort with paginator
        if (!this.search_criterias.result_type.list) {
            // This sets the default columns (empty list which means show all)
            this.set_display_result_type(sort, []);
            return;
        }
        this.search_criterias.result_type.list.sort = sort;
        this.trigger('reports:collections:submissions:change_sort');
    },

    /**
     *  Set the search criteria results type to be list of submissions, and set the sort and column in the search criteria
     *
     *  @param {Array} sort A list in order to perform sort for submissions.  [{"field_id":<field to sort>,"order":<order to sort 1 Asc and -1 Desc>}]
     *  @param {Array} columns A list of columns that should be displayed with the submissions
     */
    set_display_result_type: function (sort, columns) {
        this.search_criterias.set_display_result_type('list', { sort: sort, columns: columns });
        this.trigger('reports:collections:submissions:change_sort');
        this.trigger('reports:collections:submissions:change_columns');
    }
});

export const SensorSubmissions = Submissions.extend({
    legacy_classname: '$f.collections.SensorSubmissions',

    // TODO: do we need to subclass model: Submission (actually: FormStructure)

    url: function () {
        return '/api/v1/sensors/list/' + this.rdbms_id;
    },

    parse: function (response, options) {
        var list_of_models = [];
        $.each(response.form_submissions, function (key, collection) {
            var form_structure = new FormStructure(collection);
            list_of_models.push({ form_structure: form_structure, id: form_structure._id });
        });
        return list_of_models;
    },

    fetch: function (options) {
        this.search_criterias.metadata.set('type', 'sensor_form_submissions');
        return SensorSubmissions.__super__.fetch.call(this, options);
    }
});

export const DistributeGroups = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.DistributeGroups',

    paginator: null,
    user_id: null, // used if you'd like to grab groups for a given user
    hierarchy_connect_true_root: false, // if true, we will connect the true root ('All' team)

    _hierarchy: null, // DistributeGroups Cached sorted group hierarchy

    /**
     * We are defining this as a function due to load-time dependency issues with DistributeGroup.submodels
     */
    model: function (attrs, options) {
        return new DistributeGroup(attrs, options);
    },

    initialize: function (options) {
        options = options || {};

        this.paginator = new Paginator({ order_by: 'name' });
        this.paginator.set('limit', 9999);
        this.user_id = options.user_id ? options.user_id : null;
    },

    url: function () {
        if (this.user_id) {
            return '/api/v1/users/groups/user/' + this.user_id;
        }

        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'group_id')) {
            attrs.push('group_id');
        }
        return '/api/v1/users/groups/?' + this.paginator.get_query_string({ get_count: true });
    },

    parse: function (response) {
        this.paginator.set('results_count', response.count);
        return response.results;
    },

    comparator: function (item) {
        // All/My teams should always be on top
        return [item.get('is_editable'), item.get(this.paginator.get('order_by'))];
    },

    _set_hierarchy: function (get_full_hierarchy, force) {
        var this_ = this;

        if (this._hierarchy != null && !force) {
            return;
        }

        this._hierarchy = new DistributeGroups();

        if (!this.length) {
            return;
        }

        var true_root_group = this.findWhere({ is_editable: false, is_root: true });

        if (!true_root_group) {
            // if no true_root_group, we must be restricted to only a single team. Thus, we
            // can safely assume we only have one distribute group:
            true_root_group = this.findWhere({ is_root: true });
            this.hierarchy_connect_true_root = false;
        }

        var _attach_children = function (model, depth) {
            var distribute_group_id = model.get('id');
            if (distribute_group_id == null) {
                return;
            }
            var children_models = this_.where({ parent_distribute_group_id: distribute_group_id });

            if (children_models.length == 0) {
                return;
            }

            if (!model.get('child_distribute_groups')) {
                model.set(
                    {
                        child_distribute_groups: new DistributeGroups(),
                        depth: depth
                    },
                    { silent: true }
                );
            }

            $.each(children_models, function (i, child) {
                child.set('depth', depth + 1, { silent: true });
                _attach_children(child, depth + 1);
                var child_distribute_groups = model.get('child_distribute_groups');
                child_distribute_groups.add(child, { silent: true });
                // attach the data to the top level collection objects as well so that we can use collection.get(id)
                // to change the current position of the tree

                // this is sometimes running before it is fully populated
                // placeholder if until cause of that issue can be found
                if (this.collection.get(model.id)) {
                    this.collection
                        .get(model.id)
                        .set('child_distribute_groups', child_distribute_groups, { silent: true });
                    model.set('child_distribute_groups', child_distribute_groups, { silent: true });
                }
            });
        };

        if (this.hierarchy_connect_true_root) {
            // Correct any missing connections to the "All Teams" group.
            // Loop through all root teams that is not the true root (i.e., the "all" group).
            $.each(this.where({ is_root: true, is_editable: true }), function (i, model) {
                model.set(
                    {
                        parent_distribute_group: true_root_group,
                        parent_distribute_group_id: true_root_group.id
                    },
                    { silent: true }
                );
            });
        }

        // Add top-level groups to hierarchy
        var roots_criteria = { is_root: true };
        if (this.hierarchy_connect_true_root) {
            // if we have connected our true root to our other roots, we only want to start at the single true root
            roots_criteria.is_editable = false;
        }

        $.each(this.where(roots_criteria), function (i, model) {
            _attach_children(model, 0);
            this_._hierarchy.add(model);
        });

        // If we want the full hierarchy including unrelated teams, we're good to go
        if (get_full_hierarchy) {
            return;
        }

        var num_removed = -1;
        /**
         * Checks the leaves of our hierarchy tree for irrelevant groups (i.e., !is_associated groups with no children)
         * and removes them. Call recursively to traverse the entire tree.
         */
        var _remove_irrelevant_hierarchy_leaves = function (model) {
            // Recursively check each child
            if (model && model.get('child_distribute_groups') && model.get('child_distribute_groups').length > 0) {
                model.get('child_distribute_groups').each(function (child, i) {
                    _remove_irrelevant_hierarchy_leaves(child);
                });
            }

            // Remove model if appropriate
            if (
                model &&
                (!model.get('child_distribute_groups') || model.get('child_distribute_groups').length == 0) &&
                !model.get('is_associated')
            ) {
                model.trigger('destroy', model, model.collection); // Do not use model.destroy(), as that tries to make a server call
                num_removed++;
            }
        };

        while (num_removed != 0) {
            // continue removing until there are no more to remove
            num_removed = 0;
            this.each(function (_model, i) {
                _remove_irrelevant_hierarchy_leaves(_model);
            });
        }
    },

    /**
     * Gets a hierarchical ordering of groups.
     *
     * @author:bosco Note: if you already got hierarchy with get_full_hierarchy = false, and then call again with
     * get_full_hierarchy = true, a new build of the hierarchy will not run, and the last build will be sent back.
     * Need to set force = true.
     *
     * @param  {boolean} get_full_hierarchy (Default false) If true, will return a hierarchy of all teams, regardless of whether
     *                                                      or not the user in question is related to them.
     * @param  {boolean} force (Default false)              If true will set team hierarchy even if already set
     * @return {DistributeGroups}            A collection of teams. If a team has children teams, they will be
     *                                                      stored in the model's attribute child_distribute_groups.
     */
    get_hierarchy: function (get_full_hierarchy, force) {
        this._set_hierarchy(get_full_hierarchy, force);
        return this._hierarchy;
    }
});

export const DistributeGroup = BaseZenputModel.extend({
    legacy_classname: '$f.models.DistributeGroup',

    defaults: {
        id: null,
        name: null,
        is_associated: false, // how this DistributeGroup is assoicated to whatever was creating it
        // and how a user is associated to the given distribute group
        is_editable: true,
        parent_distribute_group: null,
        parent_distribute_group_id: null,
        child_distribute_groups: null
    },

    submodels: {
        child_distribute_groups: DistributeGroups
    },

    url: function () {
        return '/api/v1/users/group/get/' + this.id;
    },

    validation: {
        name: { required: true, minLength: 2 }
    }
});

export const UserDistributeGroupAndRole = Backbone.Model.extend({
    legacy_classname: '$f.models.UserDistributeGroupAndRole',

    defaults: {
        id: null, // 'user-<user_id>' or 'group-<group_id>' Done like this to be unique
        raw_id: null, // The raw id of either user_id or group_id
        name: null, // The label of the item
        type: null, // The type of object either 'user' or 'group'
        is_registered: null, // For user, if the user has registered their account
        user_role: null // A default value of null for users without a defined user_role_id
    }
});

export const UsersDistributeGroupsAndRoles = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.UsersDistributeGroupsAndRoles',

    model: UserDistributeGroupAndRole,
    paginator: null, // Only using search_query to get results, controller handling rest
    typeahead_search_attrs: ['name', 'address'], // typeahead collection attributes to search

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        this.paginator = new Paginator();
        if (options.exclude_groups_with_parents) {
            this.paginator.set('exclude_groups_with_parents', true);
        }
        if (options.allow_all_company_group) {
            this.paginator.set('allow_all_company_group', true);
        }
        if (options.include_roles) {
            this.paginator.set('include_roles', true);
        }
        if (options.show_null_user) {
            this.paginator.set('show_null_user', true);
            this.paginator.set('null_user_name', options.null_user_name);
        }
    },

    url: function () {
        // This is the API we use for autocomplete. Override the collection url to use a different one
        var url = '/api/v1/users/list_users_groups/?' + this.paginator.get_query_string();
        if (this.paginator.get('exclude_users_roles')) {
            // exclude_user_roles is a collection of AccountOwnerUser
            var chosen_user_role_ids = _.map(this.paginator.get('exclude_users_roles').models, function (model, idx) {
                return model.get('user_role_id');
            });
            chosen_user_role_ids = _.filter(chosen_user_role_ids, function (val, idx) {
                return val;
            }); // strip out undefined and nulls
            url += '&exclude_user_role_ids=' + chosen_user_role_ids.join($f.utils.STRING_DELIMITER);
        }
        if (this.paginator.get('exclude_groups_with_parents')) {
            url += '&exclude_groups_with_parents=True';
        }
        if (this.paginator.get('allow_all_company_group')) {
            url += '&allow_all_company_group=True';
        }
        if (this.paginator.get('include_roles')) {
            url += '&include_roles=True';
        }
        if (this.paginator.get('show_null_user')) {
            url += '&show_null_user=True&null_user_name=' + this.paginator.get('null_user_name');
        }

        return url;
    }
});

/**
 * This collection handles special parsing required for collections representing form distribution
 * among users, groups and roles.
 **/
export const UsersDistributeGroupsAndRolesForFormDistribution = UsersDistributeGroupsAndRoles.extend({
    legacy_classname: '$f.collections.UsersDistributeGroupsAndRolesForFormDistribution',

    parse: function (response) {
        $.each(response.results.users.results, function (i, user) {
            user.type = 'user';
            if (typeof user.id == 'string' && _.contains(user.id, '~')) {
                return true;
            }
            user.raw_id = user.id;
            user.id = 'user~' + user.id;
        });
        $.each(response.results.groups.results, function (i, group) {
            group.type = 'group';
            if (typeof group.id == 'string' && _.contains(group.id, '~')) {
                return true;
            }
            group.raw_id = group.id;
            group.id = 'group~' + group.id;
        });

        var all_results = response.results.users.results.concat(response.results.groups.results);

        if (this.include_roles) {
            $.each(response.results.roles.results, function (i, role) {
                role.type = 'role';
                if (typeof role.id == 'string' && _.contains(role.id, '~')) {
                    return true;
                }
                role.raw_id = role.id;
                role.id = 'role~' + role.id;
            });
            all_results = all_results.concat(response.results.roles.results);
        }

        return all_results;
    }
});

/**
 * Our base Zenput form that holds all of our form fields
 *
 * @todo: author: kgstew update this model *ahem* collection to have its own fetch method so we do not have to
 * make a custom ajax request every time we want this collection outside of the builder.
 */
export const FormStructure = Backbone.Collection.extend(
    {
        legacy_classname: '$f.models.FormStructure',

        model: BaseField,
        metadata: FormMetadata,
        smetadata: FormSmetadata, // for submission metadata, like date submitted, etc
        subscribers: null, // Users
        can_edit: false,
        is_active: null, // Submission is_active state

        comparator: function (item) {
            return item.get('id');
        },

        constructor: function (collection, options) {
            if (!collection || collection == undefined) {
                FormStructure.__super__.constructor.call(this);
                this.metadata = new this.metadata();
                this.smetadata = new FormSmetadata();
                return;
            }

            // we know this is coming from elastic search
            // @jira:MOBILE-360 this will be unnecessary once we change this to fetch from Mongo
            if (collection.zp_fields) {
                this._id = collection.id;
                FormStructure.__super__.constructor.call(this, collection.zp_fields);
                this.metadata = new this.metadata(collection.zp_metadata);
                this.smetadata = new this.smetadata(collection.zp_smetadata);
            } else {
                this._id = collection._id;
                FormStructure.__super__.constructor.call(this, collection.fields);
                this.metadata = new this.metadata(collection.metadata);
                this.smetadata = new this.smetadata(collection.smetadata);
            }

            if (collection.subscribers) {
                this.subscribers = new Users(collection.subscribers);
            }

            this.can_edit = !!collection.can_edit;
            this.is_active = !collection.deleted;
        },

        toJSON: function () {
            var model = {
                fields: this.map(function (_model) {
                    return _model.toJSON();
                })
            };
            _.extend(model, { _id: this._id });
            _.extend(model, { metadata: this.metadata.toJSON() });
            _.extend(model, { smetadata: this.smetadata.toJSON() });
            return model;
        },

        remove: function (model) {
            var order = this.get_order();
            var index = order.indexOf(model.id.toString());

            if (index > -1) {
                // @bug:FORMS-137 to be safe, make sure model is in the array. If not, we'd remove the wrong one
                order.splice(index, 1);
                this.metadata.set('order', order);
            }

            // now its safe to remove the object
            FormStructure.__super__.remove.call(this, model);
            model.remove();
        },

        /**
         * iterate over the fields in the proper order
         *
         * @param {function} callback A function to call for each field found sends the field model and index back
         * @param {view} view The view that called this
         * @param {array} options A array of options for the each.
         */
        each: function (callback, view, options) {
            var parent = this;
            var order = this.get_order();
            var exclude = options != undefined && options.exclude != undefined ? options.exclude : [];

            _.each(
                order,
                function (key) {
                    var model = parent.get(key);
                    if ($.inArray(model.get('type'), exclude) == -1) {
                        return callback.call(this, model, key);
                    }
                },
                view
            );
        },

        /**
         *  Reset this formstructure from another one
         *
         *  @param {FormStructure} the form_structure to reset this form structure with
         *  @param {Array} options Options for the reset of this formstructure
         */
        reset_from_another: function (form_structure, options) {
            var this_ = this;
            var reset_fs = form_structure instanceof Backbone.Collection ? form_structure.toJSON() : form_structure;
            this.metadata.clear({ silent: true });
            this.smetadata.clear({ silent: true });
            this.models = [];
            this.metadata.set(reset_fs.metadata);
            this.smetadata.set(reset_fs.smetadata);
            _.each(reset_fs.fields, function (field) {
                this_.models.push(new BaseField(field));
            });

            this.length = this.models.length;
        },

        get_order: function () {
            return this.metadata.get('order');
        },

        get_next_field_id_by_id: function (id) {
            var order = this.get_order();

            return order[order.indexOf(id.toString()) + 1];
        },

        get_previous_field_id_by_id: function (id) {
            var order = this.get_order();

            return order[order.indexOf(id.toString()) - 1];
        },

        get_last_field_id: function () {
            return _.last(this.get_order());
        },

        is_submitted: function () {
            return !!this.smetadata.get('date_submitted');
        },

        /**
         *
         * Given an array of field ids sort them by the collection order and return them in order;
         *
         * @param {*} field_ids
         */
        get_fields_sorted_by_order: function (field_ids) {
            var this_ = this;
            return _.sortBy(field_ids, function (id) {
                return this_.get_order().indexOf(id.toString());
            });
        },

        /**
         * Return an array of all dependent parents in the collection
         */
        get_dependent_parents: function () {
            return this.filter(function (model) {
                return model.is_dependent_parent();
            });
        },

        /**
         * Returns an array of field pair tuples (the idea of tuples anyway)
         * The first tuple element is the dependent parent
         * The second tuple element is the last descendent field
         *
         */
        get_dependent_field_groups: function () {
            var parents = this.get_dependent_parents();

            return _.reduce(
                parents,
                function (groups, parent) {
                    _.each(parent.get_dependent_parent_groups(), function (group) {
                        groups.push(group);
                    });
                    return groups;
                },
                []
            );
        },

        /**
         *
         * Given a list of section parents and a field model this will remove any parents from the
         * parent_section_stack which are no longer valid parents
         *
         * @param {*} parent_section_stack
         * @param {*} model
         */
        _remove_nested_parents: function (parent_section_stack, model) {
            return _.filter(parent_section_stack, function (parent_section_field) {
                if (model.get('type') === 'section') {
                    return parent_section_field.get_dependent_field_level() < model.get_dependent_field_level();
                }

                return parent_section_field.get_dependent_field_level() <= model.get_dependent_field_level();
            });
        },

        /**
         * Get all the sections in this template
         * A section ends when we reach another section field at the same level of nesting or
         * the next field is in the ancestor level of nesting
         *
         * @returns {<id>: { section_field_ids: [<field_id>,...] section_parent_id: <parent_id>} , ...}
         */
        get_section_references: function () {
            var this_ = this;
            var parent_section_stack = [];

            if (this.models.length == 0) {
                return {};
            }

            var order = this.get_order();

            return _.reduce(
                order,
                function (section_data, order_value) {
                    var model = this_.get(order_value);
                    var last_parent = _.last(parent_section_stack);

                    if (parent_section_stack.length) {
                        if (model.get('type') === 'section') {
                            if (model.get_dependent_field_level() <= last_parent.get_dependent_field_level()) {
                                parent_section_stack = this_._remove_nested_parents(parent_section_stack, model);
                            }
                        }

                        if (model.get_dependent_field_level() < last_parent.get_dependent_field_level()) {
                            parent_section_stack = this_._remove_nested_parents(parent_section_stack, model);
                        }

                        // Add this field to all sections currently in the parent_section_stack.
                        _.each(parent_section_stack, function (item) {
                            section_data[item.id].section_field_ids.push(model.id);
                        });
                    }

                    // Push a new section to the parent_section_stack
                    if (model.get('type') === 'section') {
                        section_data[model.id] = {
                            section_field_ids: [],
                            section_parent_id: parent_section_stack.length ? last_parent.id : null
                        };
                        parent_section_stack.push(model);
                    }

                    return section_data;
                },
                {}
            );
        },

        /**
         * Fetch the submission's pdf location on s3
         * Must be submitted to return a value
         *
         * @param success_cb - passed either a fully formed s3 location or null
         */
        get_pdf_url: function (success_cb) {
            if (!this.is_submitted()) {
                return success_cb(null);
            }
            var ajax = $f.utils.select_ajax();
            ajax({
                url: '/api/v1/forms/get/' + this._id.$oid + '?output=pdf_url',
                dataType: 'json',
                type: 'GET',
                success: function (response) {
                    success_cb(response.pdf_url);
                }
            });
        }
    },
    {
        get_share_link: function (submission_id, success_cb, error_cb) {
            var ajax = $f.utils.select_ajax();
            ajax({
                url: `/api/v1/forms/share/${submission_id}/`,
                dataType: 'json',
                type: 'GET',
                success: function (response) {
                    success_cb(response.results.url);
                },
                error: function (response) {
                    error_cb(response);
                }
            });
        }
    }
);

/**
 * Our pagination object that sits inside of a Collection
 */
export const Paginator = Backbone.Model.extend({
    legacy_classname: '$f.models.Paginator',

    defaults: {
        results_count: 0,
        start: 0,
        limit: 20,
        order_by: 'name',
        search_by: 'accounts', // our default to search by when the page loads
        search_query: '',
        num_selected: 0,
        select_all: false // If all items are selected = true, else false
    },

    // @todo:john - this needs to be changed.  there shouldnt be a allowlist needed here.  i'm unsure why this
    // was ever even here
    default_query_string_attrs: null, // []

    initialize: function () {
        this.default_query_string_attrs = [
            'start',
            'limit',
            'order_by',
            'search_by',
            'search_query',
            'form_template_id'
        ];
        this.listenTo(this, 'change:select_all', this.change_select_all);
        this.set('tz', jstz.determine().name());
    },

    change_select_all: function (model) {
        // The model is this
        var select_all = model.get('select_all');
        var num_selected = select_all ? model.get('results_count') : 0;
        model.set('num_selected', num_selected);
    },

    reset_pagination: function () {
        this.set('start', 0);
        this.set('num_selected', 0);
    },

    /**
     * Get's a URL query string of all appropriate paginator attributes in a way that our server will understand.
     * Null values will not be included.
     *
     * @param {array} attrs A list of the model attributes to include in the querystring, if not null. (Optional)
     * @param {boolean} get_count If true, will return "count=true" as well
     * @return {string} The query string (e.g., "start=0&limit=20&order_by=name").
     */
    get_query_string: function (options) {
        options = options || {};
        if (!options.attrs) {
            options.attrs = this.default_query_string_attrs;
        }

        var querystring = '';

        var this_ = this;
        $.each(options.attrs, function (i, attr_name) {
            var attr = this_.get(attr_name);

            if (attr === null || attr === '' || typeof attr === 'undefined') {
                return true;
            }

            var value = attr instanceof Object ? JSON.stringify(attr) : attr;
            querystring += `${attr_name}=${encodeURIComponent(value)}&`;
        });

        querystring = querystring.substring(0, querystring.length - 1); // chop off that last "&"

        if (options.get_count) {
            querystring += '&count=true';
        }

        return querystring;
    },

    increase_results_count: function (n) {
        n = $f.utils.is_nil(n) ? 1 : n;
        this.set('results_count', this.get('results_count') + n);
    },

    decrease_results_count: function (n) {
        n = $f.utils.is_nil(n) ? 1 : n;
        this.set('results_count', this.get('results_count') - n);
    },

    next_page: function () {
        this.set('start', this.get('start') + this.get('limit'));
    },

    previous_page: function () {
        this.set('start', this.get('start') - this.get('limit'));
    }
});

/**
 * A paginator for our reports submissions list with a default limit of 50 instead of the
 * standard 20.
 *
 * @see Submissions
 */
export const SubmissionsPaginator = Paginator.extend({
    legacy_classname: '$f.models.SubmissionsPaginator',

    defaults: $.extend({}, Paginator.prototype.defaults, {
        limit: 50,
        order_by: 'smetadata.date_submitted'
    }),

    initialize: function () {
        SubmissionsPaginator.__super__.initialize.apply(this, arguments);

        // @todo These are unused by this controller. When we refactor our paginator, we should have a
        // slimmer set of defaults that don't include these
        delete this.defaults.search_by;
        delete this.defaults.search_query;
        delete this.defaults.select_all;
        delete this.defaults.num_selected;

        this.unset('search_by').unset('search_query').unset('select_all').unset('num_selected');
    },

    reset_pagination: function () {
        this.set('start', 0);
        // @todo Also unused by this controller, like above. Shouldn't be necessary to do this after refactor.
        this.unset('num_selected');
    }
});

/**
 * Company
 */

export const Company = Backbone.Model.extend({
    legacy_classname: '$f.models.Company',

    defaults: {
        id: null,
        name: null,
        features: [],
        sensor_integration_types: []
    },

    url: function () {
        return '/api/v1/companies/get/' + (this.id ? this.id : '');
    },

    parse: function (response, options) {
        if (options.collection) {
            return response;
        } // this is so collection.fetch wont call us
        return response.results;
    },

    has_feature: function (feature) {
        return _.contains(this.get('features') || [], feature);
    },

    has_sensor_integration_type: function (integration_type) {
        return _.contains(this.get('sensor_integration_types') || [], integration_type);
    }
});

/**
 * Users
 */
export const User = BaseZenputModel.extend({
    legacy_classname: '$f.models.User',

    defaults: {
        id: null,
        first_name: null,
        last_name: null,
        display_name: '',
        email: null,
        sms_number: null,
        api_token: null,
        is_admin: null,
        is_manager: null,
        is_owner: null,
        userprofile__sms_number: null,
        userprofile__locale: null,
        temperature_unit: null
    },

    get_locale: function () {
        return this.get('userprofile__locale');
    },

    get_display_name: function () {
        if (this.get('display_name')) {
            return this.get('display_name');
        }

        return $f.utils.generate_display_name(
            this.get('first_name'),
            this.get('last_name'),
            this.get('email'),
            this.get('userprofile__sms_number')
        );
    },

    validation: {
        first_name: { required: true, minLength: 2, maxLength: 30 },
        last_name: { required: true, minLength: 2, maxLength: 30 },
        userprofile__sms_number: { required: false, msg: 'Please enter a cell phone number' }
    },

    url: '/api/v1/users/',

    sync: function (method, model, options) {
        if (method == 'create' || method == 'update') {
            options.type = 'POST';
            options.url = model.url + 'save/' + (model.id ? model.id : '');
        } else {
            options.url = model.url + 'get/' + (model.id ? model.id : '');
        }
        return Backbone.sync(method, model, options);
    },

    parse: function (response, options) {
        if (options.collection) {
            return response;
        } // this is so collection.fetch wont call us
        return response.results;
    },

    is_owner: function () {
        if (this.get('is_owner')) {
            return true;
        }
        return false;
    },

    /**
     * Returns whether the user is a manager. Currently will return true is an owner as well
     */
    is_admin: function () {
        if (this.get('is_admin') || this.is_owner()) {
            return true;
        }
        return false;
    },

    /**
     * Returns whether the user is a manager. Currently will return True is an owner as well
     */
    is_manager: function () {
        if (this.get('is_manager') || this.is_owner()) {
            return true;
        }
        return false;
    },

    is_submitter: function () {
        if (!this.is_manager() && !this.is_admin() && !this.is_owner()) {
            return true;
        }
        return false;
    }
});

export const Users = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.Users',

    model: User,
    tag_id: null,
    rdbms_id: null,
    paginator: null,
    typeahead_search_attrs: ['first_name', 'last_name'],

    initialize: function (options) {
        options = options || {};
        this.paginator = new Paginator({ order_by: '-date_joined' });
        this.rdbms_id = options.rdbms_id ? options.rdbms_id : null;
    },

    url: function () {
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'group_id')) {
            attrs.push('group_id');
        }
        if (!_.contains(attrs, 'override_hierarchy_restriction')) {
            attrs.push('override_hierarchy_restriction');
        }

        return '/api/v1/users/list_users/?' + this.paginator.get_query_string({ get_count: true });
    },

    /**
     * Override so we can keep track of our paginator.  For some reason I didn't override the 'add' method
     */
    add_new: function (model, success_callback, error_callback) {
        if (model.isNew()) {
            this.paginator.set('results_count', this.paginator.get('results_count') + 1);
        }
        model.save().success(success_callback).error(error_callback);
        Users.__super__.add.call(this, model);
    },

    /**
     * Get all users in the format that Tribute wants for autocomplete.
     *
     * @see https://github.com/zurb/tribute#loading-remote-data
     */
    to_tribute_autocomplete: function () {
        return this.map(function (user) {
            var display = user.get_display_name();

            return {
                id: user.id,
                user_role: user.get('userprofile__user_role__name'),
                key: display,
                value: display
            };
        });
    }
});

export const UsersV3 = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.UsersV3',
    search_param: 'search',

    model: User,
    tag_id: null,
    rdbms_id: null,
    paginator: null,
    typeahead_search_attrs: ['first_name', 'last_name'],

    default_paginator_options: {
        limit: 30,
        order_by: 'name',
        search: '',
        start: null
    },

    initialize: function (models, options) {
        const this_ = this;
        options = options || {};
        $.extend(this, _.omit(options, 'search_options'));

        if (!this.paginator) {
            if (options && options.search_options) {
                _.extend(this_.default_paginator_options, options.search_options);
            }
            this.paginator = new Paginator(this.default_paginator_options);
            if (options.search_options && options.search_options.update_paginator) {
                options.search_options.update_paginator(this.paginator);
            }
        }

        UsersV3.__super__.initialize.apply(this, arguments);
    },

    url: function () {
        const attrs = ['limit', 'order_by', 'search', 'start'];
        const query_string = this.paginator.get_query_string({
            attrs: attrs,
            get_count: true
        });

        if (this.allow_cross_company_access) {
            // Include reporting company users
            return '/api/v3/users/managed/?' + query_string + '&include_self=true';
        }

        return '/api/v3/users/?' + query_string;
    }
});

/**
 * Users who can access a given location. Backs the location-scoped assignee
 * typeahead — same paging/search behaviour as UsersV3, but hits the
 * per-location endpoint so results are limited to that location's roster.
 */
export const LocationUsersV3 = UsersV3.extend({
    legacy_classname: '$f.collections.LocationUsersV3',
    location_id: null,

    url: function () {
        const attrs = ['limit', 'order_by', 'search', 'start'];
        const query_string = this.paginator.get_query_string({
            attrs: attrs,
            get_count: true
        });

        return '/api/v3/locations/' + this.location_id + '/users/?' + query_string;
    }
});

/**
 * User Device
 */
export const UserDevice = Backbone.Model.extend({
    legacy_classname: '$f.models.UserDevice',

    defaults: {
        id: null,
        name: null
    },

    url: '/api/v1/users/devices/',

    sync: function (method, model, options) {
        options.type = 'POST';
        if (method == 'create' || method == 'update') {
            options.url = model.url + 'save/';
        } else if (method == 'delete') {
            options.url = model.url + 'delete/' + model.id;
        }
        return Backbone.sync(method, model, options);
    }
});

export const UserNotificationSetting = Backbone.Model.extend({
    legacy_classname: '$f.models.UserNotificationSetting ',

    defaults: {
        name: null,
        order: null,
        triggers: null,
        delivery_type: null,
        /* options is used only for displaying which options are available
         * for which setting.  they are not stored */
        options: [],
        category: null,
        translation_id: null
    },
    idAttribute: 'order'
});

export const UserNotificationSettings = Backbone.Collection.extend({
    legacy_classname: '$f.collections.UserNotificationSettings',

    push_notifications_enabled: false, // this gets set during this ajax call
    sms_notifications_enabled: false, // this gets set during this ajax call

    model: UserNotificationSetting,
    comparator: 'order',
    url: '/api/v2/users/settings/notifications/',

    sync: function (method, model, options) {
        options = options || {};
        switch (method) {
            case 'create':
            case 'update':
                options.type = 'PATCH';
                break;
            default:
                options.type = 'GET';
        }
        return Backbone.sync(method, model, options);
    },

    parse: function (response) {
        this.push_notifications_enabled = response.data.push_notifications_enabled;
        this.sms_notifications_enabled = response.data.sms_notifications_enabled;
        return response.data.results;
    },

    save: function (options) {
        this.sync('create', this, options);
    },

    filter_by_category: function (category) {
        return new UserNotificationSettings(this.where({ category: category }));
    }
});

export const UserDevices = Backbone.Collection.extend({
    legacy_classname: '$f.collections.UserDevices',

    model: UserDevice,
    paginator: null,

    initialize: function (options) {
        options = options || {};
        this.paginator = new Paginator({ order_by: 'name' });
    },

    url: function () {
        return '/api/v1/users/devices/list/?' + this.paginator.get_query_string({ get_count: true });
    },

    parse: function (response) {
        this.paginator.set('results_count', response.count);
        return response.results;
    },

    comparator: function (item) {
        return item.get(this.paginator.get('order_by'));
    }
});

/**
 * User Roles
 */
export const UserRole = Backbone.Model.extend({
    legacy_classname: '$f.models.UserRole',

    defaults: {
        id: null,
        name: null
    },

    validation: {
        name: { required: true, minLength: 2 }
    },

    url: '/api/v1/users/roles/',

    sync: function (method, model, options) {
        if (method == 'update' || method == 'create') {
            options.url = model.url + 'save/';
        } else if (method == 'delete') {
            options.url = model.url + 'delete/' + model.id;
        }

        return Backbone.sync(method, model, options);
    }
});

export const UserRolesPaginator = Paginator.extend({
    defaults: $.extend({}, Paginator.prototype.defaults, {
        order_by: 'name',
        limit: 500
    }),

    override_default_query_string_attrs: [
        'start',
        'limit',
        'order_by',
        'search_by',
        'search_query',
        'form_template_id',
        'group_id'
    ],

    initialize: function () {
        UserRolesPaginator.__super__.initialize.apply(this, arguments);
        this.default_query_string_attrs = this.override_default_query_string_attrs;
    }
});

export const UserRoles = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.UserRoles',
    model: UserRole,
    paginator: null, // UserRolesPaginator

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new UserRolesPaginator();
        }
    },

    url: function () {
        return '/api/v1/users/roles/?' + this.paginator.get_query_string({ get_count: true });
    },

    parse: function (response) {
        this.paginator.set('results_count', response.count);
        return response.results;
    },

    comparator: function (item) {
        return item.get(this.paginator.get('order_by'));
    }
});

/**
 * Accounts
 */
export const Account = Backbone.Model.extend({
    legacy_classname: '$f.models.Account',

    defaults: {
        id: null, // Do not remove id attribute.
        is_selected: false,
        name: null,
        country: null,
        address: null,
        city: null,
        state: null,
        region: null,
        zipcode: null,
        email: null,
        phone: null,
        external_key: null,
        owners: null,
        tags: null, // AccountTags when tags are associated
        time_zone: null,
        has_sensors: false,
        has_gateways: false
    },

    has_gathered_tags: false,

    has_sensor_integration_type: function (integration_type) {
        return _.contains(this.get('sensor_integration_types') || [], integration_type);
    },

    initialize: function (options) {
        var this_ = this;
        Account.__super__.initialize.apply(this, arguments);

        this._convert_json_to_models();
        this.on('change', function () {
            this_._convert_json_to_models();
        });
    },

    validation: {
        name: {
            msg: '(Required)',
            required: true,
            minLength: 2
        },
        address: {
            msg: '(Required)',
            required: true
        },
        city: {
            msg: '(Required)',
            required: true
        },
        state: {
            msg: '(Required)',
            required: true
        },
        zipcode: {
            fn: function (value, attr, computed_state) {
                // Validate zipcode for US and allow null for international
                if (computed_state.country !== 'US') {
                    return false;
                }
            },
            msg: '(Invalid)',
            pattern: /^\d{5}(?:[-\s]\d{4})?$/g,
            required: false
        },
        country: {
            msg: '(Required)',
            pattern: /[A-Z][A-Z]/g,
            required: true
        },
        email: {
            fn: function (value, attr, computed_state) {
                return !$f.utils.validate_email(value);
            },
            msg: '(Invalid)',
            required: false
        }
    },

    constructor: function (attributes, options) {
        // if model.tags is set and not instance of AccountTags then must be an array/dict
        if (attributes && attributes.tags && !(attributes.tags instanceof AccountTags)) {
            attributes.tags = new AccountTags(attributes.tags.length > 0 ? attributes.tags : null);
        }

        // if model.owners is set and not instance of AccountOwnerUsers) then must be an array/dict
        if (attributes && attributes.owners && !(attributes.owners instanceof AccountOwnerUsers)) {
            attributes.owners = new AccountOwnerUsers(attributes.owners.length > 0 ? attributes.owners : null);
        }

        Account.__super__.constructor.call(this, attributes, options);
        // If tags are not set up through constructor, then set such and set up empty AccountTags collection
        if (this.get('tags') != null) {
            this.has_gathered_tags = true;
        } else {
            this.set('tags', new AccountTags());
        }
    },

    url: '/api/v1/accounts/account/',

    sync: function (method, model, options) {
        if (method == 'read') {
            options.url = model.url + 'get/' + this.id;
        } else if (method == 'create') {
            options.url = model.url + 'save/';
        } else if (method == 'update') {
            options.url = model.url + 'save/' + this.id;
        } else if (method == 'delete') {
            options.url = model.url + 'remove/' + this.id;
        }

        return Backbone.sync(method, model, options);
    },

    // Did not complete below since at time no need for it - Getting tags for account when .get('tags') is called if they do not exist
    //  get: function(attr, options) {
    //      options = options || {};
    //      if (attr == 'tags') {
    //            // If tags do not exist or options force is set to True, go get tags, else pass back what tags you have
    //          if (tags == null || options.force == true) {
    //              // Go get tags for the account
    //              this._set_tags();
    //          }
    //        }
    //        results = SearchCriteriaMetadata.__super__.get.call(this,attr, options);
    //        return results;
    //    },

    // possible solution: take what's out of parse (or constructor) and listen to sync()
    // Also consider overridding this method in accounts.js to avoid breaking anything
    parse: function (response, options) {
        if (response.tags && response.tags instanceof Array) {
            this.set('tags', new AccountTags(response.tags));
            delete response.tags;
        }

        if (response.owners && response.owners instanceof Array) {
            this.set('owners', new AccountOwnerUsers(response.owners));
            delete response.owners;
        }

        return response;
    },

    /**
     * Get a link to a map for the account's lat/lon, based on what platform the user is on. (web/android/ios)
     * @return {string} A url to a map if available, otherwise empty string
     */
    get_latlon_link: function () {
        if (!this.get('lat') || !this.get('lon')) {
            return '';
        }

        if ($f.mobile.is.android()) {
            return `geo:0,0?q=${this.get('lat')},${this.get('lon')}(${this.get('name')})`;
        }

        if ($f.mobile.is.ios()) {
            // @author:david apparently there's no way to let the user choose a maps app on iOS. This will always open Apple Maps, as will "maps://"
            return `http://maps.apple.com/?ll=${this.get('lat')},${this.get('lon')}&q=${this.get('name')}`;
        }

        if ($f.mobile.is.windows()) {
            return `bingmaps:?collection=point.${this.get('lat')}_${this.get('lon')}_${this.get('name')}`;
        }

        // fallback to web
        return `https://www.google.com/maps?q=${this.get('lat')},${this.get('lon')}`;
    },

    _convert_json_to_models: function () {
        if (this.get('owners') && !(this.get('owners') instanceof Backbone.Collection)) {
            this.set('owners', new AccountOwnerUsers());
        } else if (!this.get('owners')) {
            this.set('owners', new AccountOwnerUsers());
        }
    }
});

export const Accounts = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.Accounts',

    model: Account,
    paginator: null,
    tag_id: null,
    include_user_roles: false,
    loc: null, // this is a lat/long like so:  '37.2321,-122.03948'
    typeahead_search_attrs: ['name', 'address', 'city'],

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
        if (!this.paginator) {
            this.paginator = new Paginator({ order_by: 'name' });
        }
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'group_id')) {
            attrs.push('group_id');
        }
        if (!_.contains(attrs, 'tag_id')) {
            attrs.push('tag_id');
        }

        // When adding a model if select_all is selected than have new accounts selected as well
        var this_ = this;
        this.listenTo(this, 'add', function (account) {
            var select_all = this_.paginator.get('select_all');
            if (select_all) {
                account.set('is_selected', select_all); // Default is is_selected = false, so only setting if different than default
            }
        });
    },

    url: function () {
        var additional_where = this.tag_id ? '&tag_id=' + this.tag_id : '';
        additional_where += this.loc ? '&loc=' + this.loc : '';
        additional_where += this.include_user_roles ? '&include_user_roles=true' : '';
        additional_where += this.paginator.get('group_id')
            ? '&group_id=' + encodeURIComponent(this.paginator.get('group_id'))
            : '';
        additional_where += this.paginator.get('fields')
            ? '&fields=' + encodeURIComponent(this.paginator.get('fields'))
            : '';
        additional_where += this.allow_cross_company_access ? '&allow_cross_company_access=true' : '';

        if (this.paginator.get('order_by') == 'distance') {
            this.paginator.set('count', false);
        }

        // this defaults to True on the backend but include if added
        var include_group_children = this.paginator.get('include_group_children');
        if (include_group_children !== undefined) {
            additional_where += '&include_group_children=' + include_group_children.toString();
        }

        return (
            '/api/v1/accounts/list_accounts/?' +
            this.paginator.get_query_string({ get_count: this.paginator.get('order_by') != 'distance' }) +
            additional_where
        );
    },

    /**
     * Override so we can keep track of our paginator.  For some reason I didn't override the 'add' method
     */
    add_new: function (model, success_callback, error_callback) {
        if (model.isNew()) {
            this.paginator.set('results_count', this.paginator.get('results_count') + 1);
        }
        model.save().success(success_callback).error(error_callback);
        Accounts.__super__.add.call(this, model);
    }
});

/**
 * Tags of accounts
 */
export const AccountTag = BaseZenputModel.extend(
    {
        legacy_classname: '$f.models.AccountTag',

        defaults: {
            id: null,
            name: null,
            is_editable: 1,
            is_active: 1
        },

        url: '/api/v1/accounts/tag/save/'
    },
    {
        default_multi_edit_fields: {
            tags: null
        }
    }
);

export const AccountOwnerUser = Backbone.Model.extend({
    legacy_classname: '$f.models.AccountOwnerUser',

    defaults: {
        id: null,
        first_name: null,
        last_name: null,
        user_role: null,
        user_role_id: null,
        email: null
    },

    sync: function (method, model, options) {
        options.type = 'POST';
        if (method == 'create' || method == 'update') {
            options.url = model.url + 'save/';
        }
        return Backbone.sync(method, model, options);
    }
});

export const AccountOwnerUsers = Backbone.Collection.extend({
    legacy_classname: '$f.collections.AccountOwnerUsers',

    model: AccountOwnerUser,
    account_id: null,
    paginator: null,

    initialize: function (options) {
        this.paginator = new Paginator({ start: null, limit: null, order_by: 'first_name' });
    },

    url: function () {
        var additional_where = this.account_id ? '&account_id=' + this.account_id : '';
        return (
            '/api/v1/accounts/list_owners/?' + this.paginator.get_query_string({ get_count: true }) + additional_where
        );
    },

    parse: function (response) {
        this.paginator.set('results_count', response.count);
        return response.results;
    },

    comparator: function (item) {
        return item.get(this.paginator.get('order_by'));
    }
});

export const AccountTags = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.AccountTags',

    model: AccountTag,
    account_id: null, // If only want to get tags for a specific account

    initialize: function (options) {
        this.paginator = new Paginator({ start: null, limit: null, order_by: 'name' });
    },
    paginator: null,

    url: function () {
        var additional_where = this.account_id ? '&account_id=' + this.account_id : '';
        additional_where += this.paginator.get('group_id')
            ? '&group_id=' + encodeURIComponent(this.paginator.get('group_id'))
            : '';
        return '/api/v1/accounts/list_tags/?' + this.paginator.get_query_string({ get_count: true }) + additional_where;
    },

    parse: function (response) {
        this.paginator.set('results_count', response.count);
        return response.results;
    },

    comparator: function (item_first, item_second) {
        // Make sure that our 'All Locations' fields is always sorted on top
        // if no field is selected
        if (item_first.get('is_editable') === false) {
            return -1;
        }

        // Normal sort for everything else
        // https://backbonejs.org/#Collection-comparator
        const order_by_field = this.paginator.get('order_by');
        const first_value = item_first.get(order_by_field);
        const second_value = item_second.get(order_by_field);

        if (first_value < second_value) {
            return -1;
        } else if (first_value > second_value) {
            return 1;
        } else {
            return 0;
        }
    }
});

export const AccountAndTag = Backbone.Model.extend({
    legacy_classname: '$f.models.AccountAndTag',

    defaults: {
        id: null, // 'account-<account_id>' or 'tag-<tag_id>' Done like this to be unique
        raw_id: null, // The raw id of either account_id or tag_id
        name: null, // The label of the account or tag
        type: null // The type of object either 'account' or 'tag'
    }
});

export const AccountsAndTags = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.AccountsAndTags',

    model: AccountAndTag,
    paginator: null, // Only using search_query to get results, controller handling rest
    typeahead_search_attrs: ['name', 'address'],
    is_active: null,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        this.paginator = new Paginator();
    },

    url: function () {
        var additional_where = this.is_active ? '&is_active=' + this.is_active : '';
        return '/api/v1/accounts/list_accounts_tags/?' + this.paginator.get_query_string() + additional_where;
    }
});

export const Locations = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.Locations',
    search_param: 'search',

    model: AccountAndTag,
    paginator: null,
    typeahead_search_attrs: ['address', 'name'],
    default_paginator_options: {
        limit: null,
        order_by: 'name',
        search: '',
        start: null
    },

    initialize: function (models, options) {
        const this_ = this;
        options = options || {};
        $.extend(this, _.omit(options, 'search_options'));

        if (!this.paginator) {
            if (options && options.search_options) {
                _.extend(this_.default_paginator_options, options.search_options);
            }
            this.paginator = new Paginator(this.default_paginator_options);
            if (options.search_options && options.search_options.update_paginator) {
                options.search_options.update_paginator(this.paginator);
            }
        }

        Locations.__super__.initialize.apply(this, arguments);
    },

    url: function () {
        const attrs = ['limit', 'order_by', 'search', 'start'];
        let additional_where = this.allow_cross_company_access ? '&allow_cross_company_access=true' : '';
        additional_where += this.include_fbc_locations ? '&include_fbc_locations=true' : '';

        return (
            '/api/v3/locations/?' +
            this.paginator.get_query_string({
                attrs: attrs,
                get_count: true
            }) +
            additional_where
        );
    }
});

/**
 * Tasks
 */
export const TaskStatus = Backbone.Model.extend({
    legacy_classname: '$f.models.TaskStatus',

    defaults: {
        type: 'open', // open, closed
        name: 'Incomplete'
    }
});

export const TaskReply = Backbone.Model.extend({
    legacy_classname: '$f.models.TaskReply',

    defaults: {
        type: null,
        form_template: null
    },
    initialize: function (attributes, options) {
        this._convert_json_to_models();
        var this_ = this;
        this.on('change', function () {
            this_._convert_json_to_models();
        });
    },

    /**
     * Convert sub-models from json objects to real collections
     */
    _convert_json_to_models: function () {
        if (this.get('form_template') && !(this.get('form_template') instanceof Backbone.Model)) {
            this.set('form_template', new FormTemplate(this.get('form_template')), { silent: true });
        }
    }
});

export const BaseTaskSource = BaseZenputModel.extend({
    legacy_classname: '$f.models.TaskSource',

    defaults: {
        type: null,
        rule: null, // $f.rules.models.Rule
        event: null, // $f.models.Event
        form_submission: null // FormStructure
    },

    submodels: {
        form_submission: FormStructure // @bug @author:david this should be MobileFormStructure on mobile!
    }
});

export const TaskFulfillment = Backbone.Model.extend({
    legacy_classname: '$f.models.TaskFulfillment',

    parent_task: null, // BaseTask

    defaults: {
        type: null, // this should be the same as BaseTask.reply.type
        date_created: null, // datetime started on the phone
        date_completed: null, // datetime finished on the phone
        date_submitted: null, // datetime received by server
        user: null, // User - the completer of this task

        // Used for type:form_submission
        form_template: null, // { id: ## }
        form_submission: null, // { $oid: ## }

        // Used for type:quick
        fields: null, // [MobileMediaField],
        lat: null,
        lon: null
    },

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        var this_ = this;

        if (!$.isArray(this.get('fields'))) {
            this.set('fields', []);
        }

        this._convert_json_to_models();
        this.on('change', function () {
            this_._convert_json_to_models();
        });
    },

    _convert_json_to_models: function () {
        var this_ = this;
        $.each(this.get('fields'), function (i, field) {
            if (!(field instanceof Backbone.Model)) {
                this_.get('fields')[i] = new BaseField(field);
            }
        });
    }
});

export const Followers = Backbone.Collection.extend({
    legacy_classname: '$f.collections.Followers',

    model: User
});

export const FollowUpAction = Backbone.Model.extend({
    legacy_classname: '$f.models.FollowUpAction',

    defaults: {
        assigned_user: null,
        date_due: null,
        description: null,
        form: null,
        instructions_images: null,
        location: null,
        reply_type: 'task',
        require_photo: false,
        title: null
    },

    validation: {
        date_due: { fn: '_validate_date_due' },
        description: { fn: '_validate_description' },
        form: { fn: '_validate_form' },
        location: { fn: '_validate_location' },
        title: { fn: '_validate_title' }
    },

    initialize: function (attributes, options) {
        if (!this.get('instructions_images')) {
            this.set('instructions_images', new $f.collections.Images());
        }
    },

    _validate_date_due: function (value, attr, computed_state) {
        if (!value) {
            return 'Please enter a due date.';
        }
    },

    _validate_description: function (value, attr, computed_state) {
        if (!value || !value.trim() || value.length < 2) {
            return 'Please enter a description.';
        }
    },

    _validate_form: function (value, attr, computed_state) {
        if (computed_state.reply_type === 'form' && !value) {
            return 'Please enter a form.';
        }
    },

    _validate_location: function (value, attr, computed_state) {
        if (!value) {
            return 'Please enter a location.';
        }
    },

    _validate_title: function (value, attr, computed_state) {
        if (!value || !value.trim() || value.length < 2) {
            return 'Please enter a title.';
        }
    }
});

export const Image = Backbone.Model.extend({
    legacy_classname: '$f.models.Image',

    defaults: {
        url: null, // s3 url
        native_url: null, // local url for editing tasks on mobile
        base_64: null // base_64 data URI
    },

    /**
     * Returns whether or not the model's url is an image data URI
     * @returns {boolean}
     */
    is_image_data_uri: function () {
        const filePath = this.get('url').s3_key ? this.get('url').s3_key : this.get('url');
        return filePath.indexOf('data:image') === 0;
    },

    /**
     * Get our URL including our S3 bucket
     *
     * @note @author:david @author:kgstew Android will not open PDFs with https URLs
     * in the native PDF viewer so we will use http instead of https if requested.
     */
    get_full_url: function (use_native_url) {
        const url = this.get('url');
        const imageUrl = url.s3_key ? url.s3_key : url;

        if (!imageUrl) {
            return null;
        }

        if ($f.storage.is_data_uri(url)) {
            if ($f.mobile.is.windows()) {
                // for whatever reason, cdvfile links don't always work on windows. Use ms-appdata instead.
                return imageUrl.replace('cdvfile://localhost/persistent/', 'ms-appdata:///local/');
            }

            if (use_native_url) {
                if (!this.get('native_url')) {
                    return null;
                }
                return this.get('native_url');
            }

            return imageUrl;
        }

        return $f.storage.get_storage_full_url(imageUrl);
    }
});

/*
 * Need to save the base_64 data URI for rendering of the thumbnail before it is processed
 */
export const Images = Backbone.Collection.extend({
    legacy_classname: '$f.collections.Images',

    model: function (url, options) {
        options = options || {};

        var model = new Image({ url: url });
        model.collection = options.collection;
        return model;
    },

    toJSON: function () {
        return this.pluck('url');
    }
});

export const BaseTaskProject = BaseZenputModel.extend(
    _.extend({}, TaskProjectUtilsMixin, {
        legacy_classname: '$f.models.BaseTaskProject'
    })
);

export const BaseTask = BaseTaskProject.extend(
    {
        legacy_classname: '$f.models.Task',

        task_source_klass: BaseTaskSource,

        merge_with_project: false, // Are we going to add this task to a project?

        submodels: {
            subscribers: Users
        },

        defaults: {
            id: null,
            title: null,
            description: null,
            title_image: null, // []
            version: { id: 1 },
            company: null, // Company
            status: null, // TaskStatus
            reply: null, // TaskReply
            fulfillment: null, // TaskFulfillment
            project: null, // Project
            account: null, // Account
            source: null, // BaseTaskSource
            reporter: null, // User
            assignee: null, // User
            followers: null, // Followers
            date_created: null,
            date_modified: null,
            date_due: null,
            date_start_local: null,
            date_due_local: null,
            // not an attribute we receive from the backend, but one we can set when creating new tasks
            // which then generates date_due. Date_due should not be used to create tasks from the front-end.
            date_due_naive: null,
            date_start: null,
            is_active: true,
            is_closed: false,
            time_zone: null,
            lat: null,
            lon: null,
            // Used internally but should never be sent to server:
            is_edit_mode: false,
            // title_image_field: null, // ImageAndVideoFieldViewBase - temporary model to hold images until we upload, where we convert this to title_iamge []
            deleted: false,
            has_comments: false,
            subscribers: null // Users, not set for list views
        },

        _compute_mobile_properties: function () {
            if (!$f.utils.is_mobile_app()) {
                return;
            }

            const processing_record = $f.mobile.pending_tasks_in_processing.get(this.get('id'));

            if (processing_record) {
                if (['complete', 'complete_with_exception'].includes(this.get('current_state'))) {
                    $f.mobile.pending_tasks_in_processing.remove_records(this.get('id'));
                } else {
                    this.set('current_state', 'processing');
                }
            }

            if (!$f.mobile.is.connected()) {
                this._compute_mobile_offline_properties();
            }
        },

        _compute_mobile_offline_properties: function () {
            var date_start = this.get('date_start');
            if (this.get('current_state') === 'unavailable' && date_start && date_start.$date < moment().valueOf()) {
                this.set('current_state', 'open');
            }
            var date_due = this.get('date_due');
            if (this.get('current_state') === 'open' && date_due && date_due.$date < moment().valueOf()) {
                this.set('current_state', 'overdue');
            }
        },

        initialize: function (attributes, options) {
            this.set('status', new TaskStatus(), { silent: true });
            if (!this.get('followers')) {
                this.set('followers', [], { silent: true });
            } // safer not to define [] or {} values in `this.defaults`
            this.set(attributes); // should do this automatically, but doesn't with collection.fetch()
            var this_ = this;

            if (!this.get('title_image')) {
                this.set('title_image', [], { silent: true });
            } // safer not to define [] or {} values in `this.defaults`

            this._convert_json_to_models();
            this.on('change', function () {
                this_._convert_json_to_models();
            });

            this._compute_mobile_properties();

            if (!this.get('project') || !this.get('project').id) {
                this.set('project', null);
            } else {
                this.merge_with_project = true;
            }
        },

        validation: {
            title: { fn: '_validate_title' },
            project: { fn: '_validate_project_title' },
            reply: { fn: '_validate_reply' }
        },

        _validate_reply: function (value, attr, computed_state) {
            var form_template_title = value.get('form_template') ? value.get('form_template') : null;
            if (value.get('type') == 'form_submission' && !form_template_title) {
                return 'Form is required when Task Type is Submit Form';
            }

            if (value.get('type') != 'form_submission' && form_template_title) {
                return 'Form is not allowed unless Task Type is Submit Form';
            }
        },

        _validate_title: function (value, attr, computed_state) {
            if (!value || !value.trim() || value.length < 2) {
                return 'Please enter a title.';
            }
        },

        /**
         * Project Title cannot be just spaces
         */
        _validate_project_title: function (value, attr, computed_state) {
            if (value != null && value.get('title') != null && value.get('id') == null) {
                return 'You must select an existing project';
            }
        },

        url: '/api/v1/tasks/task',

        sync: function (method, model, options) {
            switch (method) {
                case 'read':
                    options.url = `${model.url}/get/${model.id}`;
                    break;
                case 'create':
                case 'update':
                    options.url = `${model.url}/save/`;
                    break;
                case 'delete':
                    options.url = `${model.url}/archive/${model.id}`;
            }

            return Backbone.sync(method, model, options);
        },

        parse: function (response, options) {
            return response.results;
        },

        /**
         * Convert sub-models from json objects to real collections
         *
         * @note @author:david This can now be handled by BaseZenputModel if we use the this.submodels {}
         */
        _convert_json_to_models: function () {
            if (this.get('status') && !(this.get('status') instanceof Backbone.Model)) {
                this.set('status', new TaskStatus(this.get('status')), { silent: true });
            }
            if (this.get('company') && !(this.get('company') instanceof Backbone.Model)) {
                this.set('company', new Company(this.get('company')), { silent: true });
            }
            if (this.get('reply') && !(this.get('reply') instanceof Backbone.Model)) {
                this.set('reply', new TaskReply(this.get('reply')), { silent: true });
            }
            if (this.get('source') && !(this.get('source') instanceof Backbone.Model)) {
                this.set('source', new this.task_source_klass(this.get('source')), { silent: true });
            }
            if (this.get('reporter') && !(this.get('reporter') instanceof Backbone.Model)) {
                this.set('reporter', new User(this.get('reporter')), { silent: true });
            }
            if (this.get('project') && !(this.get('project') instanceof Backbone.Model)) {
                this.set('project', new Project(this.get('project')), { silent: true });
            }
            if (this.get('assignee') && !(this.get('assignee') instanceof Backbone.Model)) {
                this.set('assignee', new User(this.get('assignee')), { silent: true });
            }
            if (this.get('account') && !(this.get('account') instanceof Backbone.Model)) {
                this.set('account', new Account(this.get('account')), { silent: true });
            }
            if (this.get('fulfillment') && !(this.get('fulfillment') instanceof Backbone.Model)) {
                this.set(
                    'fulfillment',
                    new TaskFulfillment(this.get('fulfillment'), {
                        parent_task: this
                    }),
                    { silent: true }
                );
            }
            if (this.get('followers') && !(this.get('followers') instanceof Backbone.Collection)) {
                this.set('followers', new Followers(this.get('followers')), { silent: true });
            }

            if (this.get('title_image') == null || this.get('title_image') == '') {
                this.set('title_image', [], { silent: true });
            }
            if (this.get('title_image') && !(this.get('title_image') instanceof Backbone.Collection)) {
                this.set('title_image', new Images(this.get('title_image')), { silent: true });
            }
        },

        /**
         * Detect weather or not this task has been fulfilled.
         */
        is_fulfilled: function () {
            return this.get('status').get('type') == 'closed';
        },

        /**
         * Check if the user's company has the feature task_edit_unrestrict
         * @returns {Boolean}
         */
        company_has_task_edit_unrestrict_feature: function () {
            if ($f.mobile) {
                return $f.active_company.has_feature('task_edit_unrestrict');
            } else {
                return $f.user.company.has_feature('task_edit_unrestrict');
            }
        },

        /**
         * Checks if a task can be edited by the current user
         * @returns {Boolean}
         */
        user_can_edit: function (user) {
            var reporter_user = this.get('reporter');
            var assignee_user = this.get('assignee');

            if (user.is_admin() || (this.company_has_task_edit_unrestrict_feature() && user.is_manager())) {
                return true;
            }

            if (user.is_manager()) {
                const source = this.get('source');
                const is_triggered = Boolean(source && source.get('event') && source.get('event').id);

                // Don't allow managers to modify tasks assigned to them by trigger @jira:ZP-5398
                if (is_triggered && user.id === assignee_user.id) {
                    return false;
                }

                if (assignee_user.is_admin() || (reporter_user.is_admin() && assignee_user.is_manager())) {
                    return false;
                }

                return true;
            }

            var is_own_task = reporter_user.id === user.id;
            var source = this.get('source');
            var type = source ? source.get('type') : '';
            if (is_own_task && type === 'rule_event') {
                return false;
            }

            return is_own_task;
        },

        /**
         * Checks if a task can be deleted by the current user
         * Any User can delete unassigned tasks that they are able to edit
         * Managers can edit but not delete or archive tasks assigned to submitters by admins
         * @returns {Boolean}
         */
        user_can_delete: function (user) {
            var reporter_user = this.get('reporter');
            var assignee_user = this.get('assignee');
            //Can delete and archive with task edit unrestrict feature flag
            if (user.is_admin() || (this.company_has_task_edit_unrestrict_feature() && user.is_manager())) {
                return true;
            }
            //removes delete button when user is creating a new task
            if (this.get('account') === null) {
                return false;
                //admin and owner can delete any task
            } else if (user.is_admin() || user.is_owner()) {
                return true;
                // no one can delete tasks assigned by admin unless admin
            } else if (reporter_user.is_admin()) {
                return false;
                // cannot delete task assigned to admin
            } else if (assignee_user && assignee_user.is_admin()) {
                return false;
                //any user can delete unassigned tasks (excepts those assigned by admin)
            } else if (assignee_user && assignee_user.id === null) {
                return true;
                //prevent managers from deleting tasks assigned by admins but OK to delete tasks assigned by managers
            } else if (user.is_manager()) {
                if (reporter_user.is_admin()) {
                    return false;
                } else if (reporter_user.is_manager() || reporter_user.is_submitter()) {
                    return true;
                }
            } else if (user.is_submitter() && reporter_user.is_submitter()) {
                return true;
            }
            return false;
        },

        /**
         * Format relative date_due for the tasks time zone, not the users.
         * @returns { null || { label: {String}, color: {String} }}
         */
        get_formatted_relative_date_and_time: function () {
            var date_due_aware = this._get_zoned_time('date_due');
            if (date_due_aware === null) {
                return null;
            }
            var date_info = $f.utils.get_date_str_with_color(date_due_aware);
            var time_due = this.get_time_start_and_due_str();

            if (time_due) {
                var date_start_aware = this._get_zoned_time('date_start');
                if (date_start_aware) {
                    var date_start_info = $f.utils.get_date_str_with_color(date_start_aware);
                    if (date_start_info.label !== date_info.label) {
                        var time_parts = time_due.split('-');
                        date_info.label =
                            date_start_info.label +
                            ', ' +
                            time_parts[0].trim() +
                            ' - ' +
                            date_info.label +
                            ', ' +
                            time_parts[1].trim();
                        return date_info;
                    }
                }
                date_info.label += ', ' + time_due;
            }

            return date_info;
        },

        delete: function (options) {
            var this_ = this;
            options = options || {};
            options.success = options.success || function () {};
            options.error = options.error || function () {};

            var ajax = $f.utils.select_ajax();
            ajax({
                url: `${this_.url}/destroy/${this_.id}`,
                dataType: 'json',
                type: 'POST',
                cache: false,
                success: function (response) {
                    options.success();
                },
                error: function (response) {
                    options.error();
                }
            });
        },

        /*
         * Calculate filter status from is_active and is_closed
         * logic copied from tasks/views.py:_build_common_task_request_criterion
         */
        get_filter_status: function () {
            var is_active = this.get('is_active');
            var is_closed = this.get('is_closed');
            if (is_active === true && is_closed === false) {
                return 'active_incomplete';
            } else if (is_active === false && is_closed === false) {
                return 'archived_incomplete';
            } else if (is_closed === true) {
                return 'complete';
            } else {
                return 'all_tasks';
            }
        }
    },
    {
        default_multi_edit_fields: {
            date_due: null,
            project_id: null,
            assignee_user_id: null
        }
    }
);

// Model to contain attributes for multi-edit update data, initialized with different defaults based on the updating object
export const MultiEditUpdateData = Backbone.Model.extend({
    legacy_classname: '$f.models.MultiEditUpdateData'
});

// This is the model to sync when doing a batch update on tasks
export const BaseMultiEdit = Backbone.Model.extend({
    legacy_classname: '$f.models.BaseMultiEdit',

    defaults: {
        url: null,
        update_data: null, // MultiEditUpdateData
        collection: null, // $f.collection.Tasks
        id: null // if only passing in the id of a single model
    },

    get_query_string: function (url) {
        // common logic between different multi-edit forms
        // all have inclusion/exclusion logic based on the current state of the collection.paginator.select_all
        var inclusion_exclusion_str, search_query;

        if (this.get('id')) {
            // passed in just a single id, there is no collection
            inclusion_exclusion_str = 'include=' + JSON.stringify([this.get('id')]);
            search_query = '';
        } else {
            var collection = this.get('collection');
            var models = new Backbone.Collection(
                collection.where({ is_selected: !collection.paginator.get('select_all') })
            );
            var model_ids = models.pluck('id');
            inclusion_exclusion_str = collection.paginator.get('select_all')
                ? '&exclude=' + JSON.stringify(model_ids)
                : '&include=' + JSON.stringify(model_ids);
            // search query is the get parameters that the search parameters on the page are using
            search_query = collection.paginator.get_query_string({ get_count: false });
        }
        var json_update_string = JSON.stringify(this.get('update_data'));

        return `${url}?${search_query}${inclusion_exclusion_str}&update_data=${json_update_string}`;
    },

    sync: function (method, model, options) {
        switch (method) {
            case 'create':
                options.url = model.get_query_string(this.get('url'));
                break;
            case 'update':
                options.url = model.get_query_string(this.get('url'));
                break;
            case 'delete':
        }

        return Backbone.sync(method, model, options);
    }
});

export const BaseBatchTask = BaseTask.extend({
    legacy_classname: '$f.models.BatchTask',

    defaults: {
        id: null,
        title: null,
        description: null,
        title_image: null, // []
        version: { id: 1 },
        reply: null, // TaskReply
        project: null, // Project
        user_role: null, // UserRole
        distribute_group: null, // DistributeGroup
        account_tag: null, // AccountTag
        date_due: null, // @deprecated @author:david this is no longer used except for mobile. Once we switch
        // mobile over to use the same process for creating tasks/projects as the webapp, this
        // can be removed.
        date_start: null,
        estimate_task_count: 0,
        estimate_user_count: 0,
        time_frame: null, // This is only used on daily recurring projects

        date_due_type: null,
        days_after: null
    },

    initialize: function (options) {
        this.set(
            {
                project: new Project(),
                user_role: new UserRole(),
                distribute_group: new DistributeGroup(),
                account_tag: new AccountTag()
            },
            {
                silent: true
            }
        );

        BaseBatchTask.__super__.initialize.apply(this, arguments);
    },

    _convert_json_to_models: function () {
        if (this.get('project') && !(this.get('project') instanceof Backbone.Model)) {
            this.set('project', new Project(this.get('project')), { silent: true });
        }
        if (this.get('user_role') && !(this.get('user_role') instanceof Backbone.Model)) {
            this.set('user_role', new UserRole(this.get('user_role')), { silent: true });
        } else if (this.get('user_role') === null) {
            this.set('user_role', new UserRole(), { silent: true });
        }
        if (this.get('distribute_group') && !(this.get('distribute_group') instanceof Backbone.Model)) {
            this.set('distribute_group', new DistributeGroup(this.get('distribute_group')), { silent: true });
        } else if (this.get('distribute_group') === null) {
            this.set('distribute_group', new DistributeGroup(), { silent: true });
        }
        if (this.get('account_tag') && !(this.get('account_tag') instanceof Backbone.Model)) {
            this.set('account_tag', new AccountTag(this.get('account_tag')), { silent: true });
        }

        BaseBatchTask.__super__._convert_json_to_models.apply(this, arguments);
    },

    sync: function (method, model, options) {
        switch (method) {
            case 'read':
                options.url = `${model.url}/estimate_batch_generate/?${model.get_estimate_query_string()}`;
                break;
            case 'create':
                options.url = `${model.url}/generate/`;
                break;
            case 'update':
            case 'delete':
        }

        return Backbone.sync(method, model, options);
    },

    validation: $.extend(
        {
            user_role: { fn: '_validate_user_role' }
        },
        BaseTask.prototype.validation
    ),

    /**
     * User role is required; verify the model has an ID.
     */
    _validate_user_role: function (value, attr, computed_state) {
        if (!value.get('id')) {
            return 'Please select a user role.';
        }
    },

    _validate_reply: function (value, attr, computed_state) {
        if (this.get('reply').get('type') != 'form_submission') {
            return false;
        }

        var id, title;
        try {
            id = this.get('reply').get('form_template').get('id');
            title = this.get('reply').get('form_template').get('title');
        } catch (e) {
            // keep null values
        }

        if (!id || !title) {
            return 'Please select a form template';
        }
    },

    parse: function (response) {
        return response.results;
    },

    get_estimate_query_string: function (options) {
        var querystring = '';

        var this_ = this;
        $.each(['user_role', 'account_tag', 'distribute_group'], function (i, attr_name) {
            if (!this_.get(attr_name).get('id')) {
                return true;
            }
            querystring += `${attr_name}_id=${encodeURIComponent(this_.get(attr_name).get('id'))}&`;
        });

        querystring = querystring.substring(0, querystring.length - 1); // chop off that last "&"

        return querystring;
    }
});

export const Tasks = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.Tasks',

    model: BaseTask,
    paginator: null, // Paginator
    typeahead_search_attrs: ['title'],
    default_paginator_options: {
        order_by: 'date_due_local',
        search_by: 'tasks',
        include_future_tasks: true,
        view_type: 'full'
    },

    // Allows us to include global group search options when filtering task list. Mobile dashboard uses these instead
    // of offering group filter options in the task tab
    allow_group_id_on_me_filter: false,

    /**
     * This collection allows paginator extension via search_options and paginator_options attributes in the options.
     * A default set of paginator options exists. Do not override these defaults unless you mean to.
     * @param models
     * @param options
     */
    initialize: function (models, options) {
        var this_ = this;
        options = options || {};
        $.extend(this, _.omit(options, 'search_options'));

        if (!this.paginator) {
            if (options && options.search_options) {
                _.extend(this_.default_paginator_options, options.search_options);
            }

            if (options && options.paginator_options) {
                _.extend(this_.default_paginator_options, options.paginator_options);
            }

            this.paginator = new Paginator(this_.default_paginator_options);

            // TODO - search_options is being passed in either as a primitive, or as an instance of BaseSearchOptions
            if (options.search_options && options.search_options.update_paginator) {
                options.search_options.update_paginator(this.paginator);
            }
        }

        this_._repair_group_id();
        this.listenTo(this.paginator, 'change', function () {
            this_._repair_group_id();
        });
    },

    url: function () {
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'status')) {
            attrs.push('status');
        }
        if (!_.contains(attrs, 'loc')) {
            attrs.push('loc');
        }
        if (!_.contains(attrs, 'filter')) {
            attrs.push('filter');
        }
        if (!_.contains(attrs, 'assignee_user_id')) {
            attrs.push('assignee_user_id');
        }
        if (!_.contains(attrs, 'group_id')) {
            attrs.push('group_id');
        }
        if (!_.contains(attrs, 'account_id')) {
            attrs.push('account_id');
        }
        if (!_.contains(attrs, 'project_ids')) {
            attrs.push('project_ids');
        }
        if (!_.contains(attrs, 'search_query')) {
            attrs.push('search_query');
        }
        if (!_.contains(attrs, 'start_date')) {
            attrs.push('start_date');
        }
        if (!_.contains(attrs, 'end_date')) {
            attrs.push('end_date');
        }
        if (!_.contains(attrs, 'submitted_date_start')) {
            attrs.push('submitted_date_start');
        }
        if (!_.contains(attrs, 'submitted_date_end')) {
            attrs.push('submitted_date_end');
        }
        if (!_.contains(attrs, 'include_future_tasks')) {
            attrs.push('include_future_tasks');
        }
        if (!_.contains(attrs, 'include_future_tasks_v2')) {
            attrs.push('include_future_tasks_v2');
        }
        if (!_.contains(attrs, 'include_date_due_null')) {
            attrs.push('include_date_due_null');
        }
        if (!_.contains(attrs, 'view_type')) {
            attrs.push('view_type');
        }
        if (!_.contains(attrs, 'include_has_exceptions')) {
            attrs.push('include_has_exceptions');
        }
        if (!_.contains(attrs, 'group_by_overdue')) {
            attrs.push('group_by_overdue');
        }
        if (!_.contains(attrs, 'parent_project_id')) {
            attrs.push('parent_project_id');
        }
        if (!_.contains(attrs, 'bucket')) {
            attrs.push('bucket');
        }
        if (!_.contains(attrs, 'tz')) {
            attrs.push('tz');
        }
        if (!_.contains(attrs, 'type')) {
            attrs.push('type');
        }

        return '/api/v1/tasks/list_tasks/?' + this.paginator.get_query_string({ get_count: true });
    },

    /**
     * Adds a single new model to the collection and also updates the paginator. Only to be used with new models,
     * though they may have an ID so you can save() it before add_new()ing it.
     *
     * @param {model} add_new The new model to add
     */
    add_new: function (model) {
        this.paginator.increase_results_count();
        this.add(model);
    },

    /**
     * We will only include the group_id if allow_group_id_on_me_filter is true and filter is 'me'. Otherwise, we'll remvoe it.
     */
    _repair_group_id: function () {
        if (this.allow_group_id_on_me_filter) {
            return;
        }

        if (this.paginator.get('filter') !== 'me') {
            return;
        }

        this.paginator.set('group_id', null, { silent: true });
    }
});

export const SearchOption = Backbone.Model.extend({
    legacy_classname: '$f.models.SearchOption',

    defaults: {
        field: null,
        value: null,
        display_value: null,
        select_options: null, // []
        offline_only: false // if true, should only be used by local and not sent to server
    },

    initialize: function () {
        if (!this.get('select_options')) {
            this.set('select_options', []);
        }
    }
});

/**
 * Recurring Projects!
 */

/**
 * Helpers for recurring projects
 */
export const global_recurring_utils = {
    legacy_classname: '$f.utils.recurring',

    weekdays: new Backbone.Collection([
        {
            code: 'MO',
            name: 'Monday',
            abbreviation: 'M',
            rrule_weekday_obj: RRule.MO,
            weekday_js: 1
        },
        {
            code: 'TU',
            name: 'Tuesday',
            abbreviation: 'T',
            rrule_weekday_obj: RRule.TU,
            weekday_js: 2
        },
        {
            code: 'WE',
            name: 'Wednesday',
            abbreviation: 'W',
            rrule_weekday_obj: RRule.WE,
            weekday_js: 3
        },
        {
            code: 'TH',
            name: 'Thursday',
            abbreviation: 'T',
            rrule_weekday_obj: RRule.TH,
            weekday_js: 4
        },
        {
            code: 'FR',
            name: 'Friday',
            abbreviation: 'F',
            rrule_weekday_obj: RRule.FR,
            weekday_js: 5
        },
        {
            code: 'SA',
            name: 'Saturday',
            abbreviation: 'S',
            rrule_weekday_obj: RRule.SA,
            weekday_js: 6
        },
        {
            code: 'SU',
            name: 'Sunday',
            abbreviation: 'S',
            rrule_weekday_obj: RRule.SU,
            weekday_js: 0
        }
    ]),

    frequencies: new (Backbone.Collection.extend({
        NEVER: 7,
        YEARLY: RRule.YEARLY, // 0
        MONTHLY: RRule.MONTHLY, // 1
        WEEKLY: RRule.WEEKLY, // 2
        DAILY: RRule.DAILY // 3
    }))([
        {
            id: 7, // NEVER
            name: 'Never'
        },
        {
            id: RRule.YEARLY,
            name: 'Yearly',
            unit: 'year',
            plural_unit: 'years'
        },
        {
            id: RRule.MONTHLY,
            name: 'Monthly',
            unit: 'month',
            plural_unit: 'months'
        },
        {
            id: RRule.WEEKLY,
            name: 'Weekly',
            unit: 'week',
            plural_unit: 'weeks'
        },
        {
            id: RRule.DAILY,
            name: 'Daily',
            unit: 'day',
            plural_unit: 'days'
        }
    ])
};

export const RruleCustomAttrs = BaseZenputModel.extend({
    legacy_classname: '$f.models.RruleCustomAttrs',

    defaults: {
        date_start: null, // this is the date that project CLASS will start
        date_start_naive: null, // a naive version of date_start. While we rely on rrule.date_start
        // to be set for rrule, etc., we will only send this to the back-end so it
        // can convert it to UTC14. @see RecurringProject.save()/.parse()
        dtstart_naive: null, // Same as above, but for rrule.dtstart
        set_min_start: true, // Set to true uses a min start 2 days ahead, false uses today
        dtstart_local: null, // Used in v2 projects.
        time_zone: null, // Used by for specifing time_zone of execution of recurring rule
        monthly_recurring_day: null //dom(day of month), dow(day of week), eom(end of month)
    }
});

export const Rrule = BaseZenputModel.extend({
    legacy_classname: '$f.models.Rrule',

    defaults: {
        frequency: null,
        dtstart: null,
        until: null,
        count: null,
        interval: 1,
        wkst: null,
        byweekday: null,
        bymonth: null,
        bysetpos: null,
        bymonthday: null,
        byyearday: null,
        byweekno: null,
        byhour: null,
        byminute: null,
        bysecond: null,
        custom_attrs: null
    },

    submodels: {
        custom_attrs: RruleCustomAttrs
    },

    /**
     * Assure that none of the above defaults are ever set as `undefined`. If so,
     * we'll change them to `null`.
     */
    initialize: function (attributes, options) {
        options = options || {};
        $.extend(this, options);

        var this_ = this;
        this.get('custom_attrs') || this.set('custom_attrs', {});
        var now = new Date();
        var dtstart_local = $f.utils.get_start_of_day(now).getTime();

        if (this.get('custom_attrs').get('dtstart_local')) {
            dtstart_local = Date.parse(this.get('custom_attrs').get('dtstart_local'));
            this.set('dtstart', null);
        }

        if (!this.get('dtstart')) {
            this.set('dtstart', { $date: dtstart_local }); // defaults to today
        }

        if (!this.get('custom_attrs').get('date_start') || !this.get('custom_attrs').get('date_start').$date) {
            if (this.get('custom_attrs').get('set_min_start')) {
                if (this.get('frequency') === $f.utils.recurring.frequencies.DAILY) {
                    now = moment(now).add(1, 'days').toDate();
                }
            }
            this.get('custom_attrs').set('date_start', { $date: $f.utils.get_start_of_day(now).getTime() });
        }

        this._initialize_defaults_for_frequency();

        // Set up our listeners for later changes
        this.on('change', function () {
            $.each(Object.keys(this_.attributes), function (i, attr) {
                if (typeof this_.get(attr) !== 'undefined') {
                    return true;
                } // continue
                this_.set(attr, null, { silent: true });
            });

            if (this_.get('frequency') == $f.utils.recurring.frequencies.WEEKLY) {
                this_._reset_dtstart_for_weekday();
            }
        });

        this.on('change:byweekday', function () {
            if (!this.validate) {
                return;
            } // validation hasn't init'd yet
            this.validate();
        });

        this.on('change:frequency', function () {
            this_._initialize_defaults_for_frequency();
        });

        this.on('change:dtstart', function () {
            if (this_.get('frequency') == $f.utils.recurring.frequencies.YEARLY) {
                this_.set_yearly_recurring_day(this.get('dtstart').$date);
            }
        });
    },

    set_yearly_recurring_day: function (date) {
        if (_.isNumber(date)) {
            date = new Date(date);
        }

        this.set({
            bymonthday: date.getDate(),
            bymonth: date.getMonth() + 1
        });
    },

    /**
     * Set how we should recur monthly, either by day or by week.
     * @param {string} type  The recurring type, either 'dow' or 'dom'
     */
    set_monthly_recurring_by_type: function (type) {
        if (!_.contains(['dom', 'dow', 'eom'], type)) {
            throw new Error('Programmer Error: unknown type of monthly recurring');
        }

        this.get('custom_attrs').set('monthly_recurring_day', type);

        var today = new Date();
        if (this.get('dtstart') && this.get('dtstart').$date) {
            today = new Date(this.get('dtstart').$date);
        }
        if (type == 'eom') {
            this.set({ byweekday: null, bymonthday: -1 });
            return;
        }
        if (type == 'dom') {
            this.set({ byweekday: null, bymonthday: today.getDate() });
            return;
        }

        // 'dow'
        // This will set the byweekday property by day of week. e.g. 3rd thursday of the month.
        // (this will be converted to a proper RRule Weekday object by the model)
        this.set({
            byweekday: {
                weekday: $f.utils.recurring.weekdays.findWhere({ weekday_js: today.getDay() }).get('weekday_js'),
                n: $f.utils.get_num_week_of_month(today)
            },
            bymonthday: null
        });
    },

    set_until_date: function (date) {
        if (!date) {
            this.set('until', null);
            return;
        }

        this.set({
            until: { $date: moment(date).endOf('day').toDate().getTime() },
            count: null // make sure count is unset
        });
    },

    set_until_count: function (count) {
        this.set({
            until: null, // make sure until is unset
            count: count
        });
    },

    /**
     * Get an RRule instance of this Model
     * @return {RRule} The RRule instance
     */
    get_rrule_instance: function () {
        var json = this.toJSON();

        // RRule wants 'freq' instead of 'frequency'
        json.freq = json.frequency;
        delete json.frequency;

        delete json.custom_attrs;

        // Make sure we send dates, not { $date: ### }'s
        if (json.dtstart && json.dtstart.$date) {
            json.dtstart = moment(json.dtstart.$date)._d;
        }
        if (json.until && json.until.$date) {
            json.until = moment(json.until.$date)._d;
        }

        // Make sure we have byweekday in the correct format
        if (json.byweekday) {
            delete json.bymonthday;
            if (_.isArray(json.byweekday)) {
                json.byweekday = this._byweekday_array_to_rrule_weekday(json.byweekday);
            } else {
                json.byweekday = this._byweekday_object_to_rrule_weekday(json.byweekday);
            }
        }

        return new RRule(json);
    },

    _initialize_defaults_for_frequency: function () {
        // @note frequency should never be 'NEVER', since this model should not exist in that case

        // Default byweekday to today in the case of choosing weekly frequency
        if (
            this.get('frequency') == $f.utils.recurring.frequencies.WEEKLY &&
            (!this.get('byweekday') || !this.get('byweekday').length)
        ) {
            var default_weekday_selection = $f.utils.recurring.weekdays
                .findWhere({ weekday_js: new Date().getDay() })
                .get('rrule_weekday_obj').weekday;
            this.set('byweekday', default_weekday_selection !== null ? [default_weekday_selection] : null);
            return;
        }

        // Default bymonthday to today in the case of choosing a MONTHLY frequency
        if (this.get('frequency') == $f.utils.recurring.frequencies.MONTHLY) {
            this.set({
                bymonthday: new Date(this.get('dtstart').$date).getDate(),
                bymonth: null
            });
        } else if (this.get('frequency') == $f.utils.recurring.frequencies.YEARLY) {
            this.set_yearly_recurring_day(this.get('dtstart').$date);
        } else {
            // Removing this in any other scenario because it overrides the selected frequency and turns it into monthly
            this.set({
                bymonthday: null,
                bymonth: null
            });
        }
    },

    /**
     * Converts an array of integers representing weekdays as per http://www.kanzaki.com/docs/ical/rrule.html
     * into an array of RRule Weekday objects.
     * */
    _byweekday_array_to_rrule_weekday: function (days_array) {
        var frequency = this.get('frequency');

        // only support weekly for now
        if (!days_array || frequency != $f.utils.recurring.frequencies.WEEKLY) {
            return days_array;
        }

        var rrule_byweekday = $f.utils.recurring.weekdays.filter(function (weekday) {
            return _.contains(days_array, weekday.get('rrule_weekday_obj').weekday);
        });

        rrule_byweekday = new Backbone.Collection(rrule_byweekday).pluck('rrule_weekday_obj');

        if (rrule_byweekday.length > 0) {
            return rrule_byweekday;
        }

        return null;
    },

    /**
     * Convert an onject of { weekday: #, n: # } (used for monthly recurring by nth weekday)
     * to a proper RRule Weekday object.
     */
    _byweekday_object_to_rrule_weekday: function (byweekday) {
        if (!byweekday) {
            return byweekday;
        }
        if (_.isArray(byweekday)) {
            return byweekday;
        }
        if (byweekday.nth) {
            return byweekday;
        } // already good to go, it's a Weekday object

        var weekday = $f.utils.recurring.weekdays.findWhere({ weekday_js: byweekday.weekday });
        return RRule[weekday.get('code')].nth(byweekday.n);
    },

    /**
     * Gets the next date from list of JS weekday INTs relative to start_date.
     *
     * @param   {Array} weekdays    A list of integers representing the days of the week.
     * @param   {Date}  start_date  The date on which the first instance of a project will be created.
     * @returns {Array}             A Date object.
     */
    get_earliest_date: function (weekdays, start_date) {
        var dates = [];

        for (var i in weekdays) {
            // We have to convert the JS INT into the corresponding Moment INT.
            var day = weekdays[i] !== 6 ? weekdays[i] + 1 : 0;

            var date = moment(start_date).days(day);
            if (date._d < start_date) {
                date.add(7, 'days');
            }

            dates.push(date._d);
        }

        dates.sort(function (a, b) {
            return a - b;
        });

        return dates[0];
    },

    /**
     * Sets dtstart to the next day after the date_start that matches the first day in byweekday.
     */
    _reset_dtstart_for_weekday: function () {
        if (!this.get('custom_attrs').get('date_start')) {
            return;
        }

        var byweekday = this.get('byweekday');
        if (!byweekday || !byweekday.length) {
            return;
        }

        // Here we'll get the next day after the date_start.
        var date_start = new Date(this.get('custom_attrs').get('date_start').$date);
        var new_start = this.get_earliest_date(byweekday, date_start);

        this.set('dtstart', { $date: new_start });
    },

    validation: {
        byweekday: function () {
            if (this.get('frequency') !== $f.utils.recurring.frequencies.WEEKLY) {
                return;
            }
            if (!this.get('byweekday') || this.get('byweekday').length == 0) {
                return 'Please select a day of the week.';
            }
        }
    }
});

export const Recurring = BaseZenputModel.extend({
    legacy_classname: '$f.models.Recurring',

    defaults: {
        id: null,
        queue_create_data: null,
        rrule: null,
        is_active: true
    },

    submodels: {
        rrule: Rrule
    },

    initialize: function (attrs, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.get('rrule')) {
            this.set('rrule', new Rrule(), { silent: true });
        }
    },

    /**
     * Rather than implementing our own Backbone.Validation here, let's just forward
     * the validation request to rrule.
     */
    validate: function () {
        if (!this.get('rrule').validate) {
            return;
        }
        return this.get('rrule').validate.apply(this.get('rrule'), arguments);
    }
});

/**
 * A recurring-compatible version of a Project.
 */
export const RecurringProject = BaseZenputModel.extend({
    legacy_classname: '$f.models.RecurringProject',

    _NAIVE_FORMAT: 'YYYY-MM-DDTHH:mm:ss',

    defaults: {
        id: null,
        title: null,
        description: null,
        company: null, // Company
        tasks: null, // Tasks

        date_due: null,
        date_due_naive: null, // (str) the time stored without timezone, as it should be viewed in the local time.
        // Unlike the rest of our dates, this is a simple ISO string without the 'Z' UTC identifier.
        date_start: null, // The date this project INSTANCE (not class) will start. For the class, see rrule.custom_attrs.date_start
        date_start_naive: null, // (str) a string version of date_start without any timezone information. Should be interpreted as local.
        date_created: null,
        date_modified: null,
        num_tasks_closed: null,

        num_tasks: null,
        is_active: true,

        user_role: null, // UserRole
        create_tasks_data: null, // BaseBatchTask
        recurring: null, // Recurring
        followers: null, // Followers
        is_auto_archive: false,
        is_started: true, // has the recurring project been started?
        allow_any_user_fulfill: false // allow any user to fulfill tasks generated by this project
    },

    submodels: {
        tasks: Tasks,
        company: Company,
        create_tasks_data: BaseBatchTask,
        recurring: Recurring,
        followers: Followers
    },

    url: '/api/v1/projects/project',

    sync: function (method, model, options) {
        switch (method) {
            case 'read':
                options.url = `${model.url}/get/${model.id}`;
                break;
            case 'delete':
                options.url = `${model.url}/delete/${model.id}`;
                break;
            case 'create':
                options.url = `${model.url}/save/`;
                break;
            case 'update':
                options.url = `${model.url}/save/`;
                break;
        }

        return Backbone.sync(method, model, options);
    },

    /**
     * We need to convert naive versions of dtstart and start_date to Date objects so they can be used
     * by RRule.
     */
    parse: function (response) {
        if (!response.results) {
            return response;
        }

        if (!response.results.recurring) {
            // only need to convert for recurring
            return response.results;
        }

        var rrule = response.results.recurring.rrule;
        var custom_attrs = rrule.custom_attrs;

        if (custom_attrs && custom_attrs.dtstart_naive) {
            rrule.dtstart = { $date: moment(custom_attrs.dtstart_naive, this._NAIVE_FORMAT).toDate().getTime() };
        }

        if (custom_attrs && custom_attrs.date_start_naive) {
            custom_attrs.date_start = {
                $date: moment(custom_attrs.date_start_naive, this._NAIVE_FORMAT).toDate().getTime()
            };
        }

        return response.results;
    },

    initialize: function (options) {
        this.set('create_tasks_data', this.get('create_tasks_data') || new BaseBatchTask(), { silent: true });
        if (!this.get('followers')) {
            this.set(
                'followers',
                new Followers({
                    id: $f.user.id,
                    display_name: $f.user.display_name
                }),
                { silent: true }
            );
        }
    },

    /**
     * Set our recurring frequency. If $f.utils.recurring.frequencies.NEVER, we will set the recurring
     * attribute to null.
     */
    set_recurring_frequency: function (frequency_id) {
        this.update_project_followers(frequency_id);

        if (frequency_id == $f.utils.recurring.frequencies.NEVER) {
            this.set({
                recurring: null,
                date_start: null
            });
            return;
        }

        this.set({
            date_due: null,
            recurring: new Recurring({
                rrule: { frequency: frequency_id }
            })
        });

        // for daily, the dtstart should always match the date_start, so let's default it
        if (frequency_id == $f.utils.recurring.frequencies.DAILY) {
            this.get('recurring')
                .get('rrule')
                .set('dtstart', _.clone(this.get('recurring').get('rrule').get('custom_attrs').get('date_start')));
            this.get('create_tasks_data').set('time_frame', { start: '00:00', end: '23:59' });
        } else {
            this.get('create_tasks_data').set('time_frame', null); // We only want to pass values in time_frame if frequency is daily.
        }
    },

    update_project_followers: function (frequency_id) {
        // If we are setting the project to daily recurring we want to remove the default followers so the
        // project creator does not get spammed with daily notifications.
        if (frequency_id === $f.utils.recurring.frequencies.DAILY) {
            if (this.get('followers').length === 1 && this.get('followers').at(0).id === $f.user.id) {
                this.get('followers').reset();
            }
        } else if (this.get('followers').length === 0) {
            // If we removed the followers when setting recurring to daily, we want to add back the default so
            // the project creator will get notifications
            this.get('followers').add(
                new User({
                    id: $f.user.id,
                    display_name: $f.user.display_name
                })
            );
        }
    },

    validation: {
        title: function () {
            if (this.get('create_tasks_data').get('reply').get('type') === 'form_submission') {
                // A title is required on the backend in order to create the Submission object, so we'll set it to the
                // name of the form.
                var form_title = this.get('create_tasks_data').get('title');
                if (form_title) {
                    this.set('title', form_title);
                }
            } else if (!this.get('title')) {
                return _t('mobile_projects_title_required');
            }
        },

        user_role: function () {
            if (!this.get('create_tasks_data').get('user_role').get('id')) {
                return _t('mobile_projects_user_role_required');
            }
        },

        date_due_naive: function (value, attr, computedState) {
            if (this.get('is_auto_archive') && !this.get('recurring') && !this.get('date_due_naive')) {
                return _t('mobile_projects_due_date_required');
            }
        },

        recurring: function () {
            if (!this.get('recurring')) {
                return;
            }
            var recurring_validation = this.get('recurring').validate();
            if (!recurring_validation) {
                return;
            }

            // Return the first error recurring gives us
            return recurring_validation[Object.keys(recurring_validation)[0]];
        },

        distribute_group: function () {
            if (this.isNew() && !this.get('create_tasks_data').get('distribute_group').get('id')) {
                return _t('mobile_projects_team_required');
            }
        }
    },

    set_time_frame: function (start, end) {
        this.get('create_tasks_data').set('time_frame', { start: start, end: end });
    },

    set_days_after: function (days_after) {
        this.get('create_tasks_data').set('days_after', days_after);
    },

    /**
     * Generate a text summary that describes the RRule.
     */
    get_text_summary: function () {
        var rrule = this.get('recurring').get('rrule').get_rrule_instance();
        var summary_text = `Starting on ${this.get_recurring_start_date().format(
            'MMMM D, YYYY'
        )}, repeats ${rrule.toText()}`;

        var due_type = this.get('create_tasks_data').get('due_type');
        if (due_type && due_type === 'between') {
            var time_frame = this.get('create_tasks_data').get('time_frame');
            return (
                summary_text +
                `, due between ${$f.utils.get_formatted_time_from_24_hour_time(
                    time_frame.start
                )} and ${$f.utils.get_formatted_time_from_24_hour_time(time_frame.end)} local time`
            );
        } else if (due_type && due_type === 'after' && this.get('create_tasks_data').get('days_after')) {
            return summary_text + `, due ${this.get('create_tasks_data').get('days_after')} days later`;
        } else if (due_type && due_type === 'end_of_period') {
            // For weekly recurring with multiple days there is a product rule that says the end of the period is
            // the end of the day.
            var byweekday = this.get('recurring').get('rrule').get('byweekday');
            if (byweekday && byweekday.length > 1) {
                return summary_text + `, due by the end of ${'day'}`;
            }
            return (
                summary_text +
                `, due by the end of ${$f.utils.recurring.frequencies
                    .get(this.get('recurring').get('rrule').get('frequency'))
                    .get('unit')}`
            );
        } else {
            return summary_text;
        }
    },
    get_recurring_start_date: function () {
        var rrule = this.get('recurring').get('rrule').get_rrule_instance();
        var custom_attrs = this.get('recurring').get('rrule').get('custom_attrs');
        var date_start;

        if (custom_attrs.get('dtstart_local')) {
            // V2 date start
            date_start = custom_attrs.get('dtstart_local');
        } else if (custom_attrs.get('date_start') && custom_attrs.get('date_start').$date) {
            // V1 date start
            date_start = custom_attrs.get('date_start').$date;
        } else {
            // rrule.after function call is Extremely slow. The project
            // https://www.zenput.com/projects/#recurring/id/46003347
            // takes 20+ seconds to calculate it. and then it's discarded because
            // dtstart_local exists.
            date_start = rrule.after(new Date(0));
        }
        return moment.isMoment(date_start) ? date_start : moment(date_start);
    },
    /**
     * Gets a recurring frequency string from an integer.
     *
     * @returns {String} A frequency string or null.
     */
    get_recurring_frequency_string: function () {
        var frequencies = ['Yearly', 'Monthly', 'Weekly', 'Daily', 'Hourly', 'Minutely', 'Secondly'];
        var frequency = null;

        if (this.get('parent_project')) {
            try {
                frequency = this.get('parent_project').get('recurring').get('rrule').get('frequency');
            } catch (e) {
                /* keep null frequency */
            }
        } else {
            try {
                frequency = this.get('recurring').get('rrule').get('frequency');
            } catch (e) {
                /* keep null frequency */
            }
        }

        return frequency !== null ? frequencies[frequency] : null;
    },

    /**
     * Rather than sending a true dtstart and custom_attrs.date_start, we will send naive versions of these
     * as custom_attrs.date_start_naive and custom_attrs.dtstart_naive. Our back-end will convert these to UTC14
     * (universal start_of_day) for processing. We will convert these naive strings
     */
    save: function (attributes, options) {
        var this_ = this;

        if (!this.get('recurring')) {
            // no modifications if doing a one-off project
            return RecurringProject.__super__.save.apply(this, arguments);
        }

        var rrule = this.get('recurring').get('rrule');
        var custom_attrs = rrule.get('custom_attrs');

        // start off fresh
        custom_attrs.set({
            dtstart_naive: null,
            date_start_naive: null
        });

        if (rrule.get('dtstart')) {
            custom_attrs.set('dtstart_naive', moment(rrule.get('dtstart').$date).format(this._NAIVE_FORMAT));
            rrule.set('dtstart', null, { silent: true });
        }

        if (custom_attrs.get('date_start')) {
            custom_attrs.set(
                'date_start_naive',
                moment(custom_attrs.get('date_start').$date).format(this._NAIVE_FORMAT)
            );
            custom_attrs.set('date_start', null, { silent: true });
        }

        // if our save fails, we need to reverse this
        options = options || {};
        options.error || (options.error = function () {});
        var _error = options.error;
        options.error = function () {
            if (custom_attrs.get('dtstart_naive')) {
                rrule.set('dtstart', {
                    $date: moment(custom_attrs.get('dtstart_naive'), this_._NAIVE_FORMAT).toDate().getTime()
                });
            }

            if (custom_attrs.get('date_start_naive')) {
                custom_attrs.set('date_start', {
                    $date: moment(custom_attrs.get('date_start_naive'), this_._NAIVE_FORMAT).toDate().getTime()
                });
            }

            return _error.apply(this, arguments);
        };

        return RecurringProject.__super__.save.call(this, attributes, options);
    },

    is_v2_project: function () {
        return Project._is_v2_project(this);
    }
});

/**
 * Projects!
 */
export const Project = BaseTaskProject.extend(
    {
        legacy_classname: '$f.models.Project',

        defaults: {
            id: null,
            title: null,
            description: null,
            company: null, // Company
            tasks: null, // Tasks
            date_due: null,
            date_due_naive: null,
            date_start: null,
            date_start_naive: null,
            date_created: null,
            date_modified: null,
            create_tasks_data: null, // BaseBatchTask
            create_tasks_account_tags: null,
            num_tasks_closed: null,
            num_tasks: null,
            is_active: true,
            is_auto_archive: false,
            is_shared: false,
            recurring: null, // Recurring
            followers: null, // Followers
            allow_any_user_fulfill: false // allow any user to fulfill tasks generated by this project
        },

        submodels: {
            tasks: Tasks,
            company: Company,
            recurring: Recurring,
            followers: Followers,
            parent_project: RecurringProject,
            create_tasks_data: BaseBatchTask
        },

        url: '/api/v1/projects/project',

        sync: function (method, model, options) {
            options = options || {};

            try {
                var reply_type = this.get('create_tasks_data').get('reply').get('type');
            } catch (e) {
                reply_type = null;
            }

            if (reply_type !== 'form_submission') {
                this.set('allow_any_user_fulfill', false); // this should never be true if we're not saving a form_submission project
            }

            switch (method) {
                case 'read':
                    options.url = `${model.url}/get/${model.id}`;
                    break;
                case 'delete':
                    options.url = `${model.url}/archive/${model.id}`;
                    break;
                case 'update':
                case 'create':
                    options.url = `${model.url}/save/`;
                    break;
            }

            return Backbone.sync(method, model, options);
        },

        unarchive: function (options) {
            var this_ = this;
            options = options || {};
            options.success = options.success || function () {};
            options.error = options.error || function () {};

            var ajax = $f.utils.select_ajax();
            ajax({
                url: `${this_.url}/unarchive/${this_.id}`,
                dataType: 'json',
                type: 'POST',
                cache: false,
                success: function (response) {
                    options.success();
                },
                error: function (response) {
                    options.error();
                }
            });
        },

        delete: function (options) {
            var this_ = this;
            options = options || {};
            options.success = options.success || function () {};
            options.error = options.error || function () {};

            var ajax = $f.utils.select_ajax();
            ajax({
                url: `${this_.url}/destroy/${this_.id}`,
                dataType: 'json',
                type: 'POST',
                cache: false,
                success: function (response) {
                    options.success();
                },
                error: function (response) {
                    options.error(response);
                }
            });
        },

        /**
         * Set the model as 'archived' and 'delete' (archive) it from the server, but keep the
         * model alive in JS.
         */
        destroy_remotely: function () {
            this.set('is_active', false);
            return this.sync('delete', this);
        },

        parse: function (response) {
            return response.results ? response.results : response;
        },

        get_date_due_naive_as_local_date: function () {
            var ddn = this.get('date_due_naive');
            if (!ddn) {
                return null;
            }

            // Warning: Javascript will always natively parse ISO strings as UTC, even if the Z is
            // missing from the end. Instead, we parse with moment and then return the date from that:
            return moment(ddn).toDate();
        },

        /**
         * Get the date_start for a one_off or recurring instance project. It falls back to date_start because
         * one-off projects do have a date_start_naive as of now.
         *
         * @returns {Date} date_start for one-off projects and date_start_naive for recurring instances. null if neither
         * exists.
         */
        get_date_start_as_local_date: function () {
            try {
                var dsn = this.get('date_start_naive') || this.get('date_start').$date;
            } catch (e) {
                return null;
            }
            // Warning: Javascript will always natively parse ISO strings as UTC, even if the Z is
            // missing from the end. Instead, we parse with moment and then return the date from that:
            return moment(dsn).toDate();
        },

        /**
         * Get a relative date due object (like $f.utils.get_relative_date()) for our date_due_naive, but override
         * the color to only be red if the actual due_date has passed in the last time zone. We don't want to
         * show red if there could still be some tasks due 'today' in a previous time zone.
         */
        get_relative_date_due_considering_timezone: function () {
            if (!this.get('date_due')) {
                return null;
            }

            var due_relative = $f.utils.get_relative_date_from_today(this.get_date_due_naive_as_local_date()); // what we display will always be based on the naive time

            if (due_relative.color != 'red') {
                return due_relative;
            }

            if (moment().isBefore(moment(this.get('date_due').$date))) {
                due_relative.color = '';
            }

            return due_relative;
        },

        /**
         * Gets a recurring frequency string from an integer.
         *
         * @returns {String} A frequency string or null.
         */
        get_recurring_frequency_string: function () {
            var frequencies = ['Yearly', 'Monthly', 'Weekly', 'Daily', 'Hourly', 'Minutely', 'Secondly'];
            var frequency = null;
            if (this.get('parent_project')) {
                try {
                    if (this.get('recurrence_frequency')) {
                        // for v3 responses
                        frequency = this.get('recurrence_frequency');
                    } else {
                        frequency = this.get('parent_project').get('recurring').get('rrule').get('frequency');
                    }
                } catch (e) {
                    /* keep null frequency */
                }
            } else {
                try {
                    frequency = this.get('recurring').get('rrule').get('frequency');
                } catch (e) {
                    /* keep null frequency */
                }
            }

            return frequency !== null ? frequencies[frequency] : null;
        },

        /**
         * Gets the project location tag if set
         *
         * @returns {String} A location string or null.
         */
        get_account_tags: function () {
            try {
                var account_tags = this.get('create_tasks_account_tags');
                if (account_tags) {
                    if (account_tags.length === 1) {
                        return account_tags[0].name;
                    } else if (account_tags.length > 1) {
                        return account_tags.length + ' tags';
                    }
                }
                return this.get('create_tasks_data').get('account_tag').get('name');
            } catch (e) {
                return null;
            }
        },

        get_account_tags_names: function () {
            return _.pluck(this.get('create_tasks_account_tags'), 'name');
        },

        get_task_account_names: function () {
            return _.pluck(this.get('create_tasks_accounts'), 'name');
        },

        is_v2_project: function () {
            return Project._is_v2_project(this);
        }
    },
    {
        /**
         * Get edit path for project v2 models
         *
         * We have to modify the hash `#` value to |hash| so it can be captured properly in zenput-front-end
         */
        get_v2_edit_path: function (model_id) {
            var hash = encodeURIComponent(window.location.hash);
            return `/projects/edit/${model_id}?successurl=${window.location.pathname + hash}`;
        },
        /**
         * Helper to share between Project and RecurringProject
         * @param model
         * @private
         */
        _is_v2_project: function (model) {
            if (model.get('create_tasks_data') && model.get('create_tasks_data').get('version').id) {
                return model.get('create_tasks_data').get('version').id === 2;
            }
            return false;
        }
    }
);

export const Projects = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.Projects',

    model: Project,
    paginator: null, // Paginator
    typeahead_search_attrs: ['title'],

    url: function () {
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'loc')) {
            attrs.push('loc');
        }
        if (!_.contains(attrs, 'filter')) {
            attrs.push('filter');
        }
        if (!_.contains(attrs, 'type')) {
            attrs.push('type');
        }
        if (!_.contains(attrs, 'is_active')) {
            attrs.push('is_active');
        }
        if (!_.contains(attrs, 'group_id')) {
            attrs.push('group_id');
        }

        // If we are filtering by the user's own Projects, ignore the team filter.
        // We will also hide the team selector form the title_nav if this is the case.
        // (@see $f.Projects.views.TitleNavView.prototype.templateHelpers() for web
        // and ProjectsView.prototype.toolbarnav_subtitle())
        if (this.paginator.get('filter') == 'me') {
            this.paginator.set('group_id', null);
        }
        this.paginator.set('limit', 20);

        return '/api/v3/projects/list_projects/?' + this.paginator.get_query_string({ get_count: true });
    },

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new Paginator({ order_by: 'date_due' });
        }
    },

    comparator: function (item) {
        return item.get(this.paginator.get('order_by'));
    }
});

export const BaseRecurringProjects = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.RecurringProjects',

    model: Project,
    paginator: null, // Paginator
    typeahead_search_attrs: ['title'],

    url: function () {
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'parent_project_id')) {
            attrs.push('parent_project_id');
        }
        if (!_.contains(attrs, 'group_id')) {
            attrs.push('group_id');
        }
        if (!_.contains(attrs, 'project_type')) {
            attrs.push('project_type');
        }

        return '/api/v3/projects/list_projects/?' + this.paginator.get_query_string({ get_count: true });
    },

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);

        if (!this.paginator) {
            this.paginator = new Paginator({ order_by: '-date_due' });
        }
    },

    comparator: function (item) {
        return item.get(this.paginator.get('order_by'));
    }
});

export const BaseRecurringParentProjects = BaseRecurringProjects.extend({
    legacy_classname: '$f.collections.RecurringParentProjects',
    allow_one_off_projects: null,
    include_archived: null,
    project_type: null,

    url: function () {
        const additionalParams = [
            this.paginator.get_query_string({ get_count: true }),
            ...(this.allow_one_off_projects ? ['allow_one_off_projects=true'] : []),
            ...(this.include_archived ? ['include_archived=true'] : []),
            ...(this.project_type ? [`project_type=${this.project_type}`] : [])
        ].join('&');
        return `/api/v1/projects/recurring/list_parent_projects/?${additionalParams}`;
    }
});

/**
 * Ingredient Temperature!
 */
export const IngredientTemperatureCategory = BaseZenputModel.extend({
    defaults: {
        id: null,
        name: null
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        IngredientTemperatureCategory.__super__.initialize.apply(this, arguments);
    },

    fetch_ingredients_count: function (cb) {
        return this.fetch(cb);
    }
});

export const IngredientTemperatureCategories = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.IngredientTemperatureCategories',
    limit: 500,
    model: IngredientTemperatureCategory,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    url: function () {
        return `/api/v3/activities/ingredients/categories/?limit=${this.limit}`;
    }
});

export const Ingredient = BaseZenputModel.extend({
    legacy_classname: '$f.models.Ingredient',

    defaults: {
        id: null,
        name: null,
        category: null,
        company: null,
        date_modified: null,
        has_expiration: null,
        translations: []
    },

    url: function () {
        return `/api/v3/activities/ingredients/${this.id}/?`;
    }
});

/**
 * Collection of ingredients.
 */
export const Ingredients = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.Ingredients',
    search_param: 'search',
    model: Ingredient,
    paginator: null,
    default_paginator_options: {
        start: null,
        limit: null,
        order_by: 'name',
        is_disabled: false,
        search: '',
        include_non_expiring: true
    },

    initialize: function (models, options) {
        var this_ = this;
        options = options || {};
        $.extend(this, _.omit(options, 'search_options'));

        if (!this.paginator) {
            if (options && options.search_options) {
                _.extend(this_.default_paginator_options, options.search_options);
            }
            this.paginator = new Paginator(this.default_paginator_options);
            if (options.search_options && options.search_options.update_paginator) {
                options.search_options.update_paginator(this.paginator);
            }
        }

        Ingredients.__super__.initialize.apply(this, arguments);
    },

    url: function () {
        var attrs = ['start', 'limit', 'order_by', 'search'];

        return (
            '/api/v3/activities/ingredients/?' +
            this.paginator.get_query_string({
                attrs: attrs,
                get_count: true
            })
        );
    }
});

export const EquipmentCategory = BaseZenputModel.extend({
    legacy_classname: '$f.models.EquipmentCategories',

    defaults: {
        id: null
    },

    url: function () {
        return `/api/v2/equipment/categories/${this.id}/`;
    }
});

export const EquipmentCategories = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.EquipmentCategories',
    limit: 500,

    defaults: {
        id: null
    },

    url: function () {
        return `/api/v2/equipment/categories/?limit=${this.limit}`;
    }
});

export const FollowUpActions = BaseZenputCollection.extend({
    legacy_classname: '$f.collections.FollowUpActions',
    model: FollowUpAction,

    defaults: {
        id: null
    },

    url: function () {
        return '/api/v3/follow_up_actions/';
    }
});

/**
 * get url vars helper method.  will handle hash params as well
 */
$.extend({
    get_url_vars: function () {
        // @deprecated - please use URLSearchParams
        var vars = [],
            hash;
        var hashes = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&');
        var anchors = window.location.href.match(/#(.*)/); // check for anchors, treat them as regular URL's

        if (anchors && anchors.length > 0) {
            hashes = hashes.concat(anchors[1].split('&'));
        }

        for (var i = 0; i < hashes.length; i++) {
            hash = hashes[i].split('=');
            vars.push(hash[0]);
            vars[hash[0]] = hash[1] ? hash[1].replace('#', '') : '';
        }
        return vars;
    },

    get_url_var: function (name) {
        return $.get_url_vars()[name];
    }
});

export default $f;
